<template>
  <svg class="huosu-logo-defs" width="0" height="0" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="speedGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#FF8347" />
        <stop offset="50%" stop-color="#FF6A2C" />
        <stop offset="100%" stop-color="#FF6A2C" />
      </linearGradient>
      <linearGradient id="keyBase" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#1E2029" />
        <stop offset="100%" stop-color="#0B0C10" />
      </linearGradient>
      <symbol id="logo-velo" viewBox="0 0 40 40">
        <rect x="3" y="4" width="34" height="34" rx="9" fill="url(#keyBase)" stroke="rgba(255,106,44,.35)" stroke-width="1" />
        <path d="M13 14 L20 20 L13 26" stroke="url(#speedGrad)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
        <path d="M20 14 L27 20 L20 26" stroke="url(#speedGrad)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity=".55" />
      </symbol>
    </defs>
  </svg>
  <section v-if="landingVisible" class="landing-page">
    <div class="landing-wrap">
      <header class="landing-header">
        <button class="landing-brand" @click="startLandingTrial('learning')" aria-label="进入火速英语">
          <svg class="huosu-logo-mark" aria-hidden="true"><use href="#logo-velo" /></svg>
          <span>火速<em>英语</em></span>
        </button>
        <nav class="landing-links" aria-label="落地页导航">
          <a href="#landing-features" @click.prevent="scrollLandingTo('landing-features')">核心能力</a>
          <a href="#landing-how" @click.prevent="scrollLandingTo('landing-how')">怎么练</a>
          <a href="#landing-scenes" @click.prevent="scrollLandingTo('landing-scenes')">适用场景</a>
          <a href="#landing-pricing" @click.prevent="scrollLandingTo('landing-pricing')">定价</a>
        </nav>
        <div class="landing-nav-actions">
          <button v-if="!isDesktopShell && !pwaStandalone" class="landing-button ghost pwa-landing-trigger" @click="openPwaInstallGuide">添加至桌面</button>
          <button class="landing-button ghost" @click="openLoginFromLanding">登录</button>
          <button class="landing-button primary" @click="startLandingTrial('dictation')">免费体验</button>
        </div>
      </header>

      <main class="landing-hero">
        <section class="landing-hero-copy">
          <p class="landing-badge"><span></span>桌面沉浸式英语输入训练 · 内测开放中</p>
          <h1>每天练一点，<br />听懂、<strong>拼对</strong>、记牢。</h1>
          <p class="landing-copy">
            从看懂句意，到不看答案听写，再按复习计划反复练习。打字是练习方式，独立回忆才是检验；登录后仍可继续免费学习。
          </p>
          <div class="landing-actions">
            <button class="landing-button primary large" @click="startLandingTrial('dictation')">马上开始练习</button>
            <button class="landing-button ghost large" @click="startLandingTrial('typing')">体验打字模式</button>
          </div>
          <div class="landing-metrics" aria-label="产品指标">
            <div><b>独立回忆</b><span>听写检验掌握</span></div>
            <div><b>间隔复习</b><span>根据表现安排</span></div>
            <div><b>原包重练</b><span>保留每次生成内容</span></div>
          </div>
        </section>

        <aside class="landing-demo" aria-label="火速英语练习演示">
          <div class="landing-demo-top">
            <i></i><i></i><i></i>
            <span>交互演示 · 以下数字为示例</span>
          </div>
          <p class="landing-demo-label">请听中文，键入英文 <kbd>SPACE</kbd> 播放语音</p>
          <h2>她习惯在每天早晨读一会儿书。</h2>
          <div class="landing-typing-line" :class="{ flash: landingTypingFlash }">
            <span class="landing-typed">{{ landingTypedText }}</span><em></em><small class="landing-pending">{{ landingPendingText }}</small>
          </div>
          <div class="landing-demo-stats">
            <div><strong>62</strong><span>WPM</span></div>
            <div><strong>98%</strong><span>准确率</span></div>
            <div><strong>3</strong><span>连击</span></div>
            <div><strong>D-7</strong><span>连续天数</span></div>
          </div>
          <div class="landing-keyboard" aria-hidden="true">
            <div v-for="row in landingKeyboardRows" :key="row.join('-')">
              <span
                v-for="key in row"
                :key="key"
                :class="{ wide: key === 'Shift' || key === 'Enter' || key === 'Ctrl' || key === 'Next', space: key === 'Space', hit: landingActiveKey === key }"
              >
                {{ key }}
                <i></i>
              </span>
            </div>
          </div>
        </aside>
      </main>

      <section id="landing-features" class="landing-section">
        <p class="landing-eyebrow">// core capabilities</p>
        <h2>三件事，让“练英语”变成能坚持的事。</h2>
        <p class="landing-section-copy">听写、打字、句子理解，每一个环节都被拆成节奏可控、反馈即时的小动作。</p>
        <div class="landing-bento">
          <article class="big">
            <span>01</span>
            <h3>听写输入 · 中英同步</h3>
            <p>中文提示、示范发音、自动分词、错词高亮。语音可包含合成发音；不看答案尝试回忆，键盘就是你的听写本。</p>
            <div class="landing-mini-log">
              <b>✓ she reads</b>
              <b>✕ everyday → every day</b>
              <b>✓ a book in the morning</b>
            </div>
          </article>
          <article>
            <span>02</span>
            <h3>标准指法</h3>
            <p>拟真手势提示和键位高亮，把 10 指打字重新练起来。</p>
          </article>
          <article>
            <span>03</span>
            <h3>坚持奖励</h3>
            <p>连续练习、错题复习、掌握状态自动记录，学习进度能看见。</p>
          </article>
          <article class="wide">
            <span>04</span>
            <h3>AI 内容工厂 · 课表永不见底</h3>
            <p>后台批量生成课程、句子、词库、音频和配图。家庭亲子、考试备考、职场成人都能快速扩容。</p>
          </article>
        </div>
      </section>

      <section id="landing-how" class="landing-section">
        <p class="landing-eyebrow">// how it works</p>
        <h2>一分钟看懂怎么练。</h2>
        <div class="landing-steps">
          <article><b>01</b><h3>选一个场景</h3><p>选择适合自己的场景与难度，先练熟，再扩展。</p></article>
          <article><b>02</b><h3>听 · 想 · 打</h3><p>先听示范发音，结合中文提示，尝试把整句英文写出来。</p></article>
          <article><b>03</b><h3>即时反馈</h3><p>正确就自动前进，错误就高亮提示，重复直到真正打对。</p></article>
          <article><b>04</b><h3>回看错题</h3><p>根据实际作答表现安排复习，优先练到期内容，再增加新内容。</p></article>
        </div>
      </section>

      <section id="landing-scenes" class="landing-section">
        <p class="landing-eyebrow">// who's it for</p>
        <h2>适合每天想练一点、又不想被 App 绑架的人。</h2>
        <div class="landing-scenes">
          <article><h3>家庭陪伴</h3><p>爸妈陪着孩子练，10 分钟一个循环。</p></article>
          <article><h3>考试备考</h3><p>中高考、四六级、雅思、托福的高频句型。</p></article>
          <article><h3>口语听写</h3><p>先听懂再打出，节奏靠指尖建立。</p></article>
          <article><h3>成人场景</h3><p>邮件、会议、商务对话，把英语放回工作里。</p></article>
        </div>
      </section>

      <section id="landing-pricing" class="landing-pricing-section">
        <h2>免费开始，按需选择套餐</h2>
        <p>登录后仍可继续免费学习。下面的价格读取自当前套餐配置，不使用演示价格；购买前可在会员中心查看详细权益与条件。</p>
        <div class="landing-plan-grid">
          <article v-for="plan in landingPlans" :key="plan.key">
            <h3>{{ plan.name }}</h3><strong>¥{{ plan.price.toFixed(2) }}</strong>
            <p>{{ plan.durationDays ? `${plan.durationDays} 天有效期` : plan.key === 'free' ? '免费学习账户' : '有效期与权益请在会员中心确认' }}</p>
          </article>
        </div>
        <p v-if="landingPlansError" role="alert">{{ landingPlansError }}</p>
        <button class="landing-button ghost" :disabled="landingPlansBusy" @click="loadLandingPlans">{{ landingPlansBusy ? '正在读取套餐…' : landingPlans.length ? '刷新套餐' : '查看当前套餐' }}</button>
      </section>
      <section class="landing-cta">
        <div>
          <h2>先体验，再登录继续免费学习。</h2>
          <p>不用下载，浏览器里直接开始。体验 3 句后登录，可保存进度并继续免费练习。</p>
        </div>
        <button class="landing-button primary large" @click="startLandingTrial('typing')">马上体验打字模式</button>
      </section>

      <footer class="landing-footer">
        <div>
          <svg class="huosu-logo-mark" aria-hidden="true"><use href="#logo-velo" /></svg>
          <span>© 2026 火速英语 · 广州火鹰信息科技有限公司</span>
        </div>
        <nav>
          <a href="#landing-features" @click.prevent="scrollLandingTo('landing-features')">核心能力</a>
          <a href="#landing-how" @click.prevent="scrollLandingTo('landing-how')">怎么练</a>
          <a href="#landing-scenes" @click.prevent="scrollLandingTo('landing-scenes')">适用场景</a>
        </nav>
      </footer>
    </div>
  </section>
  <div
    v-else
    ref="theaterRef"
    class="learning-theater"
    :class="theaterClasses"
    :style="theaterStyle"
    @click.capture="handleTheaterOutsideClick"
  >
    <Transition name="practice-boot">
      <div v-if="practiceBootLoading && !trialGateOpen" class="practice-boot-overlay" role="status" aria-live="polite" aria-label="系统正在加载">
        <div class="practice-boot-aurora" aria-hidden="true"><i></i><i></i><i></i></div>
        <section class="practice-boot-card">
          <div class="practice-boot-brand">
            <svg class="huosu-logo-mark" aria-hidden="true"><use href="#logo-velo" /></svg>
            <span>火速英语</span>
          </div>
          <div class="practice-boot-orbit" aria-hidden="true">
            <i></i><i></i><i></i><b><svg class="huosu-logo-mark"><use href="#logo-velo" /></svg></b>
          </div>
          <p>正在恢复学习现场</p>
          <h2>{{ practiceBootLabels[practiceBootStage] }}</h2>
          <div class="practice-boot-track" aria-hidden="true"><i></i></div>
          <div class="practice-boot-steps" aria-hidden="true">
            <span v-for="(_, index) in practiceBootLabels" :key="index" :class="{ active: index <= practiceBootStage }"></span>
          </div>
          <small>首条学习内容就绪后将立即进入</small>
        </section>
      </div>
    </Transition>
    <button v-if="sidebarOpen" class="drawer-scrim" aria-label="关闭学习侧栏" @click="closeAllTransientPanels"></button>

    <div v-if="lessonAudioPreparingBlocking && !dailyRecommendationOpen && !trialGateOpen" class="adaptive-modal-mask lesson-audio-loading-mask">
      <section class="lesson-audio-loading-card" role="status" aria-live="polite" aria-label="正在加载学习包">
        <span>学习包资源准备</span>
        <h2>正在加载学习包</h2>
        <p>{{ lessonAudioPreparationLabel }}</p>
        <div class="lesson-audio-loading-progress"><i :style="{ width: `${lessonAudioPreparationPercent}%` }"></i></div>
        <div class="lesson-audio-loading-meta"><b>{{ lessonAudioPreparationPercent }}%</b><span>{{ lessonAudioPrepared }} / {{ lessonAudioTotal }} 条音频已处理</span></div>
        <small>当前第 1 条音频就绪即可开始，后续内容将在学习过程中同步加载。</small>
      </section>
    </div>
    <div
      v-else-if="audioPreparationNoticeVisible && !trialGateOpen && mode !== 'dictation'"
      class="lesson-audio-background-progress"
      :class="{ 'is-current-audio': currentAudioLoading, 'has-resume-notice': Boolean(resumeNotice) }"
      role="status"
      aria-live="polite"
    >
      <span><i v-if="currentAudioLoading" class="lesson-audio-spinner" aria-hidden="true"></i>{{ audioPreparationNoticeLabel }}</span>
      <b>{{ currentAudioLoading ? '当前项' : `${lessonAudioPrepared}/${lessonAudioTotal}` }}</b>
      <i><em :style="{ width: `${audioPreparationNoticePercent}%` }"></em></i>
    </div>

    <div v-if="dailyRecommendationOpen && currentUser" class="adaptive-modal-mask">
      <section class="adaptive-onboarding daily-recommendation-modal" role="dialog" aria-modal="true" aria-label="今日学习推荐">
        <header>
          <span>今日智能推荐</span>
          <h2>今天继续按目标练，还是接着上次学？</h2>
          <p>{{ adaptiveHome?.plan?.summary || '已根据你的学习目标、薄弱项和复习记录准备今日任务。' }}</p>
        </header>
        <div class="adaptive-onboarding-body daily-recommendation-body">
          <div class="daily-recommendation-overview">
            <b>{{ adaptiveHome?.plan?.targetMinutes || 20 }} 分钟</b>
            <span>{{ `${adaptiveHome?.plan?.items.length || 0} 项今日任务` }}</span>
          </div>
          <div v-if="dailyRecommendationPreparing" class="daily-recommendation-loading" role="status" aria-live="polite">
            <div>
              <span>{{ dailyRecommendationProgressLabel }}</span>
              <b>{{ dailyRecommendationPreparing ? `${dailyRecommendationProgressPercent}%` : '已就绪' }}</b>
            </div>
            <i aria-hidden="true"><em :style="{ width: `${dailyRecommendationProgressPercent}%` }"></em></i>
            <ol v-if="dailyRecommendationPreparing" class="daily-recommendation-steps">
              <li :class="dailyRecommendationStepClass(30)"><span>读取今日计划</span><b>{{ dailyRecommendationPreparationPercent >= 30 ? '完成' : '等待' }}</b></li>
              <li :class="dailyRecommendationStepClass(55)"><span>恢复学习进度</span><b>{{ dailyRecommendationPreparationPercent >= 55 ? '完成' : '等待' }}</b></li>
              <li :class="dailyRecommendationStepClass(75)"><span>整理推荐任务</span><b>{{ dailyRecommendationPreparationPercent >= 75 ? '完成' : '等待' }}</b></li>
              <li :class="dailyRecommendationStepClass(100)"><span>准备学习包音频</span><b>{{ lessonAudioPreparingBlocking ? `${lessonAudioPrepared}/${lessonAudioTotal}` : (dailyRecommendationPreparationPercent >= 100 ? '完成' : '等待') }}</b></li>
            </ol>
          </div>
          <div v-else class="daily-recommendation-lazy-note">
            <span>{{ dailyRecommendationBaseReady ? '选择学习路径后，才会加载对应句包和音频。' : '正在读取学习记录，完成后即可选择。' }}</span>
            <small v-if="dailyRecommendationError">{{ dailyRecommendationError }}</small>
          </div>
        </div>
        <footer>
          <button class="adaptive-secondary" :disabled="!dailyRecommendationReady" :aria-busy="dailyRecommendationAction === 'previous'" @click="continuePreviousLearning">{{ dailyRecommendationAction === 'previous' ? '正在加载之前学习…' : '继续之前学习' }}</button>
          <button class="adaptive-primary" :disabled="!dailyRecommendationReady" :aria-busy="dailyRecommendationAction === 'today'" @click="useTodayRecommendation">{{ dailyRecommendationAction === 'today' ? '正在加载今日任务…' : '使用今日推荐任务' }}</button>
        </footer>
      </section>
    </div>

    <div v-if="reviewPreviewNotice && !landingVisible" class="practice-sync-notice practice-review-notice" role="status">
      <span>{{ reviewPreviewNotice }}</span>
    </div>
    <div v-if="practiceSyncNotice && !landingVisible" class="practice-sync-notice" role="status">
      <span>{{ practiceSyncNotice }}</span><button @click="retryPracticeSync">重试同步</button>
    </div>
    <div v-if="resumeNotice && !dailyRecommendationOpen" class="learning-resume-notice" role="status">
      <span>{{ resumeNotice }}</span>
      <button @click="restartCurrentLesson">从头学习本节</button>
      <button aria-label="关闭提示" @click="resumeNotice = ''">×</button>
    </div>

    <div v-if="onboardingOpen && currentUser" class="adaptive-modal-mask">
      <section class="adaptive-onboarding" role="dialog" aria-modal="true" aria-label="建立学习画像">
        <header>
          <span>AI 学习画像</span>
          <h2>{{ adaptiveHome?.config.title || "用一分钟，定制你的英语学习计划" }}</h2>
          <p>{{ adaptiveHome?.config.subtitle || "以后会根据你的目标、薄弱项和记忆状态动态安排内容，分类选课仍然保留。" }}</p>
        </header>
        <div v-if="onboardingStep === 1" class="adaptive-onboarding-body questionnaire-grid">
          <section class="questionnaire-field"><label>目前水平</label><div class="adaptive-choice-grid level-choice-grid"><button v-for="item in levelOptions" :key="item.value" :class="{ active: profileForm.currentLevel === item.value }" @click="profileForm.currentLevel = item.value"><b>{{ item.label }}</b><small>{{ item.description }}<template v-if="item.value !== '零基础'"> · {{ item.value }}</template></small></button></div></section>
          <section class="questionnaire-field"><label>目标水平</label><div class="adaptive-choice-grid level-choice-grid"><button v-for="item in levelOptions.slice(1)" :key="item.value" :class="{ active: profileForm.targetLevel === item.value }" @click="profileForm.targetLevel = item.value"><b>{{ item.label }}</b><small>{{ item.description }} · {{ item.value }}</small></button></div></section>
          <section class="questionnaire-field questionnaire-wide"><label>学习目的</label><div class="adaptive-choice-grid compact-choice-grid"><button v-for="item in goalOptions" :key="item.value" :class="{ active: profileForm.goalType === item.value }" @click="profileForm.goalType = item.value">{{ item.label }}</button></div></section>
          <section class="questionnaire-field"><label>每天学习时间</label><div class="adaptive-choice-grid compact-choice-grid"><button v-for="item in dailyMinuteOptions" :key="item" :class="{ active: profileForm.dailyMinutes === item }" @click="profileForm.dailyMinutes = item">{{ item }}分钟</button></div></section>
          <section class="questionnaire-field"><label>希望重点提升</label><div class="adaptive-choice-grid compact-choice-grid"><button v-for="item in skillOptions" :key="item.value" :class="{ active: profileForm.weakSkills?.includes(item.value) }" @click="toggleWeakSkill(item.value)">{{ item.label }}</button></div></section>
        </div>
        <div v-else class="adaptive-onboarding-body placement-grid">
          <div class="placement-intro"><b>学习起点自评</b><span>这是主观自评，不是标准化考试或 CEFR 等级认证。先选择合适起点，再根据实际练习调整。</span></div>
          <p v-if="onboardingNotice" class="onboarding-notice" role="alert">{{ onboardingNotice }}</p>
          <section v-for="item in skillOptions" :key="`placement-${item.value}`" class="placement-row">
            <div><b>{{ item.label }}</b><span>{{ placementPrompt[item.value] }}</span></div>
            <div class="adaptive-choice-grid compact"><button v-for="score in placementScoreOptions" :key="score.value" :class="{ active: placementScores[item.value] === score.value }" @click="placementScores[item.value] = score.value">{{ score.label }}</button></div>
          </section>
        </div>
        <footer>
          <button class="adaptive-secondary" :disabled="adaptiveBusy" @click="onboardingStep === 1 ? (onboardingOpen = false) : (onboardingStep = 1)">{{ onboardingStep === 1 ? "稍后设置" : "上一步" }}</button>
          <button v-if="onboardingStep === 2 && onboardingNotice" class="adaptive-secondary" :disabled="adaptiveBusy" @click="openManualCourseSelection">手动选课</button>
          <button v-if="onboardingStep === 1" class="adaptive-primary" @click="enterPlacementAssessment">选择学习起点</button>
          <button v-else class="adaptive-primary" :disabled="adaptiveBusy" @click="completeOnboarding">{{ adaptiveBusy ? "正在生成画像…" : "生成我的学习计划" }}</button>
        </footer>
      </section>
    </div>

    <div v-if="adaptiveChoiceOpen && currentUser" class="adaptive-modal-mask">
      <section class="adaptive-onboarding adaptive-recommendation" role="dialog" aria-modal="true" aria-label="选择下一组智能训练">
        <header><span>AI 下一步推荐</span><h2>{{ adaptiveTrainingActive ? "练得不错，选择下一个训练包" : "画像已完成，选择第一个训练包" }}</h2><p>系统结合你的目标、薄弱项和记忆状态，每轮给出 3 个可选方向。</p></header>
        <div class="adaptive-onboarding-body recommendation-grid">
          <button v-for="item in adaptiveRecommendations" :key="item.id" class="recommendation-card" @click="chooseAdaptiveRecommendation(item)"><span>{{ item.itemType === 'word' ? '单词包' : '句子包' }} · {{ modeLabel(item.mode) }}</span><b>{{ adaptiveSentenceText(item.sentenceId) }}</b><small>{{ item.reason }} · 约{{ item.estimatedMinutes }}分钟</small><em>选择并开始 →</em></button>
          <p v-if="!adaptiveRecommendations.length" class="recommendation-empty">今日智能计划已完成，可以继续从分类课包自由练习。</p>
        </div>
        <footer><button class="adaptive-secondary" @click="adaptiveChoiceOpen = false; smartPlanOpen = true">查看完整计划</button><button class="adaptive-secondary" @click="adaptiveChoiceOpen = false">继续分类选课</button></footer>
      </section>
    </div>

    <button v-if="smartPlanOpen" class="smart-plan-backdrop" type="button" aria-label="关闭今日智能学习计划" @click="smartPlanOpen = false"></button>
    <aside v-if="smartPlanOpen" class="smart-plan-panel" @click.stop>
      <header><div><span>千人千面</span><h2>{{ adaptiveHome?.plan?.title || "今日智能计划" }}</h2><p>{{ adaptiveHome?.plan?.summary || "完成画像后，系统会自动生成每日计划。" }}</p><button class="smart-goal-edit-trigger" @click="openGoalEditor">修改学习目标</button></div><button @click="smartPlanOpen = false">关闭</button></header>
      <div class="smart-plan-body">
        <section v-if="adaptiveHome?.plan" class="smart-plan-summary"><b>{{ adaptiveHome.plan.targetMinutes }}分钟</b><span>{{ adaptiveHome.plan.items.filter(item => item.status === 'completed').length }} / {{ adaptiveHome.plan.items.length }} 已完成</span><em>{{ adaptiveHome.algorithm }}</em></section>
        <section v-if="goalEditorOpen" class="smart-goal-editor">
          <header><b>全局学习目标</b><span>保存后今日计划和后续 AI 推荐立即按新目标重算</span></header>
          <label><span>学习目的</span><select v-model="profileForm.goalType"><option v-for="item in goalOptions" :key="item.value" :value="item.value">{{ item.label }}</option></select></label>
          <label><span>目标水平</span><select v-model="profileForm.targetLevel"><option v-for="item in levelOptions.slice(1)" :key="item.value" :value="item.value">{{ item.label }} · {{ item.value }}</option></select></label>
          <label><span>每天学习</span><select v-model.number="profileForm.dailyMinutes"><option v-for="item in dailyMinuteOptions" :key="item" :value="item">{{ item }} 分钟</option></select></label>
          <div><span>重点提升</span><div class="smart-goal-skills"><button v-for="item in skillOptions" :key="item.value" :class="{ active: profileForm.weakSkills?.includes(item.value) }" @click="toggleWeakSkill(item.value)">{{ item.label }}</button></div></div>
          <footer><button @click="goalEditorOpen = false">取消</button><button class="adaptive-primary" :disabled="goalSaving" @click="saveGlobalLearningGoal">{{ goalSaving ? '正在全局更新…' : '保存并重算推荐' }}</button></footer>
        </section>
        <section class="adaptive-pack-generator">
          <div class="adaptive-pack-generator-head">
            <div><span>AI 动态学习包</span><b>围绕学习目标和重点词，生成针对性的句子</b></div>
            <label v-if="currentUser && !adaptiveTrainingPackBusy" class="adaptive-pack-count"><span>生成句子数</span><select v-model.number="adaptivePackCount" aria-label="选择本次生成句子数量"><option v-for="option in adaptiveGenerationOptions" :key="option.contentCount" :value="option.contentCount">{{ option.contentCount }} 句</option></select></label>
            <button v-if="currentUser" class="adaptive-primary adaptive-generate-button" :class="{ busy: adaptiveTrainingPackBusy }" :disabled="adaptiveTrainingPackBusy || adaptiveCreditInsufficient" :aria-busy="adaptiveTrainingPackBusy" @click="generateAdaptiveTrainingPacks">
              <i v-if="adaptiveTrainingPackBusy" :style="{ width: `${adaptiveTrainingBatch?.percent || 3}%` }"></i>
              <span aria-live="polite">{{ adaptiveGenerateButtonLabel }}</span>
            </button>
            <button v-else class="adaptive-secondary" @click="smartPlanOpen = false; openTrialLoginPanel()">登录后生成</button>
          </div>
          <div v-if="currentUser && selectedAdaptiveGenerationOption && !adaptiveTrainingPackBusy" class="adaptive-generation-quote" :class="{ insufficient: adaptiveCreditInsufficient }">
            <div><b>{{ selectedAdaptiveGenerationOption.label }}</b><span>{{ selectedAdaptiveGenerationOption.description }}</span></div>
            <div><span>生成 {{ selectedAdaptiveGenerationOption.contentCount }} 句 · 整理为 {{ selectedAdaptiveGenerationOption.estimatedPackCount }} 个大包</span><strong>{{ selectedAdaptiveGenerationOption.creditCost }} 积分</strong></div>
            <small>AI积分余额 {{ adaptiveCreditBalance }} · {{ adaptiveGenerationPricing?.membershipLabel }} <button class="ai-credit-recharge-trigger" @click="aiCreditRechargeOpen = !aiCreditRechargeOpen">充值积分</button></small>
            <em v-if="adaptiveCreditInsufficient">积分不足，请充值后生成；已有课程和内容仍可正常学习。</em>
          </div>
          <div v-if="aiCreditRechargeOpen && commerce?.aiCreditPackages?.length" class="ai-credit-package-grid">
            <button v-for="pack in commerce.aiCreditPackages" :key="pack.key" :class="{ recommended: pack.isRecommended }" :disabled="commerceBusy === `ai-credit-${pack.key}`" @click="handleAiCreditRecharge(pack.key)">
              <span>{{ pack.isRecommended ? '推荐' : 'AI积分包' }}</span><b>{{ pack.credits + pack.bonusCredits }} 积分</b><small>含赠送 {{ pack.bonusCredits }} · ¥{{ pack.price }}</small>
            </button>
          </div>
          <p v-if="commerceNotice && smartPlanOpen" class="ai-credit-notice">{{ commerceNotice }}</p>
          <div v-if="adaptiveTrainingBatch && adaptiveTrainingBatch.status !== 'completed'" class="adaptive-pack-progress" :class="`is-${adaptiveTrainingBatch.status}`">
            <div class="adaptive-progress-status-row">
              <span>{{ adaptiveBatchStatusIcon }} {{ adaptiveBatchStatusLabel }}</span>
              <time>已用时 {{ adaptiveBatchElapsed }}</time>
            </div>
            <div class="adaptive-pack-progress-line"><i :style="{ width: `${adaptiveTrainingBatch.percent}%` }"></i></div>
            <strong>{{ adaptiveTrainingBatch.percent }}% · 当前步骤：{{ adaptiveBatchStageLabel }}</strong>
            <small>{{ adaptiveTrainingBatch.events[adaptiveTrainingBatch.events.length - 1]?.detail }}</small>
            <small v-if="adaptiveTrainingPollError" class="adaptive-poll-reconnecting">连接短暂中断，正在第 {{ adaptiveTrainingPollError }} 次自动重连；任务仍保持锁定。</small>
            <div class="adaptive-progress-facts">
              <span>目标约 {{ adaptiveTrainingBatch.requestedContentCount || adaptiveTrainingBatch.totalItems }} 条</span>
              <span>已生成 {{ adaptiveTrainingBatch.completedItems }} 条</span>
              <span v-if="adaptiveTrainingBatch.creditCost">已锁定 {{ adaptiveTrainingBatch.creditCost }} 积分</span>
              <span v-if="adaptiveTrainingBatch.provider">{{ adaptiveTrainingBatch.provider }} / {{ adaptiveTrainingBatch.model }}</span>
              <span v-else>节点 adaptive_sentence_generation</span>
            </div>
            <em v-if="adaptiveTrainingBatch.nextAction"><b>下一步：</b>{{ adaptiveTrainingBatch.nextAction }}</em>
          </div>
          <div v-if="availableAdaptivePacks.length" class="adaptive-pack-section-head">
            <div><b>学习包</b><span>共 {{ availableAdaptivePacks.length }} 包 · 按生成批次排列</span></div>
            <small>最新生成的内容置顶，学习进度会自动保留。</small>
          </div>
          <div v-if="availableAdaptivePacks.length" ref="adaptivePackListRef" class="adaptive-pack-batches">
            <section
              v-for="(group, groupIndex) in availableAdaptivePackGroups"
              :key="group.batch.id"
              class="adaptive-pack-batch-group"
              :class="{ latest: groupIndex === 0, fresh: group.batch.id === recentGeneratedBatchId }"
            >
              <header>
                <div>
                  <b>{{ group.batch.id === recentGeneratedBatchId ? '刚生成' : groupIndex === 0 ? '最新批次' : '历史批次' }}</b>
                  <time>{{ formatAdaptiveBatchTime(group.batch.createdAt) }}</time>
                </div>
                <span>{{ group.entries.length }} 个学习包</span>
              </header>
              <div class="adaptive-pack-grid">
                <article v-for="entry in group.entries" :key="entry.pack.id" :class="{ studied: Boolean(adaptivePackProgress(entry.pack.id)) }">
                  <div class="adaptive-pack-main">
                    <div class="adaptive-pack-meta"><span>{{ entry.pack.energyLabel }}</span><em>{{ entry.pack.estimatedMinutes }}分钟</em><em>{{ entry.pack.items.length }}项</em></div>
                    <b>{{ adaptiveTrainingPackDisplayName(entry) }}</b>
                    <small>{{ entry.pack.description }}</small>
                    <div class="adaptive-pack-focus"><em v-for="word in entry.pack.focusWords.slice(0, 3)" :key="word">{{ word }}</em></div>
                    <div v-if="adaptivePackProgress(entry.pack.id)" class="adaptive-pack-learning-progress">
                      <div><b>{{ adaptivePackProgress(entry.pack.id)?.progressPercent }}%</b><span>已学 {{ adaptivePackProgress(entry.pack.id)?.learnedSentences }}/{{ adaptivePackProgress(entry.pack.id)?.totalSentences }} · 复习 {{ adaptivePackProgress(entry.pack.id)?.reviewCount || 0 }} 次</span></div>
                      <i><em :style="{ width: `${adaptivePackProgress(entry.pack.id)?.progressPercent || 0}%` }"></em></i>
                    </div>
                  </div>
                  <button class="adaptive-pack-start-button" @click="startGeneratedTrainingPack(entry.pack)">{{ adaptivePackProgress(entry.pack.id) ? '继续' : '开始' }} <span>→</span></button>
                </article>
              </div>
            </section>
          </div>
          <div v-if="adaptiveTrainingBatch?.events.length && adaptiveTrainingBatch.status !== 'completed'" class="adaptive-pack-events">
            <header><b>实时生成过程</b><span>{{ adaptiveTrainingBatch.events.length }} 条记录 · 自动刷新</span></header>
            <article v-for="event in adaptiveTrainingBatch.events" :key="`${event.at}-${event.phase}-${event.label}`" :class="`is-${event.status}`">
              <i>{{ event.status === 'completed' ? '✓' : event.status === 'running' ? '•••' : event.status === 'blocked' ? '!' : event.status === 'failed' ? '×' : '○' }}</i>
              <div><b>{{ event.label }}</b><span>{{ event.detail }}</span><time>{{ formatAdaptiveEventTime(event.at) }} · 进度 {{ event.percent }}%</time></div>
            </article>
          </div>
        </section>
        <section class="smart-plan-tool-buttons">
          <button :class="{ active: smartPlanDetail === 'history' }" @click="toggleSmartPlanDetail('history')"><b>学习历史</b><span>{{ learnerActivity?.learningHistory.totalPackages || 0 }} 包</span></button>
        </section>
        <section v-if="smartPlanDetail === 'history'" class="smart-plan-collapsible-list">
          <header><b>学习历史</b><span>累计练习 {{ learnerActivity?.learningHistory.totalPracticeCount || 0 }} 次</span></header>
          <article v-for="item in learnerActivity?.learningHistory.packages || []" :key="item.courseId">
            <div><b>{{ item.courseTitle }}</b><span>{{ item.progressPercent }}%</span></div>
            <i class="history-progress"><em :style="{ width: `${item.progressPercent}%` }"></em></i>
            <small>已学 {{ item.learnedSentences }} / {{ item.totalSentences }} 句 · 复习 {{ item.reviewCount || 0 }} 次 · 最近 {{ formatStudyTime(item.lastStudiedAt) }}</small>
          </article>
          <p v-if="!learnerActivity?.learningHistory.packages.length">完成练习后，这里会生成学习进度。</p>
        </section>
        <div v-if="adaptiveHome?.plan" class="smart-plan-list compact-plan-list"><button v-for="item in adaptiveHome.plan.items" :key="item.id" :class="{ done: item.status === 'completed' }" @click="startAdaptivePlanItem(item)"><span>{{ item.source === 'review' ? '复习' : '新学' }}</span><b>{{ adaptiveSentenceText(item.sentenceId) }}</b><small>{{ item.reason }} · {{ modeLabel(item.mode) }}</small></button></div>
        <button v-else class="adaptive-primary wide" @click="onboardingOpen = true; smartPlanOpen = false">建立学习画像</button>
      </div>
    </aside>

    <aside class="study-drawer" aria-label="学习侧栏">
      <div class="drawer-brand">
        <div class="drawer-brand-main">
          <div class="brand-mark">
            <svg class="huosu-logo-mark" aria-hidden="true"><use href="#logo-velo" /></svg>
          </div>
          <div>
            <h1>课程导航</h1>
          </div>
        </div>
        <nav class="learning-library-tabs" aria-label="学习资源类型">
          <button :class="{ active: catalogContentType === 'words' }" @click="setCatalogContentType('words')">单词库</button>
          <button :class="{ active: catalogContentType === 'sentences' }" @click="setCatalogContentType('sentences')">句子库</button>
        </nav>
        <button class="drawer-close" @click="closeAllTransientPanels">关闭</button>
      </div>

      <section v-if="catalogContentType === 'sentences'" class="drawer-catalog">
        <nav class="catalog-root-tabs" aria-label="句子库一级分类">
          <button
            v-for="root in learnerCategoryRoots"
            :key="root.id"
            :class="{ active: activeRootCategory?.id === root.id }"
            @click="selectSentenceRoot(root.id)"
          >
            {{ root.name }}
          </button>
        </nav>

        <aside class="catalog-category-sidebar" aria-label="句子库二级与三级分类">
          <header><strong>{{ activeRootCategory?.name || "分类" }}</strong><small>二级 / 三级</small></header>
          <button class="catalog-category-all" :class="{ active: selectedCategory?.id === activeRootCategory?.id }" @click="selectSentenceRoot(activeRootCategory?.id || '')">全部{{ activeRootCategory?.name || "分类" }}</button>
          <div v-for="category in secondLevelCategories" :key="category.id" class="catalog-category-branch">
            <div class="catalog-category-row">
              <button class="catalog-category-name" :class="{ active: activeSecondCategory?.id === category.id }" @click="selectSentenceSecond(category.id)">{{ category.name }}</button>
              <button
                v-if="sentenceThirdCategories(category.id).length"
                class="catalog-category-expand"
                :aria-label="`${sentenceSecondExpanded(category.id) ? '收起' : '展开'}${category.name}`"
                :aria-expanded="sentenceSecondExpanded(category.id)"
                @click="toggleSentenceSecond(category.id)"
              >{{ sentenceSecondExpanded(category.id) ? '⌄' : '›' }}</button>
            </div>
            <div v-if="sentenceSecondExpanded(category.id)" class="catalog-category-children">
              <button
                v-for="child in sentenceThirdCategories(category.id)"
                :key="child.id"
                :class="{ active: activeThirdCategory?.id === child.id }"
                @click="selectSentenceThird(child.id)"
              >{{ child.name }}</button>
            </div>
          </div>
          <p v-if="!secondLevelCategories.length" class="catalog-category-empty">当前一级分类下暂无二级分类。</p>
        </aside>

        <section class="package-column" aria-label="课包与场景">
          <header>
            <strong>{{ packageColumnTitle }}</strong>
            <div class="package-column-tools">
              <span>{{ filteredCourses.length }} 个课包 · {{ filteredSentenceCount }}句 / {{ filteredWordCount }}词</span>
              <div class="package-view-switch" role="group" aria-label="课程封面显示模式">
                <button :class="{ active: catalogViewMode === 'list' }" :aria-pressed="catalogViewMode === 'list'" @click="setCatalogViewMode('list')">列表</button>
                <button :class="{ active: catalogViewMode === 'grid' }" :aria-pressed="catalogViewMode === 'grid'" @click="setCatalogViewMode('grid')">宫格</button>
              </div>
            </div>
          </header>
          <p v-if="!filteredCourses.length" class="catalog-empty">当前分类暂无已发布句库，后台生成并审核发布后会显示在这里。</p>
          <div v-else class="course-package-list" :class="`is-${catalogViewMode}`">
            <article v-for="(course, courseIndex) in filteredCourses" :key="course.id" class="course-package-row" :class="{ active: catalogSelectedCourseId === course.id }">
              <button class="package-summary" @click="selectCourse(course.id)">
                <span class="package-cover" :class="{ empty: !course.cover || courseCoverFailedIds[course.id] }">
                  <img
                    v-if="course.cover && !courseCoverFailedIds[course.id]"
                    :src="courseCoverDisplayUrl(course.cover)"
                    :alt="`${course.title}封面`"
                    :loading="courseIndex < 16 || catalogSelectedCourseId === course.id ? 'eager' : 'lazy'"
                    decoding="async"
                    @error="markCourseCoverFailed(course.id)"
                  />
                  <i v-else aria-hidden="true">火</i>
                  <span class="package-cover-overlay">
                    <b>{{ course.title }}</b>
                    <small>{{ course.subtitle }}</small>
                  </span>
                </span>
                <span class="package-copy">
                  <span class="package-copy-heading">
                    <b>{{ course.title }}</b>
                    <small>{{ course.subtitle }}</small>
                  </span>
                  <i>{{ courseSentenceCount(course) }}句 / {{ courseWordCount(course) }}词</i>
                </span>
                <em class="package-toggle">{{ catalogSelectedCourseId === course.id ? "收起" : "展开" }}</em>
              </button>
              <div v-if="catalogSelectedCourseId === course.id" class="scenario-list">
                <p v-if="catalogCourseLoadingId === course.id" class="catalog-empty">正在加载本课包的课节…</p>
                <template v-else>
                <div class="whole-package-entry">
                  <div><b>连续挑战整个大包</b><small>{{ courseLessonCount(course) }} 个小关 · {{ courseSentenceCount(course) }} 句，每过一关都会记录成绩</small></div>
                  <button :disabled="wholeCourseStartingId === course.id" @click="startWholeCourse(course)">{{ wholeCourseStartingId === course.id ? "准备中…" : "开始连续闯关" }}</button>
                </div>
                <div
                  v-for="lesson in course.lessons"
                  :key="lesson.id"
                  class="scenario-item"
                >
                  <div class="scenario-row" :class="{ active: catalogExpandedLessonId === lesson.id }">
                    <button class="scenario-copy-button" @click="previewCatalogLesson(course.id, lesson.id)">
                      <span>{{ lesson.title }}</span>
                      <small>{{ lesson.description }}</small>
                      <em>{{ lessonSentenceCount(lesson) }}句 / {{ lessonWordCount(lesson) }}词 · 查看介绍</em>
                    </button>
                    <button class="scenario-enter-button" @click="enterCatalogLesson(lesson.id)">单关练习</button>
                  </div>
                  <section v-if="catalogExpandedLessonId === lesson.id && catalogExpandedLesson" class="lesson-detail-panel">
                    <header>
                      <strong>{{ catalogExpandedLesson.title }}</strong>
                      <span>{{ lessonSentenceCount(catalogExpandedLesson) }} 句 / {{ lessonWordCount(catalogExpandedLesson) }} 词 · {{ catalogExpandedLesson.isFreeTrial ? "试学" : "会员" }}</span>
                    </header>
                    <p>{{ catalogExpandedLesson.description }}</p>
                    <div v-if="catalogExpandedLesson.sentences?.length" class="lesson-detail-sentences">
                      <span v-for="sentence in catalogExpandedLesson.sentences.slice(0, 5)" :key="sentence.id">
                        <b>{{ sentence.english }}</b>
                        <em>{{ sentence.chinese }}</em>
                      </span>
                    </div>
                  </section>
                </div>
                </template>
              </div>
            </article>
          </div>
        </section>
      </section>

      <section v-else class="word-library-panel">
        <aside class="catalog-category-sidebar word-category-sidebar" aria-label="单词库分类">
          <header><strong>单词分类</strong><small>全部分类</small></header>
          <button class="catalog-category-all" :class="{ active: selectedWordCategory?.id === wordLibraryRoot?.id }" @click="selectWordLibraryCategory(wordLibraryRoot?.id || '')">全部词库</button>
          <button
            v-for="category in wordLibraryNavigationCategories"
            :key="category.id"
            class="catalog-category-name word-category-name"
            :class="{ active: selectedWordCategory?.id === category.id }"
            @click="selectWordLibraryCategory(category.id)"
          >{{ category.name }}</button>
        </aside>
        <section class="package-column word-package-column" aria-label="单词词书与词包">
          <header>
            <strong>{{ selectedWordCategory?.name || "全部词库" }}</strong>
            <div class="package-column-tools">
              <span>{{ wordFilteredCourses.length }} 个词包 · {{ wordFilteredItemCount }} 词</span>
              <div class="package-view-switch" role="group" aria-label="课程封面显示模式">
                <button :class="{ active: catalogViewMode === 'list' }" :aria-pressed="catalogViewMode === 'list'" @click="setCatalogViewMode('list')">列表</button>
                <button :class="{ active: catalogViewMode === 'grid' }" :aria-pressed="catalogViewMode === 'grid'" @click="setCatalogViewMode('grid')">宫格</button>
              </div>
            </div>
          </header>
          <p v-if="!wordFilteredCourses.length" class="catalog-empty">当前分类暂无可学习词书。</p>
          <div v-else class="course-package-list" :class="`is-${catalogViewMode}`">
            <article v-for="(course, courseIndex) in wordFilteredCourses" :key="course.id" class="course-package-row" :class="{ active: catalogSelectedCourseId === course.id }">
              <button class="package-summary" @click="selectCourse(course.id)">
                <span class="package-cover" :class="{ empty: !course.cover || courseCoverFailedIds[course.id] }">
                  <img
                    v-if="course.cover && !courseCoverFailedIds[course.id]"
                    :src="courseCoverDisplayUrl(course.cover)"
                    :alt="`${course.title}封面`"
                    :loading="courseIndex < 16 || catalogSelectedCourseId === course.id ? 'eager' : 'lazy'"
                    decoding="async"
                    @error="markCourseCoverFailed(course.id)"
                  />
                  <i v-else aria-hidden="true">火</i>
                  <span class="package-cover-overlay">
                    <b>{{ course.title }}</b>
                    <small>{{ course.subtitle }}</small>
                  </span>
                </span>
                <span class="package-copy">
                  <span class="package-copy-heading">
                    <b>{{ course.title }}</b>
                    <small>{{ course.subtitle }}</small>
                  </span>
                  <i>{{ wordCourseItemCount(course) }}词 · {{ courseLessonCount(course) }} 个小词包</i>
                </span>
                <em class="package-toggle">{{ catalogSelectedCourseId === course.id ? "收起" : "展开" }}</em>
              </button>
              <div v-if="catalogSelectedCourseId === course.id" class="scenario-list">
                <p v-if="catalogCourseLoadingId === course.id" class="catalog-empty">正在加载本词包的课节…</p>
                <template v-else>
                <div class="whole-package-entry">
                  <div><b>连续学习当前大词包</b><small>{{ courseLessonCount(course) }} 个小关 · {{ wordCourseItemCount(course) }} 词，每完成一关都会记录进度</small></div>
                  <button :disabled="wholeCourseStartingId === course.id" @click="startWholeCourse(course)">{{ wholeCourseStartingId === course.id ? "准备中…" : "开始连续学习" }}</button>
                </div>
                <div v-for="lesson in course.lessons" :key="lesson.id" class="scenario-item">
                  <div class="scenario-row" :class="{ active: catalogExpandedLessonId === lesson.id }">
                    <button class="scenario-copy-button" @click="previewCatalogLesson(course.id, lesson.id)">
                      <span>{{ lesson.title }}</span>
                      <small class="word-lesson-preview"><b>本组词表：</b>{{ wordLessonPreview(lesson) }}</small>
                      <em class="word-lesson-basis"><b>归类依据：</b>{{ wordLessonClassification(course) }} · {{ wordLessonItemCount(lesson) }}词</em>
                    </button>
                    <button class="scenario-enter-button" @click="enterCatalogLesson(lesson.id)">开始本关</button>
                  </div>
                </div>
                </template>
              </div>
            </article>
          </div>
        </section>
      </section>
    </aside>

    <main class="practice-theater" @click="handlePracticeClick">
      <div v-if="isDesktopShell" class="desktop-drag-strip" aria-hidden="true">
        <span>火速英语学习端</span>
      </div>

      <header class="practice-topbar">
        <div class="topbar-left">
          <button class="top-icon" aria-label="打开学习侧栏" @click.stop="toggleSidebar">分类</button>
          <svg class="topbar-logo" aria-hidden="true"><use href="#logo-velo" /></svg>
          <div class="lesson-trail">
            <span>{{ currentCourse?.title || "火速英语" }}</span>
            <i></i>
            <strong>{{ currentLesson?.title || "选择课节" }}</strong>
          </div>
        </div>

        <div class="topbar-right">
          <button class="smart-plan-trigger" @click.stop="smartPlanOpen = !smartPlanOpen">AI 今日计划</button>
          <button v-if="!isDesktopShell && !pwaStandalone" class="pwa-install-trigger" @click.stop="openPwaInstallGuide">添加至桌面</button>
          <div v-if="shouldShowDesktopDownload" class="desktop-download-center" @click.stop>
            <button
              class="desktop-download-trigger"
              :class="{ active: desktopDownloadOpen }"
              :title="`桌面端下载：${recommendedDesktopLabel}`"
              @click="toggleDesktopDownloadPanel"
            >
              <span>桌面端下载</span>
            </button>
            <div v-if="desktopDownloadOpen" class="desktop-download-popover">
              <button
                v-for="item in desktopPlatformOptions"
                :key="item.platform"
                class="desktop-download-option"
                :class="{ active: desktopPlatform === item.platform, disabled: !item.available }"
                @click="chooseDesktopDownload(item.platform)"
              >
                <span>
                  <b>{{ item.label }}</b>
                  <small>{{ item.available ? `v${item.latestVersion?.version}` : "后台发布后可下载" }}</small>
                </span>
                <em>{{ desktopDownloadBusy === item.latestVersion?.id ? "已开始" : item.available ? "下载" : "待发布" }}</em>
              </button>
              <p v-if="desktopDownloadNotice" class="desktop-download-notice">{{ desktopDownloadNotice }}</p>
              <p>{{ desktopDownload?.message || "正在识别当前系统..." }}</p>
            </div>
          </div>
          <button v-if="currentUser" class="learning-history-trigger" @click.stop="openLearningHistory">学习记录</button>

          <div class="mode-select" @click.stop>
            <button
              class="mode-select-trigger"
              :class="{ active: modeMenuOpen }"
              aria-haspopup="listbox"
              :aria-expanded="modeMenuOpen"
              @click="toggleModeMenu"
            >
              <span>{{ activeMode?.label || "学习" }}</span>
              <i></i>
            </button>
            <div v-if="modeMenuOpen" class="mode-select-menu" role="listbox" aria-label="选择练习模式">
              <button
                v-for="item in availableModes"
                :key="item.key"
                role="option"
                :aria-selected="mode === item.key"
                :class="{ active: mode === item.key }"
                @click="chooseMode(item.key)"
              >
                <span>{{ item.label }}</span>
                <small>{{ item.hint }}</small>
              </button>
            </div>
          </div>

          <div class="session-stats">
            <span>{{ formattedElapsed }}</span>
            <strong>{{ currentIndex + 1 }} / {{ sentences.length || 0 }}</strong>
          </div>
          <button class="top-icon fullscreen-toggle" :title="fullscreenButtonTitle" @click.stop="toggleFullscreen">
            {{ fullscreenButtonLabel }}
          </button>
          <button class="top-icon" :class="{ active: settingsOpen }" @click.stop="toggleSettingsPanel">设置</button>
          <button class="avatar-button" :class="{ active: accountPanelOpen }" aria-label="打开个人中心" @click.stop="toggleAccountPanel">
            {{ accountInitial }}
          </button>
        </div>
      </header>

      <section v-if="settingsOpen" class="quick-settings" @click.stop>
        <div class="setting-card" @click="toggleSettingMenu('displayStyle')">
          <span>显示风格</span>
          <strong>{{ displayStyleLabel }}</strong>
          <small>月</small>
          <div v-if="activeSettingMenu === 'displayStyle'" class="setting-options">
            <button v-for="option in displayStyleOptions" :key="option.value" :class="{ selected: settings.displayStyle === option.value }" @click.stop="setDisplayStyle(option.value)">
              {{ option.icon }} {{ option.label }}
            </button>
          </div>
        </div>
        <div class="setting-card" @click="toggleSettingMenu('interfaceMode')">
          <span>界面模式</span>
          <strong>{{ interfaceModeLabel }}</strong>
          <small>全</small>
          <div v-if="activeSettingMenu === 'interfaceMode'" class="setting-options">
            <button v-for="option in interfaceModeOptions" :key="option.value" :class="{ selected: settings.interfaceMode === option.value }" @click.stop="setInterfaceMode(option.value)">
              {{ option.label }}
            </button>
          </div>
        </div>
        <div class="setting-card" :class="{ 'setting-card-locked': currentLessonUsesHumanRecording }" @click="!currentLessonUsesHumanRecording && toggleSettingMenu('voice')">
          <span>发音偏好</span>
          <strong>{{ voicePreferenceLabel }}</strong>
          <small>{{ currentLessonUsesHumanRecording ? '真' : '麦' }}</small>
          <div v-if="!currentLessonUsesHumanRecording && activeSettingMenu === 'voice'" class="setting-options">
            <button v-for="voice in voiceOptions" :key="voice.code" :class="{ selected: settings.voiceCode === voice.code }" @click.stop="setVoice(voice.code)">
              {{ voice.label }}
            </button>
          </div>
        </div>
        <div class="setting-card" @click="toggleSettingMenu('speechRate')">
          <span>语音速度</span>
          <strong>{{ speechRateLabel }}</strong>
          <small>速</small>
          <div v-if="activeSettingMenu === 'speechRate'" class="setting-options">
            <button v-for="option in speechRateOptions" :key="option.value" :class="{ selected: settings.speechRate === option.value }" @click.stop="setSpeechRate(option.value)">
              {{ option.label }} · {{ option.hint }}
            </button>
          </div>
        </div>
        <button class="setting-card button-card" @click="toggleBoolean('keyboardSound')">
          <span>键盘音效</span>
          <strong>{{ settings.keyboardSound ? `ON · ${settings.keyboardSoundVolume}%` : "OFF" }}</strong>
          <small>声</small>
        </button>
        <button class="setting-card button-card" @click="toggleBoolean('autoRead')">
          <span>切换句子自动朗读</span>
          <strong>{{ settings.autoRead ? "ON" : "OFF" }}</strong>
          <small>读</small>
        </button>
        <div class="setting-card" @click="toggleSettingMenu('autoRepeat')">
          <span>自动朗读次数</span>
          <strong>{{ settings.autoPlayCount === "manual" ? "1次" : repeatLabel(settings.autoPlayCount) }}</strong>
          <small>循</small>
          <div v-if="activeSettingMenu === 'autoRepeat'" class="setting-options">
            <button v-for="option in answerRepeatOptions" :key="`auto-${option.value}`" :class="{ selected: settings.autoPlayCount === option.value || (settings.autoPlayCount === 'manual' && option.value === 1) }" @click.stop="settings.autoPlayCount = option.value">
              {{ option.label }}
            </button>
          </div>
        </div>
        <div class="setting-card" @click="toggleSettingMenu('answerRepeat')">
          <span>答题反馈播放次数</span>
          <strong>{{ repeatLabel(settings.answerPlayCount) }}</strong>
          <small>声</small>
          <div v-if="activeSettingMenu === 'answerRepeat'" class="setting-options">
            <button v-for="option in answerRepeatOptions" :key="`answer-${option.value}`" :class="{ selected: settings.answerPlayCount === option.value }" @click.stop="settings.answerPlayCount = option.value">
              {{ option.label }}
            </button>
          </div>
        </div>
        <button class="setting-card button-card" @click="toggleBoolean('segmentPractice')">
          <span>分段练习</span>
          <strong>{{ settings.segmentPractice ? "ON" : "OFF" }}</strong>
          <small>分</small>
        </button>
        <button class="setting-card button-card" @click="toggleBoolean('segmentImages')">
          <span>分段图片</span>
          <strong>{{ settings.segmentImages ? "ON" : "OFF" }}</strong>
          <small>图</small>
        </button>
        <button class="setting-card button-card" @click="toggleBoolean('autoNext')">
          <span>自动下一句</span>
          <strong>{{ settings.autoNext ? "ON" : "OFF" }}</strong>
          <small>进</small>
        </button>
        <button class="setting-card button-card" @click="toggleBoolean('puzzleAutoNext')">
          <span>拼图自动下一句</span>
          <strong>{{ settings.puzzleAutoNext ? "ON" : "OFF" }}</strong>
          <small>拼</small>
        </button>
        <button class="setting-card button-card" @click="personalModalOpen = true">
          <span>个性化</span>
          <strong>{{ fontFamilyLabel }} · {{ inputStyleLabel }} · {{ gestureModeLabel }}</strong>
          <small>字</small>
        </button>
        <button class="setting-card button-card" @click="shortcutModalOpen = true">
          <span>快捷键说明</span>
          <strong>查看</strong>
          <small>键</small>
        </button>
        <button class="setting-card button-card" @click="moreSettingsOpen = true">
          <span>更多设置</span>
          <strong>打开</strong>
          <small>...</small>
        </button>
      </section>

      <section v-if="accountPanelOpen" class="account-panel" @click.stop>
        <section v-if="!currentUser" class="drawer-card auth-card">
            <div class="auth-copy">
              <span>手机号验证码登录 / 注册</span>
              <small>先输入手机号，系统会自动识别登录或注册。</small>
            </div>
            <input
              v-model.trim="loginPhone"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              placeholder="请输入手机号"
              :class="{ invalid: loginPhone && !phoneValid }"
            />
            <small v-if="phoneAccountStatus === 'checking'" class="auth-notice">正在识别手机号…</small>
            <input v-if="loginNeedsNickname" v-model.trim="loginNickname" maxlength="30" autocomplete="name" placeholder="新用户请输入昵称 / 姓名（2-30个字）" :class="{ invalid: loginNickname && !nicknameValid }" />
            <div class="auth-row">
              <input v-model.trim="loginCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="6 位验证码" />
              <button :disabled="!loginFormReady || authBusy === 'send' || smsCountdown > 0" @click="handleSendCode">
                {{ authBusy === "send" ? "发送中" : smsCountdown > 0 ? `${smsCountdown}s` : "获取验证码" }}
              </button>
            </div>
            <button class="primary" :disabled="!loginFormReady || loginCode.trim().length < 4 || authBusy === 'login'" @click="handleLoginFromTrialGate">
              {{ authBusy === "login" ? "登录中..." : "验证码登录 / 注册" }}
            </button>
            <button class="wechat-auth" type="button" @click="authNotice = '微信登录待配置，配置后会在这里直接启用。'">微信登录（待配置）</button>
            <small v-if="authNotice" class="auth-notice">{{ authNotice }}</small>
            <small v-else>验证码 5 分钟内有效，请勿向任何人泄露。</small>
        </section>

        <nav class="account-tabs" aria-label="个人中心功能">
          <button :class="{ active: accountTab === 'overview' }" @click="accountTab = 'overview'">概览</button>
          <button :class="{ active: accountTab === 'library' }" @click="accountTab = 'library'">个人库</button>
          <button :class="{ active: accountTab === 'partner' }" @click="accountTab = 'partner'">合伙中心</button>
        </nav>

        <section v-if="accountTab === 'overview'" class="drawer-card quota-card">
          <div class="drawer-card-head">
            <span>今日练习</span>
            <strong>{{ Math.floor(bootstrap?.study.todayMinutes || 0) }} / {{ bootstrap?.study.quotaMinutes || 30 }} 分钟</strong>
          </div>
          <div class="drawer-progress">
            <i :style="{ width: `${dailyQuotaPercent}%` }"></i>
          </div>
          <small>1000小时目标 {{ thousandHourPercent }}%</small>
          <small>连续打卡 {{ learnerActivity?.checkins.streak || 0 }} 天 · 今日复习 {{ learnerActivity?.review.today || reviewSummary?.today || 0 }}</small>
          <div class="quick-action-row">
            <button :disabled="learnerActivityBusy === 'checkin' || learnerActivity?.checkins.todayChecked" @click="handleLearnerCheckin">
              {{ learnerActivity?.checkins.todayChecked ? "今日已打卡" : "今日打卡" }}
            </button>
          </div>
        </section>

        <section v-if="accountTab === 'history'" class="drawer-card learning-history-card">
          <div class="drawer-card-head">
            <span>句包学习记录</span>
            <strong>{{ learnerActivity?.learningHistory.totalPackages || 0 }} 个句包</strong>
          </div>
          <div class="history-summary-grid">
            <span><b>{{ learnerActivity?.learningHistory.learnedSentences || 0 }}</b><small>已学句子</small></span>
            <span><b>{{ learnerActivity?.learningHistory.totalPracticeCount || 0 }}</b><small>累计练习</small></span>
            <span><b>{{ learnerActivity?.learningHistory.completedPackages || 0 }}</b><small>完成句包</small></span>
          </div>
          <div v-if="learnerActivity?.learningHistory.packages.length" class="history-package-list">
            <button
              v-for="item in learnerActivity.learningHistory.packages"
              :key="item.courseId"
              type="button"
              class="history-package-entry"
              :class="{ loading: historyContinueBusyId === item.courseId }"
              :disabled="historyContinueBusyId === item.courseId"
              :aria-busy="historyContinueBusyId === item.courseId"
              :aria-label="`${item.courseTitle}，进度 ${item.progressPercent}%，点击继续学习`"
              @click="continueHistoryPackage(item)"
            >
              <div><b>{{ item.courseTitle }}</b><em>{{ item.progressPercent }}%</em></div>
              <div class="history-progress"><i :style="{ width: `${item.progressPercent}%` }"></i></div>
              <small>已学 {{ item.learnedSentences }} / {{ item.totalSentences }} 句 · 复习 {{ item.reviewCount || 0 }} 次 · 累计练习 {{ item.practiceCount }} 次 · 最近 {{ formatStudyTime(item.lastStudiedAt) }}</small>
              <span class="history-continue-label">{{ historyContinueBusyId === item.courseId ? '正在加载课程与语音…' : `${item.progressPercent >= 100 ? '开始复习' : '继续学习'} →` }}</span>
            </button>
          </div>
          <div v-else class="history-empty">完成一句练习后，这里会自动生成句包进度。</div>
          <div v-if="learnerActivity?.learningHistory.recent.length" class="history-recent-list">
            <strong>最近学过的句子</strong>
            <span v-for="record in learnerActivity.learningHistory.recent.slice(0, 20)" :key="record.id">
              <b>{{ record.sentence }}</b>
              <small>{{ record.courseTitle }} · {{ record.lessonTitle }} · {{ formatStudyTime(record.studiedAt) }}</small>
              <em :class="{ correct: record.correct }">{{ record.correct ? '正确' : `${record.score}分` }}</em>
            </span>
          </div>
        </section>

        <section v-if="accountTab === 'library'" class="drawer-card library-card">
          <div class="drawer-card-head">
            <span>个人库</span>
            <strong>{{ accountLibraryTotal }} 条</strong>
          </div>
          <div class="account-library-tabs" role="tablist" aria-label="个人库分类">
            <button :class="{ active: accountLibraryView === 'ai_packs' }" @click="selectAccountLibrary('ai_packs')"><b>AI训练包</b><span>{{ personalAiTrainingPacks.length }} 包</span></button>
            <button :class="{ active: accountLibraryView === 'mistakes' }" @click="selectAccountLibrary('mistakes')"><b>智能错句库</b><span>{{ smartMistakeLibrary?.active.length || 0 }} 条</span></button>
            <button :class="{ active: accountLibraryView === 'unknown_words' }" @click="selectAccountLibrary('unknown_words')"><b>智能生词库</b><span>{{ smartUnknownWordLibrary?.active.length || 0 }} 条</span></button>
            <button :class="{ active: accountLibraryView === 'personal_words' }" @click="selectAccountLibrary('personal_words')"><b>个人生词库</b><span>{{ personalWordLibraryTotal }} 词</span></button>
          </div>
          <div v-if="accountLibraryView === 'ai_packs'" class="account-ai-pack-library">
            <details class="learning-method-guide">
              <summary>怎样练习、怎样复习？</summary>
              <p>先阅读句意或跟随键盘提示熟悉内容，再切换听写，不看答案检验记忆。照着完成不等于已经掌握。</p>
              <p>独立听写正确按正常难度安排复习；用过答案提示或纠错后完成，不会当成轻松记住。优先练习到期内容，不必每次生成新包。</p>
              <p>每次生成都有独立训练包，按日期和学习内容区分。点“开始复习”重练原内容；更早的内容可继续加载。</p>
              <p>学习时长按前台练习时间累计，登录弹窗、设置页和后台停留不计入。旧版本历史时长无法还原，不会悄悄重算。</p>
              <p>出现“待同步”时记录保留在当前账号的本机浏览器中，恢复网络后重试。同步完成前请勿清除浏览器数据。</p>
            </details>
            <header><div><b>我的 AI 训练包</b><small>每次生成的内容独立保存，可随时回来重复训练</small></div><span>按生成时间倒序</span></header>
            <p v-if="aiHistoryError" role="alert">{{ aiHistoryError }}</p>
            <button v-if="aiHistoryHasMore" class="smart-memory-more" :disabled="aiHistoryBusy" @click="loadMoreAiHistory">{{ aiHistoryBusy ? '正在读取历史…' : '加载更早的训练包' }}</button>
            <article v-for="entry in personalAiTrainingPacks.slice(0, accountLibraryVisibleCount)" :key="entry.pack.id" class="account-ai-pack-card">
              <div class="account-ai-pack-heading">
                <b>{{ adaptiveTrainingPackDisplayName(entry) }}</b>
                <em>{{ entry.pack.energyLabel }}</em>
              </div>
              <p>{{ entry.pack.description }}</p>
              <div class="account-ai-pack-focus"><span v-for="word in entry.pack.focusWords.slice(0, 5)" :key="word">{{ word }}</span></div>
              <div v-if="adaptivePackProgress(entry.pack.id)" class="account-ai-pack-progress">
                <i><em :style="{ width: `${adaptivePackProgress(entry.pack.id)?.progressPercent || 0}%` }"></em></i>
                <span>进度 {{ adaptivePackProgress(entry.pack.id)?.progressPercent }}% · 已复习 {{ adaptivePackProgress(entry.pack.id)?.reviewCount || 0 }} 次</span>
              </div>
              <footer>
                <span>{{ entry.pack.items.length }} 句 · 约 {{ entry.pack.estimatedMinutes }} 分钟 · {{ formatAdaptiveLibraryTime(entry.batch.createdAt) }} 生成</span>
                <button :disabled="Boolean(historyContinueBusyId)" :aria-busy="historyContinueBusyId === `adaptive-pack:${entry.pack.id}`" @click="openPersonalAiTrainingPack(entry)">
                  {{ historyContinueBusyId === `adaptive-pack:${entry.pack.id}` ? '正在加载…' : adaptivePackProgress(entry.pack.id)?.progressPercent === 100 ? '开始复习' : adaptivePackProgress(entry.pack.id) ? '继续学习' : '开始学习' }} →
                </button>
              </footer>
            </article>
            <button v-if="personalAiTrainingPacks.length > accountLibraryVisibleCount" class="smart-memory-more" @click="accountLibraryVisibleCount += 12">再显示 12 个（剩余 {{ personalAiTrainingPacks.length - accountLibraryVisibleCount }} 个）</button>
            <p v-if="!personalAiTrainingPacks.length" class="library-empty">生成 AI 训练包后，会自动保存在这里。</p>
          </div>
          <div v-else-if="activeAccountSmartLibrary" class="account-smart-library-list">
            <header><div><b>{{ activeAccountSmartLibrary.title }}</b><small>按复习时间排序，优先处理到期内容</small></div><span>今日待复习 {{ activeAccountSmartLibrary.dueToday }} · 共 {{ activeAccountSmartLibrary.active.length }} 条</span></header>
            <article v-for="item in activeAccountSmartLibrary.active.slice(0, accountLibraryVisibleCount)" :key="`${item.itemType}-${item.itemId}-${item.skill}`" class="smart-memory-card">
              <div class="smart-memory-heading"><b>{{ item.displayText || (item.itemType === 'word' ? item.itemId : '历史错句') }}</b><em :class="{ due: isMemoryDue(item.dueAt) }">{{ memoryDueLabel(item.dueAt) }}</em></div>
              <p v-if="item.translation">{{ item.translation }}</p>
              <div v-if="item.commonErrors?.length" class="smart-memory-errors"><span>易错</span><i v-for="error in item.commonErrors.slice(0, 4)" :key="error">{{ error }}</i></div>
              <footer><span>{{ learningSkillLabel(item.skill) }} · 错误 {{ item.lapses }} 次 · 已复习 {{ item.reps }} 次</span><small>{{ item.sourceTitle || '学习记录' }}</small></footer>
            </article>
            <button v-if="activeAccountSmartLibrary.active.length > accountLibraryVisibleCount" class="smart-memory-more" @click="accountLibraryVisibleCount += 12">再显示 12 条（剩余 {{ activeAccountSmartLibrary.active.length - accountLibraryVisibleCount }} 条）</button>
            <p v-if="!activeAccountSmartLibrary.active.length" class="library-empty">当前没有待复习内容。</p>
          </div>
          <div v-else class="library-mini-list">
            <article v-for="library in personalWordLibrariesForPanel" :key="library.id">
              <b>{{ personalLibraryDisplayName(library) }}</b>
              <small>{{ library.description }}</small>
              <em>{{ library.items.length }} 条 · 生词</em>
              <span v-for="item in library.items.slice(0, 3)" :key="item.id">{{ item.text }} <i>{{ item.translation }}</i></span>
            </article>
            <p v-if="!personalWordLibrariesForPanel.length" class="library-empty">双击学习页面中的单词，即可加入个人生词库。</p>
          </div>
        </section>

        <section v-if="accountTab === 'overview'" class="drawer-card member-card">
          <div class="drawer-card-head">
            <span>会员中心</span>
            <strong>{{ commerce?.membership?.planName || "免费版" }}</strong>
          </div>
          <small>{{ memberStatusLine }}</small>
          <small v-if="activePaidMembership" class="member-lock-hint">{{ membershipExpiryLabel }}，有效期内不能重复购买。</small>
          <div class="coupon-row">
            <button :disabled="commerceBusy === 'coupon'" @click="handleClaimCoupon('HSNEW30')">领30元券</button>
            <button :disabled="commerceBusy === 'coupon'" @click="handleClaimCoupon('PARTNER100')">领合伙人券</button>
          </div>
          <input v-model="inviteCodeInput" placeholder="邀请码，可选" />
          <div class="plan-actions">
            <button
              v-for="plan in memberPlans"
              :key="plan.key"
              :disabled="isPlanPurchaseDisabled(plan.key)"
              @click="handleBuyPlan(plan.key)"
            >
              <span>{{ plan.name }} ¥{{ plan.price }}</span>
              <small>{{ planButtonHint(plan.key) }}</small>
            </button>
          </div>
          <small v-if="commerceNotice" class="commerce-notice">{{ commerceNotice }}</small>
          <small v-if="latestOrder">最近订单 {{ latestOrder.orderNo }} · {{ latestOrder.status }} · ¥{{ latestOrder.payAmount }}</small>
        </section>

        <section v-if="accountTab === 'partner'" class="drawer-card partner-card">
          <div class="drawer-card-head">
            <span>合伙人中心</span>
            <strong>{{ commerce?.affiliate?.inviteCode || "-" }}</strong>
          </div>
          <small>可提现 ¥{{ commerce?.affiliate?.availableBalance || 0 }} · 累计佣金 ¥{{ commerce?.affiliate?.totalCommission || 0 }}</small>
          <button class="partner-intro-button" @click="affiliateIntroOpen = true">查看推广方案</button>
          <input v-model="affiliatePayoutForm.alipayRealName" placeholder="支付宝实名" />
          <input v-model="affiliatePayoutForm.alipayAccount" placeholder="支付宝账号" />
          <div class="coupon-row">
            <button :disabled="commerceBusy === 'payout'" @click="handleSavePayout">保存收款</button>
            <button :disabled="commerceBusy === 'withdraw'" @click="handleRequestWithdrawal">申请提现</button>
          </div>
          <small v-for="material in commerce?.affiliate?.materials?.slice(0, 2) || []" :key="material.id">{{ material.title }} · {{ material.channel }}</small>
          <small class="affiliate-compliance-line">推广必须真实合规，收益以后台订单和审核规则为准。</small>
        </section>

        <section v-if="openApiStatus?.publicEnabled" class="drawer-card openapi-card">
          <div class="drawer-card-head">
            <span>开放平台</span>
            <strong>{{ openApiStatus.endpointCount }} APIs</strong>
          </div>
          <small>第三方接入、Management API 和学习数据接口说明已公开。</small>
          <button @click="openOpenApiDocs">查看接口文档</button>
        </section>

        <button v-if="currentUser" class="account-logout-button" type="button" @click="handleLogout">退出登录</button>
      </section>

      <section v-if="affiliateIntroOpen" class="affiliate-intro-modal" @click.self="affiliateIntroOpen = false">
        <div class="affiliate-intro-shell" @click.stop>
          <header>
            <div>
              <span>火速英语合伙人</span>
              <h2>把桌面沉浸式英语训练推荐给真正需要的人</h2>
              <p>面向家长、老师、学习社群和英语内容创作者，重点推广“听写 + 打字 + 复习”的可感知体验。</p>
            </div>
            <button @click="affiliateIntroOpen = false">关闭</button>
          </header>

          <div class="affiliate-intro-body">
            <section class="affiliate-value-strip">
              <div>
                <span>首单佣金</span>
                <strong>{{ commerce?.affiliate?.firstOrderRate || 0 }}%</strong>
              </div>
              <div>
                <span>续费佣金</span>
                <strong>{{ commerce?.affiliate?.renewalRate || 0 }}%</strong>
              </div>
              <div>
                <span>最低提现</span>
                <strong>¥{{ commerce?.commercialRules?.minWithdrawalAmount || 0 }}</strong>
              </div>
              <div>
                <span>邀请码</span>
                <strong>{{ commerce?.affiliate?.inviteCode || "-" }}</strong>
              </div>
            </section>

            <section class="affiliate-intro-grid">
              <article>
                <b>为什么容易讲清楚</b>
                <p>用户进来先练一句，听、看、打、纠错、复读形成闭环，比单纯背单词更容易感受到效果。</p>
              </article>
              <article>
                <b>适合推广人群</b>
                <p>英语老师、家长群主、知识博主、社群运营、短视频/直播达人，都可以围绕体验课做转化。</p>
              </article>
              <article>
                <b>推荐卖点</b>
                <p>桌面端专注训练、真实键盘输入、AI 生成课程、完整词库、按教材和场景分类扩展。</p>
              </article>
              <article>
                <b>转化路径</b>
                <p>分享邀请码或体验课素材，用户注册购买后，订单、佣金、提现申请都在后台留痕可追踪。</p>
              </article>
            </section>

            <section class="affiliate-flow">
              <span>推广流程</span>
              <ol>
                <li>复制邀请码，配合后台生成的海报、短视频脚本或直播话术。</li>
                <li>引导用户先体验核心听写/打字训练，不承诺不真实学习效果。</li>
                <li>用户购买后系统自动按订单归因，佣金进入待结算或可提现余额。</li>
                <li>完善支付宝实名信息，达到最低提现金额后提交审核。</li>
              </ol>
            </section>

            <section class="affiliate-risk">
              <b>合规要求</b>
              <p>不得夸大收益、不得诱导未成年人非理性消费、不得刷单套佣、不得冒充官方客服或老师。推广内容必须真实、清晰、可核验。</p>
            </section>
          </div>

          <footer>
            <button @click="copyAffiliateInvite">复制邀请码</button>
            <button class="primary" @click="affiliateIntroOpen = false">开始推广</button>
          </footer>
        </div>
      </section>

      <section v-if="isDesktopShell && desktopUpdateProgress && desktopUpdateProgress.phase !== 'idle'" class="desktop-update-toast" @click.stop>
        <div>
          <span>桌面端更新</span>
          <strong>{{ desktopUpdateProgress.message }}</strong>
        </div>
        <i><b :style="{ width: `${desktopUpdateProgress.percent}%` }"></b></i>
      </section>

      <Teleport to="body">
      <section v-if="trialGateOpen && !currentUser" class="trial-gate" role="dialog" aria-modal="true" aria-label="登录后继续学习" @click.stop>
        <div class="trial-gate-card">
          <button class="trial-gate-close" type="button" aria-label="返回首页" @click="dismissTrialGate">×</button>
          <span>保存你的学习进度</span>
          <strong>登录后继续免费学习</strong>
          <p>登录后即可继续免费学习，同时保存学习进度、练习记录和错题，方便下次接着学。</p>
          <input v-model.trim="loginPhone" type="tel" inputmode="tel" autocomplete="tel" placeholder="请输入手机号" :class="{ invalid: loginPhone && !phoneValid }" />
          <small v-if="phoneAccountStatus === 'checking'" class="auth-notice">正在识别手机号…</small>
          <input v-if="loginNeedsNickname" v-model.trim="loginNickname" maxlength="30" autocomplete="name" placeholder="新用户请输入昵称 / 姓名（2-30个字）" :class="{ invalid: loginNickname && !nicknameValid }" />
          <div class="trial-gate-auth-row">
            <input v-model.trim="loginCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="6 位验证码" @keydown.enter="handleLoginFromTrialGate" />
            <button type="button" :disabled="!loginFormReady || authBusy === 'send' || smsCountdown > 0" @click="handleSendCode">{{ authBusy === "send" ? "发送中" : smsCountdown > 0 ? `${smsCountdown}s` : "获取验证码" }}</button>
          </div>
          <small v-if="authNotice" class="auth-notice" role="alert">{{ authNotice }}</small>
          <div class="trial-gate-actions">
            <button type="button" class="secondary" @click="dismissTrialGate">返回首页</button>
            <button type="button" :disabled="!loginFormReady || loginCode.trim().length < 4 || authBusy === 'login'" @click="handleLoginFromTrialGate">{{ authBusy === "login" ? "登录中…" : "登录并继续免费学习" }}</button>
          </div>
        </div>
      </section>
      </Teleport>

      <section v-if="courseCompletionOpen" class="learning-dialog-layer checkpoint-dialog-layer" @click.stop>
        <div v-if="continuousCourseId === currentCourse?.id" class="checkpoint-celebration" aria-hidden="true">
          <i v-for="spark in checkpointSparks" :key="spark.id" :style="spark.style"></i>
          <b>★</b>
        </div>
        <div class="learning-dialog-card completion-card" :class="{ 'whole-course-checkpoint': continuousCourseId === currentCourse?.id }">
          <div v-if="continuousCourseId === currentCourse?.id" class="checkpoint-badge"><i>✓</i><span>小关通过</span></div>
          <span>{{ isCurrentCourseComplete ? "大包通关" : "恭喜过关" }}</span>
          <strong>{{ isCurrentCourseComplete ? (currentCourse?.title || "本课包") : (currentLesson?.title || "本节") }}已完成</strong>
          <div v-if="currentCourse" class="checkpoint-progress"><i><b :style="{ width: `${courseCheckpointPercent}%` }"></b></i><small>已通过 {{ currentCourseLessonIndex + 1 }} / {{ currentCourse.lessons.length }} 关</small></div>
          <p>{{ isCurrentCourseComplete ? "太棒了，你已完成这个大包的全部小关！" : "成绩已记录。继续下一关，保持学习节奏。" }}</p>
          <div class="completion-actions">
            <button @click="restartCurrentLesson">复习本节</button>
            <button v-if="nextLessonInCurrentCourse" class="primary next-checkpoint-action" @click="continueNextLesson"><kbd>Enter</kbd> 继续下一关</button>
            <button v-else class="primary" @click="openSmartPlanAfterCompletion">查看 AI 下一步计划</button>
            <button @click="openCourseCatalogAfterCompletion">选择其它课包</button>
          </div>
        </div>
      </section>

      <section v-if="visibilityResumeOpen" class="learning-dialog-layer" @click.stop>
        <div class="learning-dialog-card pause-card">
          <span>学习已自动暂停</span>
          <strong>欢迎回来，要继续刚才的内容吗？</strong>
          <p>离开页面期间已暂停语音、动画和学习计时，当前句子与输入进度均已保留。</p>
          <div class="completion-actions">
            <button @click="dismissVisibilityPause(false)">稍后继续</button>
            <button class="primary" @click="dismissVisibilityPause(true)">继续学习</button>
          </div>
        </div>
      </section>

      <section v-if="introDemoVisible" class="intro-demo-layer" @click.stop>
        <div class="intro-demo-copy">
          <span>首次听写演示</span>
          <strong :class="{ typing: introDemoRunning }">{{ introDemoTitle }}<i v-if="introDemoRunning && introDemoPhase === 'typing'"></i></strong>
          <small>{{ introDemoHint }}</small>
          <div v-if="introDemoRunning" class="intro-demo-progress">
            <b :style="{ width: `${introDemoProgress}%` }"></b>
            <em>{{ introDemoProgress }}%</em>
          </div>
        </div>
        <div class="virtual-keyboard" aria-label="虚拟键盘输入演示">
          <div v-for="row in virtualKeyboardRows" :key="row.join('-')" class="keyboard-row">
            <span
              v-for="keyName in row"
              :key="keyName"
              :class="{ active: introDemoActiveKey === keyName, wide: keyName === 'Space' || keyName === 'Enter' }"
            >
              {{ keyName }}
            </span>
          </div>
        </div>
        <div v-if="introShortcutVisible" class="intro-shortcut-panel">
          <b>核心快捷键</b>
          <span>Enter 提交；连续按 3 次 Enter 显示答案</span>
          <span>Ctrl + P 朗读；Ctrl + A 答案；Shift + ←/→ 切换句子</span>
          <div class="intro-demo-actions">
            <button @click="replayIntroDemo">重新演示</button>
            <button @click="finishIntroDemo">开始自己练</button>
          </div>
        </div>
      </section>

      <section class="practice-canvas" :class="[`mode-${mode}`, { 'has-group-progress': courseGroupProgressVisible }]" @click.capture="handlePracticeClick">
        <aside class="global-study-stats" aria-label="累计学习统计">
          <span>已学单词：<b>{{ cumulativeLearnedWords }}</b>词</span>
          <span>已经学句子：<b>{{ cumulativeLearnedSentences }}</b>句</span>
          <span>共学时长：<b>{{ cumulativeStudyTimeLabel }}</b></span>
        </aside>
        <aside
          v-if="courseGroupProgressVisible"
          class="course-group-progress-card"
          :class="{ expanded: courseGroupProgressExpanded }"
          aria-label="课包分组学习进度"
        >
          <button
            type="button"
            class="course-group-progress-toggle"
            :aria-expanded="courseGroupProgressExpanded"
            @click.stop="courseGroupProgressExpanded = !courseGroupProgressExpanded"
          >
            <div>
              <span>{{ activeMode?.label || "学习" }}</span>
              <strong>本课共 {{ courseGroupProgressItems.length }} 组</strong>
            </div>
            <span class="course-group-progress-toggle-meta">
              <b>{{ courseGroupOverallProgress }}%</b>
              <i>{{ courseGroupProgressExpanded ? "收起" : "展开" }}</i>
            </span>
          </button>
          <div class="course-group-progress-summary">
            <span>{{ courseGroupLearnedSentences }}/{{ courseGroupTotalSentences }} 句 · 已完成 {{ completedCourseGroups.length }} 组</span>
            <i><em :style="{ width: `${courseGroupOverallProgress}%` }"></em></i>
          </div>
          <div v-if="courseGroupProgressExpanded" class="course-group-progress-scroll">
            <p v-if="incompleteCourseGroups.length" class="course-group-progress-heading">学习中 · {{ incompleteCourseGroups.length }} 组</p>
            <button
              v-for="item in incompleteCourseGroups"
              :key="item.lessonId"
              type="button"
              class="course-group-progress-item"
              :class="{ current: item.current }"
              @click.stop="selectCourseGroup(item.lessonId)"
            >
              <span><b>第 {{ item.groupNumber }} 组</b><small>{{ item.learnedSentences }}/{{ item.totalSentences }} 句</small></span>
              <strong>{{ item.lessonTitle }}</strong>
              <i><em :style="{ width: `${item.progressPercent}%` }"></em></i>
            </button>
            <details v-if="completedCourseGroups.length" class="course-group-completed">
              <summary>已完成 {{ completedCourseGroups.length }} 组 <span>展开查看</span></summary>
              <button
                v-for="item in completedCourseGroups"
                :key="item.lessonId"
                type="button"
                class="course-group-progress-item completed"
                :class="{ current: item.current }"
                @click.stop="selectCourseGroup(item.lessonId)"
              >
                <span><b>第 {{ item.groupNumber }} 组</b><small>已完成</small></span>
                <strong>{{ item.lessonTitle }}</strong>
              </button>
            </details>
          </div>
        </aside>
        <div v-else class="status-pill">
          <span>{{ activeMode?.label || "学习" }}</span>
          <strong>{{ currentLesson?.title || "未选择课节" }} ({{ currentIndex + 1 }}/{{ sentences.length || 0 }})</strong>
        </div>

        <template v-if="currentSentence">
          <input
            ref="typingInputRef"
            value=""
            class="ghost-type-input"
            spellcheck="false"
            :aria-label="mode === 'typing' ? '打字输入' : '听写输入'"
            @input="handleGhostInput"
            @paste="markPracticeAssisted"
            @keydown="handleTypingKeydown"
          />

          <div v-if="currentIsFilmVideo && currentFilmVideoUrl" class="film-source-stage" aria-label="当前英文原声对应的电影画面">
            <video ref="filmVideoRef" :src="currentFilmVideoUrl" preload="metadata" playsinline disablepictureinpicture></video>
            <span>影视原声 · {{ currentFilmRangeLabel }}</span>
          </div>

          <div class="practice-content" :class="{ 'reviewing-answer': dictationReviewVisible }">
          <p v-if="mode === 'dictation' && audioPreparationNoticeVisible && !trialGateOpen && (!speechNotice || currentAudioLoading)" class="dictation-audio-status" role="status" aria-live="polite">{{ audioPreparationNoticeLabel }}</p>
          <div v-if="mode === 'learning'" class="learning-board" :class="{ 'wordbook-learning-board': currentIsWordbookSentence }">
            <p v-if="settings.showChinese" class="sentence-cn">{{ currentSentence.chinese }}</p>

            <aside
              v-if="wordPopupOpen"
              class="word-meaning-popover"
              :style="wordPopupStyle"
              role="dialog"
              aria-label="单词解释"
              @click.stop
            >
              <header><strong>{{ wordPopupText }}</strong><span v-if="settings.showPhonetic">{{ wordPopupStudy?.phonetic || activeWord?.usPhonetic || activeWord?.ukPhonetic }}</span></header>
              <div class="word-popover-body">
              <p v-if="wordPopupStudy?.explain"><b>本句释义</b>{{ wordPopupStudy.explain }}</p>
              <p v-else-if="wordPopupLoading">正在查询解释…</p>
              <template v-else-if="activeWord">
                <p v-for="meaning in activeWord.meanings.slice(0, 3)" :key="`${meaning.partOfSpeech}-${meaning.zh}`">
                  <b>{{ meaning.partOfSpeech }}</b>{{ meaning.zh }}
                </p>
              </template>
              <p v-else>暂未找到词典解释</p>
              </div>
              <footer><small role="status" aria-live="polite">{{ wordSaveNotice }}</small><button type="button" aria-label="关闭单词解释" @click="closeWordPopup">关闭</button></footer>
            </aside>

            <img
              v-if="settings.segmentImages && currentSentenceImageUrl"
              class="segment-support-image"
              :src="currentSentenceImageUrl"
              :alt="currentSentence.chinese || currentSentence.english"
            />

            <p v-if="speechNotice" class="speech-playback-notice" :class="{ error: speechNoticeIsError }">{{ speechNotice }}</p>

          </div>

          <div
            v-else-if="mode === 'dictation'"
            class="dictation-board"
            :class="{
              'wordbook-input-board': currentIsWordbookSentence,
              'sentence-answer-visible': !currentIsWordbookSentence && showAnswer
            }"
          >
            <p v-if="settings.showChinese" class="sentence-cn">{{ currentSentence.chinese }}</p>

            <div class="blank-row">
              <span
                v-for="state in dictationWordStates"
                :key="`${state.target}-${state.index}`"
                :class="{
                  active: state.index === activeDictationIndex,
                  filled: Boolean(state.typed),
                  correct: state.status === 'correct',
                  mismatch: state.status === 'wrong' || state.status === 'missing' || isDictationSlotMismatch(state.index, state.target),
                  missing: state.status === 'missing',
                  overfilled: isDictationTokenOverfilled(state.index, state.target)
                }"
                :style="{ '--blank-width': `${blankWidth(state.target)}px` }"
                :title="state.typed || state.target"
                @click.stop="selectDictationSlot(state.index)"
              >
                <template v-if="state.index === activeDictationIndex">
                  <b class="dictation-slot-text">{{ state.typed.slice(0, dictationCharacterCursor) }}</b>
                  <i class="dictation-caret" aria-hidden="true"></i>
                  <b class="dictation-slot-text">{{ state.typed.slice(dictationCharacterCursor) }}</b>
                </template>
                <template v-else>{{ state.typed || "" }}</template>
              </span>
            </div>

            <p v-if="dictationInputNotice" class="dictation-input-notice">{{ dictationInputNotice }}</p>

            <p v-if="feedback && !feedback.correct" class="dictation-feedback-brief" role="status">
              <span>还有 {{ dictationWrongStates.length }} 个词需要修正，完成后按 Enter 提交。</span>
              <button v-if="dictationLearningSuggestionOpen" type="button" @click.stop="switchToLearningFromSuggestion">先学习这句 →</button>
            </p>

            <p v-if="speechNotice && !currentAudioLoading" class="speech-playback-notice" :class="{ error: speechNoticeIsError }">{{ speechNotice }}</p>
          </div>

          <div v-else-if="mode === 'puzzle'" class="puzzle-board" :class="{ 'puzzle-wrong': puzzleFailureActive }">
            <p v-if="settings.showChinese" class="sentence-cn">{{ currentSentence.chinese }}</p>
            <div class="puzzle-tray">
              <div class="selected-words">
                <span v-for="id in selectedPuzzleIds" :key="id">{{ wordForPuzzleId(id) }}</span>
                <em v-if="!selectedPuzzleIds.length">点击下方单词拼出完整句子</em>
              </div>
              <div class="tray-tools">
                <button :disabled="practiceSubmitInFlight" @click.stop="undoPuzzleWord">撤销</button>
                <button :disabled="practiceSubmitInFlight" @click.stop="clearPuzzle">清空</button>
              </div>
            </div>
            <div class="candidate-words">
              <button
                v-for="item in puzzleCandidates"
                :key="item.id"
                :class="{ used: selectedPuzzleIds.includes(item.id) }"
                :disabled="selectedPuzzleIds.includes(item.id) || practiceSubmitInFlight"
                @click.stop="appendPuzzleWord(item.id)"
              >
                {{ item.word }}
              </button>
            </div>
          </div>

          <div
            v-else-if="mode === 'typing'"
            class="typing-board"
            :class="{ 'wordbook-input-board': currentIsWordbookSentence }"
          >
            <div class="typing-target-line" aria-label="打字目标英文">
              <span v-for="group in typingCharGroups" :key="group.key" class="typing-word-group">
                <span
                  v-for="item in group.items"
                  :key="`${item.index}-${item.char}`"
                  class="typing-char"
                  :class="{ done: item.done, current: item.current, pending: item.pending, space: item.char === ' ', wrong: typingErrorFlash && item.current }"
                >{{ item.display }}</span>
              </span>
            </div>
            <p v-if="settings.showChinese && feedback?.correct" class="sentence-cn typing-result-translation">{{ currentSentence.chinese }}</p>
            <div ref="typingKeyboardRef" class="finger-keyboard" :class="{ error: typingErrorFlash }">
              <div v-for="row in typingKeyboardRows" :key="row.id" class="finger-keyboard-row">
                <span
                  v-for="key in row.keys"
                  :key="key.code"
                  :data-key-code="key.code"
                  :class="{ active: key.code === activeTypingKey, home: key.home, wide: key.unit > 1.6, ghost: key.ghost }"
                  :style="{ '--key-unit': String(key.unit) }"
                >
                  {{ key.label }}
                </span>
              </div>
              <div class="typing-hand-layer" :class="`gesture-${settings.gestureMode}`" aria-hidden="true">
                <div
                  v-for="finger in typingFingerGuides"
                  :key="finger.id"
                  class="typing-finger"
                  :class="[finger.name, finger.hand, { active: finger.id === activeTypingFinger }]"
                  :style="typingFingerStyle(finger)"
                >
                  <i class="finger-nail"></i>
                  <i class="finger-segment top"></i>
                  <i class="finger-segment middle"></i>
                  <i class="finger-segment base"></i>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="mode === 'speaking'" class="speaking-board">
            <h2>{{ currentSentence.english }}</h2>
            <p v-if="settings.showChinese">{{ currentSentence.chinese }}</p>
            <button class="mic-button" :class="{ recording: isRecording }" :disabled="speechAssessing" @click.stop="toggleRecording">
              <span>{{ speechAssessing ? "识别中…" : isRecording ? "结束录音" : "开始跟读" }}</span>
            </button>
            <div v-if="speechAssessment" class="speaking-result">
              <strong>识别文本一致度 {{ speechAssessment.transcriptMatchScore }}%</strong>
              <span>你读的是：{{ speechAssessment.transcript }}</span>
              <small>{{ speechAssessment.notice }}</small>
            </div>
            <p v-if="speechNotice" class="speech-playback-notice" :class="{ error: speechNoticeIsError }">{{ speechNotice }}</p>
            <small v-if="!speechAssessment">录音仅用于本次识别，不在服务器保存。当前展示的是文本一致度，不冒充发音分。</small>
          </div>

          <section v-if="sentenceStudyDetailsVisible" class="sentence-study-details" :class="{ 'answer-study-details': mode === 'dictation' && showAnswer }">
            <header v-if="dictationReviewVisible" class="answer-review-heading">
              <span>正确答案</span><small>{{ dictationCompletionConfirmed ? '本句已完成，按 Enter 学习下一句' : '可对照答案修改下方输入，按 Enter 提交' }}</small>
            </header>
            <div v-if="studyDictionaryLoading || studyDictionaryError" class="study-dictionary-status" role="status">
              <span>{{ studyDictionaryLoading ? '正在加载逐词音标和释义…' : studyDictionaryError }}</span>
              <button v-if="studyDictionaryError && !studyDictionaryLoading" @click.stop="loadCurrentStudyDictionary">重新加载</button>
            </div>
            <div v-if="studyGrammarStatus && !['completed', 'not_applicable'].includes(studyGrammarStatus.status)" class="study-dictionary-status" role="status" aria-live="polite">
              <span>{{ studyGrammarStatus.status === 'failed' ? '本句语法暂未加载成功，已保存的资料不受影响。' : `${studyGrammarStatus.stage}，首次完成后将保存复用…` }}</span>
              <button v-if="studyGrammarStatus.status === 'failed'" @click.stop="loadCurrentStudyGrammar(true)">重试语法</button>
            </div>
            <div class="sentence-study-vocabulary">
              <div class="sentence-study-vocabulary-title">
                <span>逐词学习</span>
                <small>词性 · 音标 · 释义</small>
              </div>
              <div class="sentence-study-word-list" role="list" aria-label="词段语法与逐词学习资料">
                <section v-for="group in sentenceStudyGroups" :key="group.id" class="study-grammar-group" :class="{ 'has-grammar': !!group.role }" :style="{ '--word-color': group.color }" role="listitem" :aria-label="group.role || '单词资料'">
                  <b class="study-grammar-label" :class="{ 'empty-pos': !group.role }">{{ group.role || '\u00a0' }}</b>
                  <div class="study-grammar-group-words">
                    <article v-for="word in group.words" :key="word.id">
                      <button v-if="mode === 'learning'" type="button" class="study-word-trigger" :aria-label="`查看 ${word.text} 的解释`" @click.stop="handleLearningWordClick(word.text, $event, word.index)"><strong>{{ word.text }}</strong></button>
                      <strong v-else>{{ word.text }}</strong>
                      <small>{{ word.phonetic || (studyDictionaryLoading ? '加载中…' : '词典未收录读音') }}</small>
                      <p>{{ word.explain || (studyDictionaryLoading ? '加载中…' : '词典未收录释义') }}</p>
                      <span v-if="word.label" class="study-lexical-label">{{ word.label }}</span>
                    </article>
                  </div>
                </section>
              </div>
            </div>
          </section>
          </div>

        </template>

        <div v-else-if="!practiceBootLoading" class="empty-board">
          <h2>请选择一个课节开始练习</h2>
          <button @click.stop="toggleSidebar">打开课程分类</button>
        </div>

        <div v-if="celebrationActive" class="celebration-layer" aria-hidden="true">
          <i v-for="spark in celebrationSparks" :key="spark.id" :class="spark.kind" :style="spark.style"></i>
        </div>
        <div v-if="puzzleFailureActive" class="puzzle-failure-layer" aria-hidden="true">
          <span class="failure-mark">×</span>
          <strong>顺序还差一点</strong>
          <small>听清正确句子，再调整单词顺序</small>
        </div>
      </section>

      <footer class="practice-bottom">
        <div v-if="settings.waveform && waveformActive" class="voice-waveform" aria-hidden="true">
          <i
            v-for="bar in waveformBars"
            :key="bar.id"
            :style="{ height: `${bar.height}px`, opacity: bar.opacity }"
          ></i>
        </div>

        <div class="bottom-progress">
          <i :style="{ width: `${lessonProgressPercent}%` }"></i>
        </div>

        <div class="control-dock" aria-label="训练操作与快捷键">
          <button @click.stop="prevSentence">
            <strong>上一句</strong><small v-if="settings.ctrlHints">Shift + ←</small>
          </button>
          <button class="accent" @click.stop="handleSpeak(currentSentence?.english || '')">
            <strong>朗读</strong><small v-if="settings.ctrlHints">Ctrl + P</small>
          </button>
          <button @click.stop="toggleAnswer">
            <strong>{{ showAnswer ? "隐藏答案" : "答案" }}</strong><small v-if="settings.ctrlHints">Ctrl + A</small>
          </button>
          <button @click.stop="handleEnterSubmitShortcut">
            <strong>提交</strong><small v-if="settings.ctrlHints">Enter</small>
          </button>
          <button @click.stop="nextSentence()">
            <strong>下一句</strong><small v-if="settings.ctrlHints">Shift + →</small>
          </button>
        </div>
      </footer>
    </main>

    <div v-if="shortcutModalOpen" class="settings-modal-backdrop" @click="shortcutModalOpen = false">
      <section class="settings-modal small" @click.stop>
        <header>
          <h3>快捷键设置</h3>
          <button @click="shortcutModalOpen = false">关闭</button>
        </header>
        <div class="settings-modal-body">
          <div v-for="shortcut in shortcutRows" :key="shortcut.key" class="shortcut-row">
            <strong>{{ shortcut.label }}</strong>
            <span>{{ shortcut.value }}</span>
          </div>
          <p class="modal-note">当前版本使用固定快捷键，所有快捷键均与练习操作栏保持一致。</p>
        </div>
        <footer>
          <button class="primary" @click="shortcutModalOpen = false">关闭</button>
        </footer>
      </section>
    </div>

    <div v-if="moreSettingsOpen" class="settings-modal-backdrop" @click="moreSettingsOpen = false">
      <section class="settings-modal" @click.stop>
        <header>
          <h3>更多设置</h3>
          <button @click="moreSettingsOpen = false">关闭</button>
        </header>
        <div class="settings-modal-body">
          <section class="settings-group" aria-labelledby="sound-feedback-title">
            <div class="settings-group-head">
              <div>
                <b id="sound-feedback-title">声音与反馈</b>
                <small>分别管理朗读和键盘反馈，调整时即时生效</small>
              </div>
            </div>
            <button class="setting-line button-line" @click="toggleBoolean('keyboardSound')">
              <span><b>键盘反馈音</b><small>输入字母、退格和完成当前题目时播放轻量提示音</small></span>
              <strong>{{ settings.keyboardSound ? "已开启" : "已静音" }}</strong>
            </button>
            <label class="range-line settings-volume-line" :class="{ disabled: !settings.keyboardSound }">
              <span>键盘音量</span>
              <strong>{{ settings.keyboardSoundVolume === 0 ? "静音" : `${settings.keyboardSoundVolume}%` }}</strong>
              <input
                v-model.number="settings.keyboardSoundVolume"
                type="range"
                min="0"
                max="100"
                step="5"
                :disabled="!settings.keyboardSound"
                aria-label="键盘音量"
                @change="previewKeyboardSound"
              />
              <small>拖动后会播放一次试听；0% 为静音，仅影响键盘反馈音</small>
            </label>
          </section>
          <section class="settings-group" aria-labelledby="answer-behavior-title">
            <div class="settings-group-head">
              <div>
                <b id="answer-behavior-title">答题与显示</b>
                <small>控制错误提示、自动进入下一题及学习辅助信息</small>
              </div>
            </div>
          <label class="setting-line">
            <span><b>自动弹答案错误次数</b><small>提交错误达到设定次数后自动显示正确答案</small></span>
            <select v-model.number="settings.errorPopupCount">
              <option v-for="count in [1, 2, 3, 4, 5]" :key="count" :value="count">{{ count }} 次</option>
            </select>
          </label>
          <button v-for="item in moreSettingRows" :key="item.key" class="setting-line button-line" @click="toggleBoolean(item.key)">
            <span><b>{{ item.label }}</b><small>{{ item.desc }}</small></span>
            <strong>{{ settings[item.key] ? "已开启" : "已关闭" }}</strong>
          </button>
          </section>
        </div>
        <footer>
          <button @click="restoreSettings">恢复默认</button>
          <button class="primary" @click="moreSettingsOpen = false">应用设置</button>
        </footer>
      </section>
    </div>

    <div v-if="personalModalOpen" class="settings-modal-backdrop" @click="personalModalOpen = false">
      <section class="settings-modal" @click.stop>
        <header>
          <h3>个性化设置</h3>
          <button @click="personalModalOpen = false">关闭</button>
        </header>
        <div class="settings-modal-body">
          <div class="choice-section">
            <b>输入框字体</b>
            <div class="choice-grid">
              <button v-for="font in fontOptions" :key="font.value" :class="{ selected: settings.fontFamily === font.value }" @click="settings.fontFamily = font.value">
                Aa {{ font.label }}
              </button>
            </div>
          </div>
          <label class="range-line">
            <span>字号大小</span>
            <strong>{{ settings.fontSize.toFixed(1) }}rem</strong>
            <input v-model.number="settings.fontSize" type="range" min="1.6" max="3.4" step="0.1" />
            <small>小　　中　　大　　超大</small>
          </label>
          <div class="choice-section">
            <b>输入框样式</b>
            <div class="choice-grid">
              <button v-for="style in inputStyleOptions" :key="style.value" :class="{ selected: settings.inputStyle === style.value }" @click="settings.inputStyle = style.value">
                {{ style.label }}
              </button>
            </div>
          </div>
          <div class="choice-section">
            <b>打字手势</b>
            <div class="choice-grid gesture-choice-grid">
              <button
                v-for="gesture in gestureOptions"
                :key="gesture.value"
                :class="{ selected: settings.gestureMode === gesture.value }"
                @click="settings.gestureMode = gesture.value"
              >
                <span>{{ gesture.label }}</span>
                <small>{{ gesture.desc }}</small>
              </button>
            </div>
          </div>
          <div class="personal-preview" :class="`input-${settings.inputStyle}`">
            <span>Hello</span>
          </div>
        </div>
        <footer>
          <button @click="restoreSettings">恢复默认</button>
          <button class="primary" @click="personalModalOpen = false">应用设置</button>
        </footer>
      </section>
    </div>
    <div v-if="wechatPayPreparing" class="settings-modal-backdrop wechat-pay-backdrop">
      <section class="settings-modal small wechat-pay-modal wechat-pay-preparing" role="status" aria-live="polite">
        <div class="wechat-pay-loader"></div>
        <strong>正在连接微信支付</strong>
        <small>{{ wechatPayPreparing }}</small>
      </section>
    </div>
    <div v-if="wechatPayModal" class="settings-modal-backdrop wechat-pay-backdrop" @click="cancelWechatPayment">
      <section class="settings-modal small wechat-pay-modal" @click.stop>
        <header>
          <div>
            <strong>微信扫码支付</strong>
            <small>请使用微信扫描二维码完成支付</small>
          </div>
          <div class="wechat-pay-header-actions"><span>待支付</span><button class="wechat-pay-close" aria-label="关闭并取消支付" :disabled="wechatPayModal.refreshing" @click="cancelWechatPayment">×</button></div>
        </header>
        <div class="settings-modal-body wechat-pay-body">
          <h3>{{ wechatPayModal.planName }}</h3>
          <div class="wechat-pay-qr"><img :src="wechatPayModal.qrDataUrl" alt="微信支付二维码" /></div>
          <strong class="wechat-pay-amount">¥{{ wechatPayModal.amount.toFixed(2) }}</strong>
          <small>订单号：{{ wechatPayModal.orderNo }}</small>
          <p class="wechat-pay-status">{{ wechatPayModal.statusText }}</p>
        </div>
        <footer>
          <button :disabled="wechatPayModal.refreshing" @click="cancelWechatPayment">取消支付</button>
          <button class="primary" :disabled="wechatPayModal.refreshing" @click="() => refreshWechatPaymentStatus()">
            {{ wechatPayModal.refreshing ? "正在查询…" : "我已完成支付" }}
          </button>
        </footer>
      </section>
    </div>

    <Teleport to="body">
      <div v-if="pwaInstallGuideOpen" class="pwa-install-mask" role="presentation" @click.self="closePwaInstallGuide">
        <section class="pwa-install-dialog" role="dialog" aria-modal="true" aria-labelledby="pwa-install-title">
          <header class="pwa-install-header">
            <div>
              <span class="pwa-install-eyebrow">火速英语 · 快捷启动</span>
              <h2 id="pwa-install-title">添加至桌面</h2>
              <p>独立窗口启动、自动更新学习内容，无需下载额外安装包。</p>
            </div>
            <button class="pwa-install-close" aria-label="关闭添加至桌面指引" @click="closePwaInstallGuide">×</button>
          </header>

          <p class="pwa-install-status" :class="{ ready: pwaInstallReady, installed: pwaStandalone }">
            {{ pwaInstallStatus }}
          </p>

          <nav class="pwa-install-tabs" aria-label="选择设备类型">
            <button
              v-for="item in pwaGuideTabs"
              :key="item.key"
              :class="{ active: pwaGuideTarget === item.key }"
              @click="pwaGuideTarget = item.key"
            >
              <span>{{ item.icon }}</span>{{ item.label }}
            </button>
          </nav>

          <section class="pwa-guide-content" :aria-label="pwaActiveGuide.title">
            <h3>{{ pwaActiveGuide.title }}</h3>
            <p>{{ pwaActiveGuide.summary }}</p>
            <ol>
              <li v-for="(step, index) in pwaActiveGuide.steps" :key="step.title">
                <b>{{ index + 1 }}</b>
                <div><strong>{{ step.title }}</strong><span>{{ step.description }}</span></div>
              </li>
            </ol>
          </section>

          <footer class="pwa-install-footer">
            <button v-if="pwaGuideTarget === 'chrome' && pwaInstallReady" class="pwa-install-primary" @click="requestPwaInstall">立即安装火速英语</button>
            <button class="pwa-install-secondary" @click="closePwaInstallGuide">关闭指引</button>
          </footer>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import QRCode from "qrcode";
import { safeSentenceStructure, type PracticeSubmission } from "@huosu/shared";
import { getSavedTrainingPack, getPublicMembershipPlans } from "./api";
import { PracticeOutbox } from "./practice-outbox";
import { learningPresentation, studyWordDetail } from "./learning-presentation";
import { alignedSentenceIpa, annotateStudyWord, studyKey, type StudyDictionaryEntry } from "./study-annotations";
import { conciseStudyMeaning } from "./study-context";
import { sentenceAnnotationIssues } from '@huosu/shared';
import { groupStudyWords, studyGrammarRoles } from "./study-grammar";
import { hasCompleteGrammar, requiresSentenceGrammar, grammarTokens, type GrammarResult } from "@huosu/shared";
import { loadSentenceGrammar } from "./api";
import type { AdaptivePlanItem, AdaptiveTrainingBatch, AdaptiveTrainingPack, AiAssistantContextReply, AiGenerationPricing, CommerceDashboard, ContentCategory, Course, DesktopDownloadCenter, DesktopInstallationStatus, DesktopPlatform, LearnerActivityDashboard, LearnerProfile, LearningSkill, Lesson, MembershipPlanKey, OrderRecord, PracticeMode, RegisteredUser, Sentence, SpeechRateValue, VoiceAsset, VoiceCode, WordEntry } from "@huosu/shared";
import {
  addPersonalLibraryItem,
  askAiAssistant,
  claimCoupon,
  createAiCreditRechargeOrder,
  closeAiCreditRechargeOrder,
  payAiCreditRechargeOrder,
  createMembershipOrder,
  closeMembershipOrder,
  getBootstrap,
  getCourseDetail,
  clearExpiredUserSession,
  USER_AUTH_EXPIRED_EVENT,
  getCommerceMe,
  getDesktopDownloadCenter,
  getDesktopInstallationStatus,
  getMe,
  getOpenApiStatus,
  getMembershipOrder,
  getPracticeLesson,
  getPracticeLocationBySentenceId,
  getPhoneAccountStatus,
  getLearnerActivity,
  getReviewSummary,
  getAdaptiveLearningHome,
  getAdaptiveTrainingPackBatches,
  getAdaptiveTrainingGenerationPricing,
  getAdaptiveTrainingPackBatch,
  getLatestAdaptiveTrainingPackBatch,
  openDailyAdaptiveTraining,
  regenerateAdaptivePlan,
  saveLearnerProfile,
  submitPlacementAssessment,
  startAdaptiveTrainingPackGeneration,
  learnerCheckin,
  loginByPhone,
  logout,
  payMembershipOrder,
  registerDesktopInstallation,
  requestWithdrawal,
  searchWords,
  getStudyDictionary,
  assessSpeech,
  ensureTtsAudio,
  sendCode,
  submitPracticeAnswer,
  trackDesktopDownload,
  updateAffiliatePayout,
  type BootstrapData,
  type AdaptiveLearningHome
} from "./api";

type PracticeFeedback = { correct: boolean; score: number; feedback: string; wrongWords?: string[] };
type SpeechAssessment = { transcript: string; transcriptMatchScore: number; notice: string; fallbackUsed: boolean };
type ModeMeta = { key: PracticeMode; label: string; hint: string };
type SegmentView = { id: string; label: string; text: string; explain: string; phonetic: string; color: string; partOfSpeech?: string; fixedPunctuation?: boolean };
type PuzzleCandidate = { id: string; word: string; order: number };
type DictationWordStatus = "empty" | "missing" | "partial" | "wrong" | "correct";
type DictationWordState = { index: number; target: string; typed: string; status: DictationWordStatus };
type TypingFingerId = "lp" | "lr" | "lm" | "li" | "lt" | "rt" | "ri" | "rm" | "rr" | "rp";
type TypingKey = { code: string; label: string; unit: number; finger?: TypingFingerId; home?: boolean; ghost?: boolean };
type TypingFingerGuide = {
  id: TypingFingerId;
  hand: "left" | "right";
  name: string;
  left: number;
  bottom: number;
  height: number;
  width: number;
  rotate: number;
  z: number;
};
type TypingKeyTarget = { x: number; bottom: number; height: number; keyHeight: number };
type DisplayStyle = "dark" | "lightStandard" | "lightPure" | "lightGray";
type InterfaceMode = "standard" | "compact" | "wide";
type RepeatCount = "manual" | 1 | 2 | 3 | 4 | 5;
type SpeechPlaybackOrigin = "manual" | "automatic";
type GestureMode = "realistic" | "standard";
type LearnerSettings = {
  displayStyle: DisplayStyle;
  interfaceMode: InterfaceMode;
  voiceCode: VoiceCode;
  speechRate: SpeechRateValue;
  keyboardSound: boolean;
  keyboardSoundVolume: number;
  autoRead: boolean;
  autoPlayCount: RepeatCount;
  answerPlayCount: 1 | 2 | 3 | 4 | 5;
  segmentPractice: boolean;
  segmentImages: boolean;
  autoNext: boolean;
  puzzleAutoNext: boolean;
  fontFamily: "default" | "sans" | "rounded" | "serif";
  fontSize: number;
  inputStyle: "underline" | "box";
  gestureMode: GestureMode;
  showPhonetic: boolean;
  showChinese: boolean;
  answerSmoke: boolean;
  waveform: boolean;
  ctrlHints: boolean;
  errorPopupCount: number;
};

type VoiceOption = {
  code: VoiceCode;
  label: string;
  locale: "en-US" | "en-GB";
  gender: "male" | "female" | "child";
  browserHints: string[];
  avoidHints: string[];
  pitch: number;
  rate: number;
};
type SpeechRateOption = {
  value: SpeechRateValue;
  label: string;
  hint: string;
  rateMultiplier: number;
  providerSpeed: number;
};

type GuestTrialState = {
  completedSentenceIds: string[];
  completedCount: number;
  locked: boolean;
  updatedAt: string;
};

type ClientAudioAsset = VoiceAsset;

type BooleanSettingKey = {
  [K in keyof LearnerSettings]: LearnerSettings[K] extends boolean ? K : never;
}[keyof LearnerSettings];

const FREE_TRIAL_LIMIT = 3;
const GUEST_TRIAL_STORAGE_KEY = "huosu_guest_trial_state_v1";
const AUTH_USER_CACHE_KEY = "huosu_authenticated_user_v1";
const PRACTICE_LESSON_CACHE_KEY = "huosu_current_practice_lesson_v1";
const PRACTICE_POSITION_CACHE_KEY = "huosu_current_practice_position_v1";
// 桌面安装包需在完成签名与上架合规后才对外开放；PWA 添加至桌面不受影响。
const DESKTOP_CLIENT_DOWNLOAD_ENABLED = false;
const hasStoredAuthToken = Boolean(localStorage.getItem("huosu_user_token"));
const isDesktopShell = Boolean(window.huosuLearningDesktop);

function loadCachedAuthenticatedUser(): RegisteredUser | null {
  if (!hasStoredAuthToken) return null;
  try {
    const cached = JSON.parse(localStorage.getItem(AUTH_USER_CACHE_KEY) || "null") as RegisteredUser | null;
    return cached?.id && cached.status === "active" ? cached : null;
  } catch {
    return null;
  }
}

function cacheAuthenticatedUser(user: RegisteredUser | null) {
  if (user?.id) localStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(user));
  else localStorage.removeItem(AUTH_USER_CACHE_KEY);
}

function resetExpiredUserSession() {
  currentUser.value = null;
  cacheAuthenticatedUser(null);
  localStorage.removeItem(PRACTICE_LESSON_CACHE_KEY);
  localStorage.removeItem(PRACTICE_POSITION_CACHE_KEY);
  currentCourse.value = null;
  currentLesson.value = null;
  landingVisible.value = true;
  dailyRecommendationOpen.value = false;
  trialGateOpen.value = false;
}

// 游客同样会进入完整的真人录音句子包；刷新页面时不能把他直接丢回
// 默认课程。使用浏览器本机的游客标识，既能恢复当前包，也不会与登录
// 用户的学习位置混在一起。
function practiceCacheOwnerId() {
  return currentUser.value?.id || "guest";
}

const defaultSettings: LearnerSettings = {
  displayStyle: "dark",
  interfaceMode: "standard",
  voiceCode: "us_male",
  speechRate: "normal",
  keyboardSound: true,
  keyboardSoundVolume: 45,
  autoRead: true,
  autoPlayCount: "manual",
  answerPlayCount: 3,
  segmentPractice: false,
  segmentImages: false,
  autoNext: true,
  puzzleAutoNext: true,
  fontFamily: "default",
  fontSize: 2.4,
  inputStyle: "underline",
  gestureMode: "realistic",
  showPhonetic: true,
  showChinese: true,
  answerSmoke: true,
  waveform: true,
  ctrlHints: true,
  errorPopupCount: 3
};

const bootstrap = ref<BootstrapData | null>(null);
// 课程编辑台发布真人录音句子包后，会用这个受控参数直达首个课节，供
// 审核人员确认学习端播放的是对应真人句子音频，而不是 AI 配音。
const reviewPreviewCourseId = new URLSearchParams(window.location.search).get("reviewCourseId")?.trim() || "";
const currentUser = ref<RegisteredUser | null>(loadCachedAuthenticatedUser());
const landingPlans = ref<import("@huosu/shared").MembershipPlan[]>([]);
const landingPlansBusy = ref(false);
const landingPlansError = ref("");
async function loadLandingPlans() {
  if (landingPlansBusy.value) return;
  landingPlansBusy.value = true;
  landingPlansError.value = "";
  try {
    landingPlans.value = await getPublicMembershipPlans();
    if (!landingPlans.value.length) landingPlansError.value = "当前未发布套餐，可先免费体验。";
  } catch { landingPlans.value = []; landingPlansError.value = "套餐暂时读取失败，请重试；不会以演示价格替代。"; }
  finally { landingPlansBusy.value = false; }
}
const loginPhone = ref("");
const loginNickname = ref("");
const loginCode = ref("");
const authBusy = ref<"" | "send" | "login">("");
const authNotice = ref("");
const smsCountdown = ref(0);
const normalizedLoginPhone = computed(() => loginPhone.value.replace(/[^\d]/g, ""));
const phoneAccountStatus = ref<"idle" | "checking" | "existing" | "new" | "error">("idle");
const loginNeedsNickname = computed(() => phoneAccountStatus.value === "new");
const nicknameValid = computed(() => {
  const nickname = loginNickname.value.replace(/\s+/g, " ").trim();
  return nickname.length >= 2 && nickname.length <= 30 && nickname !== "火速学员";
});
const phoneValid = computed(() => /^1[3-9]\d{9}$/.test(normalizedLoginPhone.value));
const loginFormReady = computed(() => phoneValid.value
  && (phoneAccountStatus.value === "existing" || phoneAccountStatus.value === "new")
  && (!loginNeedsNickname.value || nicknameValid.value));
let phoneStatusTimerId: number | undefined;
let phoneStatusRequestId = 0;
let lastRecognizedPhone = "";
async function recognizeLoginPhone() {
  const phone = normalizedLoginPhone.value;
  if (!/^1[3-9]\d{9}$/.test(phone)) return false;
  if (lastRecognizedPhone === phone && (phoneAccountStatus.value === "existing" || phoneAccountStatus.value === "new")) return true;
  const requestId = ++phoneStatusRequestId;
  phoneAccountStatus.value = "checking";
  authNotice.value = "正在识别手机号…";
  try {
    const status = await getPhoneAccountStatus(phone);
    if (requestId !== phoneStatusRequestId || phone !== normalizedLoginPhone.value) return false;
    lastRecognizedPhone = phone;
    phoneAccountStatus.value = status.needsNickname ? "new" : "existing";
    authNotice.value = status.needsNickname ? "这是新手机号，请填写昵称后获取验证码。" : "欢迎回来，获取验证码即可登录。";
    return true;
  } catch (error) {
    if (requestId !== phoneStatusRequestId) return false;
    phoneAccountStatus.value = "error";
    authNotice.value = error instanceof Error ? error.message : "手机号识别失败，请稍后重试。";
    return false;
  }
}
watch(normalizedLoginPhone, (phone, previousPhone) => {
  if (phone === previousPhone) return;
  if (phoneStatusTimerId) window.clearTimeout(phoneStatusTimerId);
  phoneStatusRequestId += 1;
  lastRecognizedPhone = "";
  phoneAccountStatus.value = "idle";
  loginCode.value = "";
  smsCountdown.value = 0;
  if (/^1[3-9]\d{9}$/.test(phone)) {
    phoneStatusTimerId = window.setTimeout(() => void recognizeLoginPhone(), 250);
  } else {
    authNotice.value = "";
  }
});
const reviewSummary = ref<any>(null);
const learnerActivity = ref<LearnerActivityDashboard | null>(null);
const resumeNotice = ref("");
let resumeNoticeTimerId: number | undefined;
watch(resumeNotice, (notice) => {
  if (resumeNoticeTimerId) window.clearTimeout(resumeNoticeTimerId);
  resumeNoticeTimerId = undefined;
  if (!notice) return;
  resumeNoticeTimerId = window.setTimeout(() => {
    resumeNotice.value = "";
    resumeNoticeTimerId = undefined;
  }, 10_000);
});
const adaptiveHome = ref<AdaptiveLearningHome | null>(null);
const onboardingOpen = ref(false);
const smartPlanOpen = ref(false);
const goalEditorOpen = ref(false);
const goalSaving = ref(false);
const adaptivePackCount = ref(60);
const adaptiveGenerationPricing = ref<AiGenerationPricing | null>(null);
const adaptiveGenerationFallbackOptions = [
  { contentCount: 60, estimatedPackCount: 1, creditCost: 30, label: "轻量扩充", description: "生成60句，整理为1个连续学习包" },
  { contentCount: 200, estimatedPackCount: 2, creditCost: 80, label: "系统强化", description: "生成200句，整理为2个连续学习包" },
  { contentCount: 500, estimatedPackCount: 3, creditCost: 180, label: "深度进阶", description: "生成500句，整理为3个连续学习包" },
  { contentCount: 1000, estimatedPackCount: 5, creditCost: 320, label: "长期训练", description: "生成1000句，整理为5个连续学习包" }
];
const adaptiveGenerationOptions = computed(() => adaptiveGenerationPricing.value?.options?.length
  ? adaptiveGenerationPricing.value.options
  : adaptiveGenerationFallbackOptions);
const aiCreditRechargeOpen = ref(false);
const adaptiveBusy = ref(false);
const onboardingNotice = ref("");
const adaptiveChoiceOpen = ref(false);
const adaptiveTrainingActive = ref(false);
const adaptiveTrainingBatch = ref<AdaptiveTrainingBatch | null>(null);
const adaptiveTrainingBatches = ref<AdaptiveTrainingBatch[]>([]);
const aiHistoryHasMore = ref(false);
const aiHistoryBusy = ref(false);
const aiHistoryError = ref("");
let aiHistoryOffset = 0;
async function loadMoreAiHistory() {
  if (aiHistoryBusy.value || !currentUser.value) return;
  const userId = currentUser.value.id;
  aiHistoryBusy.value = true;
  aiHistoryError.value = "";
  try {
    const page = await getAdaptiveTrainingPackBatches(aiHistoryOffset);
    if (currentUser.value?.id !== userId) return;
    for (const batch of page) upsertAdaptiveTrainingBatch(batch);
    aiHistoryOffset += page.length;
    aiHistoryHasMore.value = page.length === 50;
    accountLibraryVisibleCount.value += 12;
  } catch { aiHistoryError.value = "历史训练包加载失败，请重试。"; }
  finally { aiHistoryBusy.value = false; }
}
const adaptiveTrainingPackBusy = ref(false);
const smartPlanDetail = ref<"history" | null>(null);
const accountLibraryView = ref<"ai_packs" | "mistakes" | "unknown_words" | "personal_words">("ai_packs");
const accountLibraryVisibleCount = ref(12);
const dailyRecommendationOpen = ref(false);
const dailyRecommendationPreparing = ref(false);
const dailyRecommendationAction = ref<"previous" | "today" | null>(null);
const dailyRecommendationChoiceCommitted = ref(false);
const dailyRecommendationBaseReady = ref(false);
const dailyRecommendationError = ref("");
const dailyRecommendationPreparationPercent = ref(10);
const dailyRecommendationPreparationLabel = ref("正在读取今日学习计划");
const dailyRecommendationReady = computed(() => dailyRecommendationOpen.value && dailyRecommendationBaseReady.value && !dailyRecommendationPreparing.value && !dailyRecommendationAction.value);
const dailyRecommendationProgressPercent = computed(() => {
  if (!dailyRecommendationPreparing.value) return 100;
  if (lessonAudioPreparingBlocking.value) {
    return Math.min(99, Math.max(dailyRecommendationPreparationPercent.value, 90 + Math.round(lessonAudioPreparationPercent.value * 0.09)));
  }
  return dailyRecommendationPreparationPercent.value;
});
const dailyRecommendationProgressLabel = computed(() => {
  if (!dailyRecommendationPreparing.value) return "今日推荐已准备完成";
  return lessonAudioPreparingBlocking.value ? lessonAudioPreparationLabel.value : dailyRecommendationPreparationLabel.value;
});

function dailyRecommendationStepClass(target: number) {
  const progress = dailyRecommendationProgressPercent.value;
  return {
    "is-complete": progress >= target,
    "is-active": progress < target && (target === 30 || progress >= (target === 55 ? 30 : target === 75 ? 55 : 75))
  };
}
const adaptiveTrainingPollError = ref(0);
const activeGeneratedPack = ref<AdaptiveTrainingPack | null>(null);
const adaptivePackListRef = ref<HTMLElement | null>(null);
const recentGeneratedBatchId = ref<string | null>(null);
let adaptiveTrainingPollTimer: number | undefined;
const selectedAdaptiveGenerationOption = computed(() => adaptiveGenerationOptions.value.find((option) => option.contentCount === adaptivePackCount.value)
  || adaptiveGenerationOptions.value[0]);
const adaptiveCreditBalance = computed(() => adaptiveGenerationPricing.value?.wallet.balance ?? commerce.value?.aiCredits?.balance ?? 0);
const adaptiveCreditInsufficient = computed(() => Boolean(selectedAdaptiveGenerationOption.value && adaptiveCreditBalance.value < selectedAdaptiveGenerationOption.value.creditCost));
const adaptiveBatchStatusLabel = computed(() => {
  const status = adaptiveTrainingBatch.value?.status;
  return status === "completed" ? "生成完成" : status === "blocked" ? "已停止：等待配置" : status === "failed" ? "生成失败" : status === "queued" ? "任务排队中" : "正在生成";
});
const adaptiveBatchStatusIcon = computed(() => adaptiveTrainingBatch.value?.status === "completed" ? "✓" : adaptiveTrainingBatch.value?.status === "blocked" ? "!" : adaptiveTrainingBatch.value?.status === "failed" ? "×" : "●");
const adaptiveGenerateButtonLabel = computed(() => {
  if (!adaptiveTrainingPackBusy.value) {
    if (adaptiveTrainingBatch.value?.status === "blocked") return "配置后重新生成";
    if (adaptiveTrainingBatch.value?.status === "failed") return "重新生成";
    if (adaptiveCreditInsufficient.value) return "积分不足";
    const cost = selectedAdaptiveGenerationOption.value?.creditCost;
    return cost ? `${cost} 积分生成` : adaptiveTrainingBatch.value?.packs.length ? "再生成一批" : "生成训练包";
  }
  const percent = adaptiveTrainingBatch.value?.percent || 0;
  const stage = adaptiveTrainingBatch.value?.stage;
  const label = stage === "quality_check" ? "正在质检" : stage === "response_parse" ? "正在解析" : stage === "model_check" ? "检查模型" : stage === "prompt_build" ? "准备要求" : stage === "word_selection" ? "筛选生词" : stage === "ai_generation" ? "AI 生成中" : "正在创建";
  return `${label} ${percent}%`;
});
const adaptiveBatchStageLabel = computed(() => ({
  profile: "读取学习画像", word_selection: "筛选焦点词", model_check: "检查生成模型", prompt_build: "组装训练要求",
  ai_generation: "等待 AI 返回内容", response_parse: "解析生成结果", quality_check: "逐包质量检查", completed: "训练包已就绪"
}[adaptiveTrainingBatch.value?.stage || "profile"]));
const adaptiveBatchElapsed = computed(() => {
  elapsedSeconds.value;
  const batch = adaptiveTrainingBatch.value;
  if (!batch) return "0秒";
  const end = ["completed", "failed", "blocked"].includes(batch.status) ? Date.parse(batch.updatedAt) : Date.now();
  const seconds = Math.max(0, Math.floor((end - Date.parse(batch.createdAt)) / 1000));
  return seconds >= 60 ? `${Math.floor(seconds / 60)}分${seconds % 60}秒` : `${seconds}秒`;
});
const availableAdaptivePacks = computed(() => {
  const seen = new Set<string>();
  return adaptiveTrainingBatches.value
    .filter((batch) => batch.status === "completed")
    .flatMap((batch) => batch.packs.map((pack) => ({ pack, batch })))
    .filter(({ pack }) => !seen.has(pack.id) && Boolean(seen.add(pack.id)));
});
type PersonalAiTrainingPack = { pack: AdaptiveTrainingPack; batch: AdaptiveTrainingBatch; packIndex: number };
const personalAiTrainingPacks = computed<PersonalAiTrainingPack[]>(() => {
  const seen = new Set<string>();
  return adaptiveTrainingBatches.value
    .filter((batch) => batch.status === "completed")
    .flatMap((batch) => batch.packs.map((pack, packIndex) => ({ pack, batch, packIndex })))
    .filter(({ pack }) => !seen.has(pack.id) && Boolean(seen.add(pack.id)))
    .sort((a, b) => b.batch.createdAt.localeCompare(a.batch.createdAt));
});
const availableAdaptivePackGroups = computed(() => {
  const seen = new Set<string>();
  return adaptiveTrainingBatches.value
    .filter((batch) => batch.status === "completed")
    .map((batch) => ({
      batch,
      entries: batch.packs
        .map((pack, packIndex) => ({ pack, batch, packIndex }))
        .filter(({ pack }) => !seen.has(pack.id) && Boolean(seen.add(pack.id)))
    }))
    .filter((group) => group.entries.length);
});
const onboardingStep = ref<1 | 2>(1);
const profileForm = ref<Partial<LearnerProfile>>({ currentLevel: "A1", targetLevel: "A2", goalType: "daily", dailyMinutes: 20, weakSkills: ["listening", "spelling"], preferredTopics: [] });
const fallbackLevelOptions: Array<{ value: LearnerProfile["currentLevel"]; label: string; description: string }> = [
  { value: "零基础", label: "零基础", description: "几乎没学过英语" },
  { value: "A1", label: "入门", description: "会少量单词和简单问候" },
  { value: "A2", label: "基础", description: "能应付购物、问路等日常对话" },
  { value: "B1", label: "中级", description: "能说经历、计划并读懂一般文章" },
  { value: "B2", label: "中高级", description: "能比较流利交流和表达观点" },
  { value: "C1", label: "高级", description: "能在学习、工作中熟练使用英语" }
];
const fallbackGoalOptions: Array<{ value: LearnerProfile["goalType"]; label: string }> = [{ value: "daily", label: "日常交流" }, { value: "ket", label: "KET考试" }, { value: "pet", label: "PET考试" }, { value: "school", label: "校内提升" }, { value: "travel", label: "旅行英语" }, { value: "career", label: "职场英语" }, { value: "parent_child", label: "亲子英语" }];
const fallbackSkillOptions: Array<{ value: LearningSkill; label: string }> = [{ value: "vocabulary", label: "词汇" }, { value: "listening", label: "听力" }, { value: "spelling", label: "拼写" }, { value: "speaking", label: "口语" }, { value: "reading", label: "阅读" }, { value: "writing", label: "写作" }];
const levelOptions = computed(() => adaptiveHome.value?.config.levels.filter((item) => item.enabled).sort((a, b) => a.sortOrder - b.sortOrder) || fallbackLevelOptions);
const goalOptions = computed(() => adaptiveHome.value?.config.goals.filter((item) => item.enabled).sort((a, b) => a.sortOrder - b.sortOrder) || fallbackGoalOptions);
const skillOptions = computed(() => adaptiveHome.value?.config.skills.filter((item) => item.enabled).sort((a, b) => a.sortOrder - b.sortOrder) || fallbackSkillOptions);
const dailyMinuteOptions = computed(() => adaptiveHome.value?.config.dailyMinutes || [10, 20, 30, 45, 60]);
const adaptiveRecommendations = computed(() => adaptiveHome.value?.recommendations?.slice(0, 3) || []);
const placementScoreOptions = [{ value: 30, label: "刚起步" }, { value: 50, label: "能做一些" }, { value: 70, label: "基本稳定" }, { value: 90, label: "很有把握" }];
const placementScores = ref<Record<LearningSkill, number>>({ vocabulary: 50, listening: 50, spelling: 50, speaking: 50, reading: 50, writing: 50 });
const placementPrompt: Record<LearningSkill, string> = {
  vocabulary: "能理解并正确使用当前级别的常用词",
  listening: "能听懂正常语速的日常对话与关键信息",
  spelling: "听到常用词和句子后能准确拼写",
  speaking: "能用完整句表达自己的经历、需求和观点",
  reading: "能读懂当前级别短文并找到主要信息",
  writing: "能用正确语法写出连贯的句子或短文"
};
const accountTab = ref<"overview" | "history" | "library" | "partner">("overview");
const historyContinueBusyId = ref("");
const learnerActivityBusy = ref("");
const aiQuestion = ref("这句话怎么拆成主谓宾？");
const aiReply = ref<AiAssistantContextReply | null>(null);
const commerce = ref<CommerceDashboard | null>(null);
const openApiStatus = ref<{ publicEnabled: boolean; docsUrl: string; specUrl: string; endpointCount: number } | null>(null);
const desktopDownload = ref<DesktopDownloadCenter | null>(null);
const desktopPlatform = ref<DesktopPlatform>("win_x64");
const desktopDownloadBusy = ref("");
const desktopDownloadNotice = ref("");
const desktopDownloadOpen = ref(false);
type PwaGuideTarget = "chrome" | "safari" | "ios" | "android";
type DeferredPwaInstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};
const pwaInstallGuideOpen = ref(false);
const pwaGuideTarget = ref<PwaGuideTarget>("chrome");
const pwaInstallReady = ref(false);
const pwaStandalone = ref(false);
const pwaInstallStatus = ref("按下方步骤操作，即可生成桌面或主屏图标。");
let deferredPwaInstallPrompt: DeferredPwaInstallPrompt | null = null;
const pwaGuideTabs: Array<{ key: PwaGuideTarget; label: string; icon: string }> = [
  { key: "chrome", label: "Chrome / Edge（电脑）", icon: "◉" },
  { key: "safari", label: "Mac Safari", icon: "▣" },
  { key: "ios", label: "iPhone / iPad", icon: "▯" },
  { key: "android", label: "Android / 微信", icon: "◌" }
];
const pwaGuides: Record<PwaGuideTarget, { title: string; summary: string; steps: Array<{ title: string; description: string }> }> = {
  chrome: {
    title: "Chrome / Edge：安装为独立应用",
    summary: "Windows 和 macOS 的 Chrome、Edge 均支持。浏览器提供一键安装时，直接使用下方按钮即可。",
    steps: [
      { title: "点击「立即安装火速英语」", description: "若浏览器未显示安装按钮，请点击地址栏右侧的安装图标，或打开右上角菜单。" },
      { title: "选择「安装火速英语」或「创建快捷方式」", description: "建议勾选在独立窗口中打开，使用时不会显示浏览器地址栏。" },
      { title: "从桌面或应用列表一键启动", description: "安装后会生成应用图标；网站更新会在下次打开或刷新时自动生效。" }
    ]
  },
  safari: {
    title: "Mac Safari：添加到 Dock",
    summary: "Safari 会将网站创建为独立网页 App，图标会出现在 Dock 和「应用程序」中。",
    steps: [
      { title: "在 Safari 顶部菜单栏点「文件」", description: "请保持当前火速英语页面处于激活状态。" },
      { title: "选择「添加到 Dock」", description: "macOS Sonoma 及更新系统可直接使用此功能。" },
      { title: "确认名称后完成创建", description: "之后可从 Dock 或「应用程序」中独立启动火速英语。" }
    ]
  },
  ios: {
    title: "iPhone / iPad：添加到主屏幕",
    summary: "请使用 Safari 打开本站；iOS 会将图标保存到主屏幕，并以沉浸式窗口运行。",
    steps: [
      { title: "点击 Safari 底部的「分享」图标", description: "图标是带向上箭头的方框；若在微信内，请先选择在 Safari 中打开。" },
      { title: "向上滑动菜单并选「添加到主屏幕」", description: "可在系统分享面板中搜索或找到此选项。" },
      { title: "点击右上角「添加」", description: "确认名称后，火速英语图标会出现在手机或平板主屏幕。" }
    ]
  },
  android: {
    title: "Android / 微信：添加到手机桌面",
    summary: "Android Chrome 和多数系统浏览器支持安装；微信内置浏览器需要先跳转到系统浏览器。",
    steps: [
      { title: "若在微信内，请点右上角「…」并选「在浏览器打开」", description: "微信内置浏览器不能直接安装网页应用。" },
      { title: "打开浏览器菜单，选择「安装应用」或「添加到主屏幕」", description: "Chrome 中通常位于右上角三点菜单；不同品牌系统文字可能略有不同。" },
      { title: "允许创建桌面图标", description: "完成后即可从手机桌面一键进入火速英语。" }
    ]
  }
};
const pwaActiveGuide = computed(() => pwaGuides[pwaGuideTarget.value]);
const desktopInstallStatus = ref<DesktopInstallationStatus | null>(null);
const desktopInstallCheckReady = ref(false);
const desktopLocalBridgeRunning = ref(false);
const desktopUpdateProgress = ref<{ phase: string; percent: number; message: string } | null>(null);
const desktopFullscreen = ref(false);
const browserFullscreen = ref(false);
const commerceBusy = ref("");
const commerceNotice = ref("");
type WechatPayModalState = {
  orderNo: string;
  planName: string;
  amount: number;
  qrDataUrl: string;
  statusText: string;
  refreshing: boolean;
  productType?: "membership" | "ai_credit";
};
const wechatPayModal = ref<WechatPayModalState | null>(null);
const wechatPayPreparing = ref("");
let wechatPayPollTimerId: number | undefined;
const affiliatePayoutForm = ref({ alipayAccount: "", alipayRealName: "" });
const inviteCodeInput = ref("");
const selectedCategoryId = ref("");
const catalogContentType = ref<"words" | "sentences">("sentences");
type CatalogViewMode = "list" | "grid";
const CATALOG_VIEW_MODE_STORAGE_KEY = "huosu_catalog_view_mode_v1";
const storedCatalogViewMode = localStorage.getItem(CATALOG_VIEW_MODE_STORAGE_KEY);
const catalogViewMode = ref<CatalogViewMode>(storedCatalogViewMode === "grid" ? "grid" : "list");
const selectedWordCategoryId = ref("root-wordbooks");
const expandedSentenceSecondCategoryIds = ref<string[]>([]);
const catalogWordQuery = ref("");
const catalogWordResults = ref<WordEntry[]>([]);
const catalogWordBusy = ref(false);
const catalogSelectedCourseId = ref("");
const catalogExpandedLessonId = ref("");
const catalogCourseLoadingId = ref("");
const courseCoverFailedIds = ref<Record<string, true>>({});
const wholeCourseStartingId = ref("");
const continuousCourseId = ref("");
const courseGroupProgressExpanded = ref(false);
const activeCatalogDropdown = ref<"root" | "second" | "third" | "word-root" | "word-second" | "">("");
const currentCourse = ref<Course | null>(null);
const currentLesson = ref<Course["lessons"][number] | null>(null);
const practiceBootLabels = ["定位上次学习进度", "读取当前课节", "准备首条练习"];
const practiceBootLoading = ref(isDesktopShell || hasStoredAuthToken);
const practiceBootStage = ref(0);
let practiceBootStageTimerId: number | undefined;
let practiceBootFinishTimerId: number | undefined;
const practiceBootStartedAt = Date.now();

function finishPracticeBootLoading() {
  if (!practiceBootLoading.value) return;
  const remaining = Math.max(0, 420 - (Date.now() - practiceBootStartedAt));
  if (practiceBootFinishTimerId) window.clearTimeout(practiceBootFinishTimerId);
  practiceBootFinishTimerId = window.setTimeout(() => {
    practiceBootLoading.value = false;
    if (practiceBootStageTimerId) window.clearInterval(practiceBootStageTimerId);
    practiceBootStageTimerId = undefined;
  }, remaining);
}
const currentIndex = ref(0);

function setCatalogViewMode(value: CatalogViewMode) {
  catalogViewMode.value = value;
  localStorage.setItem(CATALOG_VIEW_MODE_STORAGE_KEY, value);
}
const PRACTICE_MODE_STORAGE_KEY = "huosu_practice_mode_v1";
const practiceModeKeys: PracticeMode[] = ["learning", "dictation", "typing", "puzzle", "speaking"];

function readPreferredPracticeMode(userId?: string): PracticeMode {
  const accountValue = userId ? localStorage.getItem(`${PRACTICE_MODE_STORAGE_KEY}:${userId}`) : null;
  const value = accountValue || localStorage.getItem(PRACTICE_MODE_STORAGE_KEY) || "dictation";
  return practiceModeKeys.includes(value as PracticeMode) ? value as PracticeMode : "dictation";
}

function persistPreferredPracticeMode(value: PracticeMode) {
  localStorage.setItem(PRACTICE_MODE_STORAGE_KEY, value);
  if (currentUser.value?.id) localStorage.setItem(`${PRACTICE_MODE_STORAGE_KEY}:${currentUser.value.id}`, value);
}

function restorePreferredPracticeMode() {
  mode.value = readPreferredPracticeMode(currentUser.value?.id);
}

const mode = ref<PracticeMode>(readPreferredPracticeMode());
const showAnswer = ref(false);
const typedAnswer = ref("");
const dictationSlots = ref<string[]>([]);
const dictationCursor = ref(0);
const dictationCharacterCursor = ref(0);
const dictationSubmitted = ref(false);
const dictationCompletionConfirmed = ref(false);
const dictationInputNotice = ref("");
const DICTATION_ABSOLUTE_WORD_LIMIT = 48;
const answerErrorCount = ref(0);
const practiceSyncNotice = ref("");
const reviewPreviewNotice = ref("");
let practiceActiveMs = 0;
let practiceHintUsed = false;
const practiceMistakeSlots = new Set<number>();
function markPracticeAssisted() { practiceHintUsed = true; }
let practiceVisitId = crypto.randomUUID();
const attemptPayloads = new Map<string, PracticeSubmission>();
const practiceOutbox = new PracticeOutbox(localStorage, () => currentUser.value?.id, submitPracticeAnswer, (message, result) => {
  practiceSyncNotice.value = message;
  if (result) syncLearningStatsAfterPractice((result as { activity?: LearnerActivityDashboard }).activity);
});
function retryPracticeSync() { void practiceOutbox.flush(); }
function practicePayload(answer: string, answerMode: PracticeMode): PracticeSubmission {
  const key = `${practiceVisitId}:${answerMode}:${answer}`;
  const existing = attemptPayloads.get(key);
  if (existing) return existing;
  const payload = { sentenceId: currentSentence.value!.id, answer, mode: answerMode,
    attemptId: crypto.randomUUID(), activeDurationMs: practiceActiveMs,
    hintUsed: practiceHintUsed || ["learning", "typing", "puzzle"].includes(answerMode), errorCount: answerErrorCount.value };
  attemptPayloads.set(key, payload);
  practiceActiveMs = 0;
  return payload;
}
function queuePractice(answer: string, answerMode: PracticeMode) {
  if (!currentUser.value) return; // Guest experience is not a cloud-saved record.
  try { practiceOutbox.enqueue(practicePayload(answer, answerMode)); }
  catch { practiceSyncNotice.value = "浏览器无法保存待同步记录，请勿关闭页面；清理存储空间后重试。"; }
}

// “学习”模式没有需要判错的答案。用户听完、朗读后明确点击“下一句”时，
// 这就是一次有效学习完成动作；过去只在点击“提交”时记账，导致这类正常
// 学习永远不计入右上角累计数据。
const recordedLearningVisitIds = new Set<string>();
function recordLearningProgressBeforeLeaving() {
  const sentence = currentSentence.value;
  if (!sentence || !currentUser.value || mode.value !== "learning") return;
  // 忽略误触“下一句”：至少停留两秒才视为一次实际学习，避免刷计数。
  if (practiceActiveMs < 2000 || recordedLearningVisitIds.has(practiceVisitId)) return;
  recordedLearningVisitIds.add(practiceVisitId);
  queuePractice(sentence.english, "learning");
}
const dictationLearningSuggestionOpen = ref(false);
const typingIndex = ref(0);
const typingErrorFlash = ref(false);
const typingKeyboardRef = ref<HTMLElement | null>(null);
const answerCompareView = ref<"word" | "sentence">("sentence");
const selectedPuzzleIds = ref<string[]>([]);
const practiceSubmitInFlight = ref(false);
const activeWord = ref<WordEntry | null>(null);
const pendingWordSave = ref("");
const wordSaveNotice = ref("");
const wordSaveBusy = ref("");
const wordPopupOpen = ref(false);
const wordPopupLoading = ref(false);
const wordPopupText = ref("");
const wordPopupStudyIndex = ref(-1);
const wordPopupStyle = ref<Record<string, string>>({});
let pendingWordSaveTimerId: number | undefined;
const feedback = ref<PracticeFeedback | null>(null);
const sidebarOpen = ref(false);
const settingsOpen = ref(false);
const accountPanelOpen = ref(false);
const affiliateIntroOpen = ref(false);
const modeMenuOpen = ref(false);
const activeSettingMenu = ref<string | null>(null);
const shortcutModalOpen = ref(false);
const moreSettingsOpen = ref(false);
const personalModalOpen = ref(false);
const wordView = ref(false);
const isRecording = ref(false);
const speechAssessing = ref(false);
const speechAssessment = ref<SpeechAssessment | null>(null);
let speechRecorder: MediaRecorder | undefined;
let speechStream: MediaStream | undefined;
let speechChunks: BlobPart[] = [];
const theaterRef = ref<HTMLElement | null>(null);
const typingInputRef = ref<HTMLInputElement | null>(null);
const sessionStartedAt = ref(Date.now());
const elapsedSeconds = ref(0);
// An authenticated refresh must never paint the public landing page first.
// The token is checked synchronously; the server session is validated in the
// background during mount and can still send an invalid session home.
const landingVisible = ref(!isDesktopShell && !hasStoredAuthToken);
const landingDemoSentence = "She usually reads a book in the morning.";
const landingTypingIndex = ref(0);
const landingTypingFlash = ref(false);
let landingTypingTimer: number | undefined;
const trialGateOpen = ref(false);
const courseCompletionOpen = ref(false);
const visibilityResumeOpen = ref(false);
let visibilityPausedAt = 0;
const guestTrialState = ref<GuestTrialState>(loadGuestTrialState());
const introDemoRunning = ref(false);
const introShortcutVisible = ref(false);
const introDemoActiveKey = ref("");
const introDemoHint = ref("先听一遍，再看系统如何完成听写输入。");
const introDemoPhase = ref<"listen" | "typing" | "submit" | "complete">("listen");
const enterSubmitStreak = ref(0);
const lastEnterSubmitAt = ref(0);
const isSpeaking = ref(false);
const speechNotice = ref("");
const speechNoticeIsError = ref(false);
const waveformActive = ref(false);
const waveformProfile = ref<number[]>([]);
const celebrationActive = ref(false);
const puzzleFailureActive = ref(false);
const successFlowRunning = ref(false);
let timerId: number | undefined;
let removeDesktopUpdateListener: (() => void) | undefined;
let removeDesktopFullscreenListener: (() => void) | undefined;
let keyboardAudioContext: AudioContext | null = null;
let speechAudioContext: AudioContext | null = null;
let speechAudioResumePromise: Promise<void> | undefined;
let preparedSpeechAudio: HTMLAudioElement | undefined;
let activeSpeechBufferSource: AudioBufferSourceNode | undefined;
let activeSpeechHtmlAudio: HTMLMediaElement | undefined;
// 真人录音包始终复用同一个原始音频元素：切换句子只 seek 到已校验的
// 起止时间，不重新创建或下载一份“每句 MP3”。
let humanRecordingAudio: HTMLAudioElement | undefined;
let humanRecordingAudioUrl = "";
const filmVideoRef = ref<HTMLVideoElement>();
let filmRecordingVideo: HTMLVideoElement | undefined;
let filmRecordingVideoUrl = "";
// 暂停或切走一句时要同时结束其 Promise 和超时计时器；否则旧句 12 秒
// 后会把“播放失败”错误写到正在正常播放的新句上。
const htmlPlaybackFinishers = new WeakMap<HTMLMediaElement, () => void>();
let suppressAutoRead = false;
const speechAudioBufferCache = new Map<string, AudioBuffer>();
let celebrationTimerId: number | undefined;
let puzzleFailureTimerId: number | undefined;
let waveformAnimationFrameId: number | undefined;
let smsCountdownTimerId: number | undefined;
let waveformRunId = 0;
let speakingSerial = 0;
let successFlowSerial = 0;
const settings = ref<LearnerSettings>(loadSettings());
const ttsRequestCache = new Map<string, Promise<ClientAudioAsset | undefined>>();
// A transient failure must only cool down the exact word/voice request that
// failed. The previous global 45-second switch made one bad request silence
// every following word in the lesson.
const ttsUnavailableUntilByKey = new Map<string, number>();
const INITIAL_AUDIO_BATCH_SIZE = 1;
// One active item plus four following items keeps normal navigation instant
// without opening a full lesson's worth of provider requests at once.
const BACKGROUND_AUDIO_WINDOW_SIZE = 4;
const lessonAudioPreparingBlocking = ref(false);
const lessonAudioPreparationVisible = ref(false);
const lessonAudioPreparationLabel = ref("正在读取学习包音频");
const lessonAudioTotal = ref(0);
const lessonAudioPrepared = ref(0);
const lessonAudioFailed = ref(0);
const lessonAudioPreparationPercent = computed(() => lessonAudioTotal.value
  ? Math.min(100, Math.round((lessonAudioPrepared.value / lessonAudioTotal.value) * 100))
  : 0);
// Track only the item the learner is waiting for. Background prefetch may be
// best-effort, but a missing current word/sentence must never look like a
// frozen player.
const currentAudioLoadCounts = ref<Record<string, number>>({});
const currentAudioLoading = computed(() => {
  const sentence = currentSentence.value;
  if (!sentence?.english) return false;
  return Boolean(currentAudioLoadCounts.value[currentAudioLoadKey(sentence)]);
});
const audioPreparationNoticeVisible = computed(() => currentAudioLoading.value || lessonAudioPreparationVisible.value);
const audioPreparationNoticeLabel = computed(() => {
  if (currentAudioLoading.value) return `正在加载当前${currentIsSingleWord.value ? "单词" : "句子"}语音…`;
  return lessonAudioPreparationLabel.value;
});
const audioPreparationNoticePercent = computed(() => currentAudioLoading.value ? 68 : lessonAudioPreparationPercent.value);
let lessonAudioPreparationSerial = 0;
let lessonAudioCompletionTimer: number | undefined;
let practiceWindowPreloadTimer: number | undefined;
let continuousNextLessonAudioSerial = 0;
let continuousNextLessonAudioKey = "";
const activeBackgroundAudioPreloads = new Set<HTMLAudioElement>();
const continuousLessonAdvancing = ref(false);

const modes: ModeMeta[] = [
  { key: "learning", label: "学习", hint: "句意与结构辅助" },
  { key: "dictation", label: "听写", hint: "中文提示听写打字" },
  { key: "typing", label: "打字", hint: "键盘指法逐字输入" },
  { key: "puzzle", label: "拼图", hint: "点击单词还原语序" },
  { key: "speaking", label: "跟读", hint: "听音模仿，暂不评分" }
];

const landingKeyboardRows = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Shift", "Z", "X", "C", "V", "B", "N", "M", "Enter"],
  ["Ctrl", "Space", "Next"]
];

const roleColors = ["#00c8ff", "#00d892", "#ffcc27", "#c58cff", "#ff6fb0", "#7fe7ff"];
const voiceOptions: VoiceOption[] = [
  {
    code: "us_male",
    label: "美式男声",
    locale: "en-US",
    gender: "male",
    browserHints: ["male", "david", "mark", "alex", "daniel", "aaron", "fred"],
    avoidHints: ["female", "samantha", "victoria", "karen", "susan", "zira"],
    pitch: 0.84,
    rate: 0.94
  },
  {
    code: "us_child",
    label: "美式童声",
    locale: "en-US",
    gender: "child",
    browserHints: ["junior", "child", "kid"],
    avoidHints: [],
    pitch: 1.18,
    rate: 0.96
  },
  {
    code: "us_female",
    label: "美式女声",
    locale: "en-US",
    gender: "female",
    browserHints: ["female", "samantha", "victoria", "karen", "susan", "zira"],
    avoidHints: ["male", "david", "mark", "alex"],
    pitch: 1.04,
    rate: 0.96
  },
  {
    code: "uk_male",
    label: "英式男声",
    locale: "en-GB",
    gender: "male",
    browserHints: ["male", "daniel", "arthur", "george", "oliver"],
    avoidHints: ["female", "serena", "kate"],
    pitch: 0.84,
    rate: 0.94
  }
];
const speechRateOptions: SpeechRateOption[] = [
  { value: "slow", label: "慢速", hint: "新句首听更稳", rateMultiplier: 0.82, providerSpeed: 0.85 },
  { value: "normal", label: "中速", hint: "默认训练速度", rateMultiplier: 1, providerSpeed: 1 },
  { value: "fast", label: "快速", hint: "熟练后提速", rateMultiplier: 1.13, providerSpeed: 1.12 }
];
const displayStyleOptions: Array<{ value: DisplayStyle; label: string; icon: string }> = [
  { value: "dark", label: "深色模式", icon: "月" },
  { value: "lightStandard", label: "浅色标准", icon: "日" },
  { value: "lightPure", label: "浅色纯白", icon: "□" },
  { value: "lightGray", label: "浅色浅灰", icon: "■" }
];
const interfaceModeOptions: Array<{ value: InterfaceMode; label: string }> = [
  { value: "standard", label: "标准" },
  { value: "compact", label: "紧凑" },
  { value: "wide", label: "宽屏" }
];
const repeatOptions: Array<{ value: RepeatCount; label: string }> = [
  { value: "manual", label: "手动" },
  { value: 1, label: "1次" },
  { value: 2, label: "2次" },
  { value: 3, label: "3次" },
  { value: 4, label: "4次" },
  { value: 5, label: "5次" }
];
const answerRepeatOptions = repeatOptions.filter((item): item is { value: 1 | 2 | 3 | 4 | 5; label: string } => item.value !== "manual");
const fontOptions: Array<{ value: LearnerSettings["fontFamily"]; label: string; stack: string }> = [
  { value: "default", label: "默认字体", stack: 'Inter, "PingFang SC", "Microsoft YaHei", system-ui, sans-serif' },
  { value: "sans", label: "现代无衬线", stack: 'Arial, "PingFang SC", sans-serif' },
  { value: "rounded", label: "圆体字体", stack: '"Arial Rounded MT Bold", "PingFang SC", sans-serif' },
  { value: "serif", label: "古典优雅", stack: 'Georgia, "Times New Roman", serif' }
];
const inputStyleOptions: Array<{ value: LearnerSettings["inputStyle"]; label: string }> = [
  { value: "box", label: "方框" },
  { value: "underline", label: "横线" }
];
const gestureOptions: Array<{ value: GestureMode; label: string; desc: string }> = [
  { value: "realistic", label: "拟真手势", desc: "手指贴合键位移动，保留真实手感和按键反馈。" },
  { value: "standard", label: "标准指法", desc: "更清晰的教学线稿，突出手指分工和目标键位。" }
];
const shortcutRows = [
  { key: "speak", label: "播放语音", value: "Ctrl + P" },
  { key: "answer", label: "显示答案", value: "Ctrl + A" },
  { key: "prev", label: "上一句", value: "Shift + ←" },
  { key: "next", label: "下一句", value: "Shift + →" }
];
const virtualKeyboardRows = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M", "Enter"],
  ["Space"]
];
const typingKeyboardRows: Array<{ id: string; keys: TypingKey[] }> = [
  {
    id: "number",
    keys: [
      { code: "BACKQUOTE", label: "`", unit: 1, finger: "lp", ghost: true },
      { code: "1", label: "1", unit: 1, finger: "lp", ghost: true },
      { code: "2", label: "2", unit: 1, finger: "lr", ghost: true },
      { code: "3", label: "3", unit: 1, finger: "lm", ghost: true },
      { code: "4", label: "4", unit: 1, finger: "li", ghost: true },
      { code: "5", label: "5", unit: 1, finger: "li", ghost: true },
      { code: "6", label: "6", unit: 1, finger: "ri", ghost: true },
      { code: "7", label: "7", unit: 1, finger: "ri", ghost: true },
      { code: "8", label: "8", unit: 1, finger: "rm", ghost: true },
      { code: "9", label: "9", unit: 1, finger: "rr", ghost: true },
      { code: "0", label: "0", unit: 1, finger: "rp", ghost: true },
      { code: "MINUS", label: "-", unit: 1, finger: "rp" },
      { code: "EQUAL", label: "=", unit: 1, finger: "rp", ghost: true },
      { code: "BACKSPACE", label: "⌫", unit: 1.9, finger: "rp", ghost: true }
    ]
  },
  {
    id: "top",
    keys: [
      { code: "TAB", label: "Tab", unit: 1.8, finger: "lp", ghost: true },
      { code: "Q", label: "Q", unit: 1, finger: "lp" },
      { code: "W", label: "W", unit: 1, finger: "lr" },
      { code: "E", label: "E", unit: 1, finger: "lm" },
      { code: "R", label: "R", unit: 1, finger: "li" },
      { code: "T", label: "T", unit: 1, finger: "li" },
      { code: "Y", label: "Y", unit: 1, finger: "ri" },
      { code: "U", label: "U", unit: 1, finger: "ri" },
      { code: "I", label: "I", unit: 1, finger: "rm" },
      { code: "O", label: "O", unit: 1, finger: "rr" },
      { code: "P", label: "P", unit: 1, finger: "rp" },
      { code: "BRACKETLEFT", label: "[", unit: 1, finger: "rp", ghost: true },
      { code: "BRACKETRIGHT", label: "]", unit: 1, finger: "rp", ghost: true },
      { code: "BACKSLASH", label: "\\", unit: 1.25, finger: "rp", ghost: true }
    ]
  },
  {
    id: "home",
    keys: [
      { code: "CAPS", label: "Caps", unit: 2.05, finger: "lp", ghost: true },
      { code: "A", label: "A", unit: 1, finger: "lp", home: true },
      { code: "S", label: "S", unit: 1, finger: "lr", home: true },
      { code: "D", label: "D", unit: 1, finger: "lm", home: true },
      { code: "F", label: "F", unit: 1, finger: "li", home: true },
      { code: "G", label: "G", unit: 1, finger: "li" },
      { code: "H", label: "H", unit: 1, finger: "ri" },
      { code: "J", label: "J", unit: 1, finger: "ri", home: true },
      { code: "K", label: "K", unit: 1, finger: "rm", home: true },
      { code: "L", label: "L", unit: 1, finger: "rr", home: true },
      { code: "SEMICOLON", label: ";", unit: 1, finger: "rp" },
      { code: "QUOTE", label: "'", unit: 1, finger: "rp" },
      { code: "ENTER", label: "Enter", unit: 2.25, finger: "rp", ghost: true }
    ]
  },
  {
    id: "bottom",
    keys: [
      { code: "SHIFTLEFT", label: "Shift", unit: 2.65, finger: "lp", ghost: true },
      { code: "Z", label: "Z", unit: 1, finger: "lp" },
      { code: "X", label: "X", unit: 1, finger: "lr" },
      { code: "C", label: "C", unit: 1, finger: "lm" },
      { code: "V", label: "V", unit: 1, finger: "li" },
      { code: "B", label: "B", unit: 1, finger: "li" },
      { code: "N", label: "N", unit: 1, finger: "ri" },
      { code: "M", label: "M", unit: 1, finger: "ri" },
      { code: "COMMA", label: ",", unit: 1, finger: "rm" },
      { code: "PERIOD", label: ".", unit: 1, finger: "rr" },
      { code: "SLASH", label: "/", unit: 1, finger: "rp" },
      { code: "SHIFTRIGHT", label: "Shift", unit: 2.8, finger: "rp", ghost: true }
    ]
  },
  {
    id: "space",
    keys: [
      { code: "CTRLLEFT", label: "Ctrl", unit: 1, finger: "lp", ghost: true },
      { code: "ALTLEFT", label: "Alt", unit: 1, finger: "lt", ghost: true },
      { code: "SPACE", label: "Space", unit: 6.6, finger: "lt" },
      { code: "ALTRIGHT", label: "Alt", unit: 1, finger: "rt", ghost: true },
      { code: "CTRLRIGHT", label: "Ctrl", unit: 1, finger: "rp", ghost: true }
    ]
  }
];
const typingFingerGuides: TypingFingerGuide[] = [
  { id: "lp", hand: "left", name: "pinky", left: 18.93, bottom: 88, height: 112, width: 36, rotate: 7, z: 7 },
  { id: "lr", hand: "left", name: "ring", left: 25.04, bottom: 79, height: 122, width: 39, rotate: 4, z: 8 },
  { id: "lm", hand: "left", name: "middle", left: 31.15, bottom: 67, height: 134, width: 41, rotate: 1, z: 9 },
  { id: "li", hand: "left", name: "index", left: 37.26, bottom: 75, height: 126, width: 42, rotate: -2, z: 10 },
  { id: "lt", hand: "left", name: "thumb", left: 46.3, bottom: -34, height: 98, width: 74, rotate: 34, z: 11 },
  { id: "rt", hand: "right", name: "thumb", left: 53.7, bottom: -34, height: 98, width: 74, rotate: -34, z: 11 },
  { id: "ri", hand: "right", name: "index", left: 55.59, bottom: 75, height: 126, width: 42, rotate: 2, z: 10 },
  { id: "rm", hand: "right", name: "middle", left: 61.7, bottom: 67, height: 134, width: 41, rotate: -1, z: 9 },
  { id: "rr", hand: "right", name: "ring", left: 67.81, bottom: 79, height: 122, width: 39, rotate: -4, z: 8 },
  { id: "rp", hand: "right", name: "pinky", left: 73.93, bottom: 88, height: 112, width: 36, rotate: -7, z: 7 }
];
const typingKeyTargets = ref(buildTypingKeyTargets());
const moreSettingRows: Array<{ key: BooleanSettingKey; label: string; desc: string }> = [
  { key: "showPhonetic", label: "显示主练习区音标", desc: "学习资料和答案解析始终保留完整音标，避免遗漏关键信息" },
  { key: "showChinese", label: "显示中文", desc: "关闭后听写时不显示中文提示，便于自测" },
  { key: "answerSmoke", label: "答对烟花效果", desc: "关闭后答对时不显示烟花动画，减少性能开销" },
  { key: "waveform", label: "声音频谱动画", desc: "关闭后播放语音时不显示频谱波形动画" },
  { key: "ctrlHints", label: "快捷键文字提示", desc: "在底部练习操作栏显示每个按钮对应的快捷键" }
];

const landingTypedText = computed(() => landingDemoSentence.slice(0, landingTypingIndex.value));
const landingPendingText = computed(() => landingDemoSentence.slice(landingTypingIndex.value));
const landingActiveKey = computed(() => landingCharToKey(landingDemoSentence[landingTypingIndex.value] || ""));
const sentences = computed(() => currentLesson.value?.sentences || []);
const currentSentence = computed<Sentence | undefined>(() => {
  const sentence = sentences.value[currentIndex.value];
  return sentence ? learningPresentation(sentence, sentence.sourceMeta?.source === "figotech_curated_ecdict_wordbooks" && /小学/.test(currentCourse.value?.title || "")) : undefined;
});
const currentIsWordbookSentence = computed(() => currentSentence.value?.sourceMeta?.source === "figotech_curated_ecdict_wordbooks");
const currentIsSingleWord = computed(() => {
  const sentence = currentSentence.value;
  if (!sentence) return false;
  if (currentIsWordbookSentence.value || sentence.sourceMeta?.itemType === "word") return true;
  return normalizeWords(sentence.english).length <= 1;
});
const currentCourseLessonIndex = computed(() => {
  if (!currentCourse.value || !currentLesson.value) return 0;
  return Math.max(0, currentCourse.value.lessons.findIndex((item) => item.id === currentLesson.value?.id));
});
const currentCourseHistory = computed(() => learnerActivity.value?.learningHistory.packages.find(
  (item) => item.courseId === currentCourse.value?.id
));
const courseGroupProgressItems = computed(() => (currentCourse.value?.lessons || []).map((lesson, index) => {
  const saved = currentCourseHistory.value?.lessons.find((item) => item.lessonId === lesson.id);
  // 课程详情只会展开当前课节的 sentences，其余课节通过 sentenceCount
  // 提供轻量摘要。不能只读数组长度，否则未加载的分组都会被误判为 0/0。
  const totalSentences = Math.max(
    lessonSentenceCount(lesson),
    saved?.totalSentences || 0
  );
  const learnedSentences = Math.min(totalSentences, saved?.learnedSentences || 0);
  const progressPercent = totalSentences ? Math.round((learnedSentences / totalSentences) * 100) : 0;
  return {
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    groupNumber: index + 1,
    learnedSentences,
    totalSentences,
    progressPercent,
    completed: totalSentences > 0 && learnedSentences >= totalSentences,
    current: lesson.id === currentLesson.value?.id
  };
}));
const incompleteCourseGroups = computed(() => courseGroupProgressItems.value.filter((item) => !item.completed));
const completedCourseGroups = computed(() => courseGroupProgressItems.value.filter((item) => item.completed));
const courseGroupProgressVisible = computed(() => courseGroupProgressItems.value.length > 1);
const courseGroupTotalSentences = computed(() => courseGroupProgressItems.value.reduce((total, item) => total + item.totalSentences, 0));
const courseGroupLearnedSentences = computed(() => courseGroupProgressItems.value.reduce((total, item) => total + item.learnedSentences, 0));
const courseGroupOverallProgress = computed(() => courseGroupTotalSentences.value
  ? Math.round((courseGroupLearnedSentences.value / courseGroupTotalSentences.value) * 100)
  : 0);
const nextLessonInCurrentCourse = computed(() => currentCourse.value?.lessons[currentCourseLessonIndex.value + 1] || null);
const courseCheckpointPercent = computed(() => currentCourse.value?.lessons.length
  ? Math.round(((currentCourseLessonIndex.value + 1) / currentCourse.value.lessons.length) * 100)
  : 0);
const isCurrentCourseComplete = computed(() => {
  const course = currentCourse.value;
  const lesson = currentLesson.value;
  if (!course || !lesson) return false;
  return course.lessons.findIndex((item) => item.id === lesson.id) === course.lessons.length - 1;
});
const currentSentenceImageUrl = computed(() => {
  const rawUrl = currentSentence.value?.imageAsset?.status === "generated"
    ? currentSentence.value.imageAsset.url
    : currentSentence.value?.imageUrl;
  return rawUrl ? normalizeApiAssetUrl(rawUrl) : "";
});
const availableModes = computed(() => currentIsSingleWord.value ? modes.filter((item) => item.key !== "puzzle") : modes);
const activeMode = computed(() => availableModes.value.find((item) => item.key === mode.value) || availableModes.value[0]);
function audioAssetKey(voiceCode: VoiceCode, speechRate: SpeechRateValue) {
  void speechRate;
  return voiceCode;
}
const selectedVoice = computed(() => voiceOptions.find((voice) => voice.code === settings.value.voiceCode) || voiceOptions[0]);
const currentLessonUsesHumanRecording = computed(() => {
  const courseIsHumanRecording = currentCourse.value?.sourceMeta?.audioSourceType === "human_recording";
  return Boolean(courseIsHumanRecording || currentLesson.value?.sentences.some((sentence) => isHumanRecordingSentence(sentence)));
});
const currentIsFilmVideo = computed(() => currentSentence.value?.sourceMeta?.mediaSourceType === "film_video" || currentCourse.value?.sourceMeta?.mediaSourceType === "film_video");
const currentFilmVideoUrl = computed(() => currentIsFilmVideo.value ? normalizeApiAssetUrl(playableAudioAssetUrl(selectedAudioAsset.value, currentSentence.value)) : "");
const currentFilmRangeLabel = computed(() => {
  const asset = selectedAudioAsset.value;
  const range = humanRecordingRange(currentSentence.value, asset);
  const start = Math.max(0, Number(range?.startMs || 0) / 1000).toFixed(1);
  const end = Math.max(0, Number(range?.endMs || 0) / 1000).toFixed(1);
  return `${start}s–${end}s`;
});
const voicePreferenceLabel = computed(() => currentIsFilmVideo.value ? "影视原声" : currentLessonUsesHumanRecording.value ? "真人原声" : selectedVoice.value.label);
const selectedSpeechRate = computed(() => speechRateOptions.find((option) => option.value === settings.value.speechRate) || speechRateOptions[1]);
const selectedAudioAsset = computed(() => {
  const sentence = currentSentence.value;
  if (!sentence?.audioAssets) return undefined;
  if (isHumanRecordingSentence(sentence)) return sentence.audioAssets.human_recording as unknown as ClientAudioAsset | undefined;
  return sentence.audioAssets[audioAssetKey(settings.value.voiceCode, settings.value.speechRate)] as unknown as ClientAudioAsset | undefined;
});
const speechRateLabel = computed(() => selectedSpeechRate.value.label);
const displayStyleLabel = computed(() => displayStyleOptions.find((option) => option.value === settings.value.displayStyle)?.label || "深色模式");
const interfaceModeLabel = computed(() => interfaceModeOptions.find((option) => option.value === settings.value.interfaceMode)?.label || "标准");
const fontFamilyLabel = computed(() => fontOptions.find((font) => font.value === settings.value.fontFamily)?.label || "默认字体");
const inputStyleLabel = computed(() => inputStyleOptions.find((option) => option.value === settings.value.inputStyle)?.label || "横线");
const gestureModeLabel = computed(() => gestureOptions.find((option) => option.value === settings.value.gestureMode)?.label || "拟真手势");
const targetWords = computed(() => normalizeWords(currentSentence.value?.english || ""));
const typedTokens = computed(() => (mode.value === "dictation" ? dictationSlots.value : normalizeWords(typedAnswer.value)));
const typingChars = computed(() => Array.from(typingPracticeText(currentSentence.value?.english || "")));
const typingCompleted = computed(() => Boolean(typingChars.value.length) && typingIndex.value >= typingChars.value.length);
const activeTypingChar = computed(() => typingChars.value[typingIndex.value] || "");
const activeTypingKey = computed(() => (typingCompleted.value ? "ENTER" : keyCodeForTypingChar(activeTypingChar.value)));
const activeTypingKeyMeta = computed(() => typingKeyboardRows.flatMap((row) => row.keys).find((key) => key.code === activeTypingKey.value));
const activeTypingFinger = computed<TypingFingerId>(() => activeTypingKeyMeta.value?.finger || "li");
const typingCharStates = computed(() =>
  typingChars.value.map((char, index) => ({
    index,
    char,
    display: char === " " ? " " : char,
    done: index < typingIndex.value,
    current: index === typingIndex.value && !typingCompleted.value,
    pending: index > typingIndex.value
  }))
);
const typingCharGroups = computed(() => {
  const groups: Array<{ key: string; items: typeof typingCharStates.value }> = [];
  let items: typeof typingCharStates.value = [];
  for (const item of typingCharStates.value) {
    items.push(item);
    if (item.char === " ") {
      groups.push({ key: `word-${groups.length}-${items[0]?.index || 0}`, items });
      items = [];
    }
  }
  if (items.length) groups.push({ key: `word-${groups.length}-${items[0]?.index || 0}`, items });
  return groups;
});

function buildTypingKeyTargets(): Record<string, TypingKeyTarget> {
  const rowMetrics: Record<string, { center: number; bottom: number }> = {
    number: { center: 306, bottom: 20 },
    top: { center: 240, bottom: 18 },
    home: { center: 174, bottom: 18 },
    bottom: { center: 108, bottom: 12 },
    space: { center: 48, bottom: -8 }
  };
  const keyboardWidth = 1080;
  const contentWidth = 1044;
  const sidePadding = 18;
  const unitWidth = 56;
  const gap = 10;
  return typingKeyboardRows.reduce<Record<string, TypingKeyTarget>>((targets, row) => {
    const totalWidth = row.keys.reduce((sum, key) => sum + key.unit * unitWidth, 0) + Math.max(0, row.keys.length - 1) * gap;
    let cursor = sidePadding + (contentWidth - totalWidth) / 2;
    const metric = rowMetrics[row.id] || rowMetrics.home;
    for (const key of row.keys) {
      const keyWidth = key.unit * unitWidth;
      const center = cursor + keyWidth / 2;
      targets[key.code] = {
        x: (center / keyboardWidth) * 100,
        bottom: metric.bottom,
        height: Math.max(62, metric.center - metric.bottom),
        keyHeight: unitWidth
      };
      cursor += keyWidth + gap;
    }
    return targets;
  }, {});
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function activeTypingFingerHeight(finger: TypingFingerGuide, target: TypingKeyTarget, rotate: number) {
  const verticalHeight = target.height / Math.max(0.68, Math.cos(Math.abs(rotate) * Math.PI / 180));
  const lengthBias: Record<TypingFingerId, number> = {
    lp: -10,
    lr: 2,
    lm: 22,
    li: 10,
    lt: 28,
    rt: 28,
    ri: 10,
    rm: 22,
    rr: 2,
    rp: -10
  };
  if (finger.name === "thumb") return Math.max(finger.height * 0.92, verticalHeight + lengthBias[finger.id]);
  return Math.max(finger.height * 0.82, verticalHeight + lengthBias[finger.id]);
}

function activeTypingFingerLeft(finger: TypingFingerGuide, targetX: number, height: number, rotate: number) {
  const reachRatio = finger.name === "thumb" ? 0.58 : 0.9;
  const tipOffsetPercent = Math.sin(rotate * Math.PI / 180) * height * reachRatio / 1080 * 100;
  return clampNumber(targetX - tipOffsetPercent, 7, 93);
}

function activeTypingFingerWidth(finger: TypingFingerGuide) {
  if (finger.name === "thumb") return finger.width + 8;
  if (finger.name === "pinky") return finger.width + 4;
  return finger.width + 5;
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(new Date(value));
}

function typingFingerStyle(finger: TypingFingerGuide) {
  const targets = typingKeyTargets.value;
  const homeKeyByFinger: Record<TypingFingerId, string> = {
    lp: "A",
    lr: "S",
    lm: "D",
    li: "F",
    lt: "SPACE",
    rt: "SPACE",
    ri: "J",
    rm: "K",
    rr: "L",
    rp: "SEMICOLON"
  };
  const homeTarget = targets[homeKeyByFinger[finger.id]];
  const baseBottom = finger.name === "thumb" ? -34 : -22;
  const keyScale = homeTarget ? clampNumber(homeTarget.keyHeight / 56, 0.55, 1) : 1;
  const homeLeftOffset = finger.id === "lt" ? -3.2 : finger.id === "rt" ? 3.2 : 0;
  const style: Record<string, string> = {
    "--finger-left": `${homeTarget ? homeTarget.x + homeLeftOffset : finger.left}%`,
    "--finger-bottom": `${homeTarget ? baseBottom : finger.bottom}px`,
    "--finger-height": `${homeTarget ? homeTarget.bottom + homeTarget.keyHeight - baseBottom + 3 : finger.height}px`,
    "--finger-width": `${finger.width * keyScale}px`,
    "--finger-rotate": `${finger.rotate}deg`,
    "--finger-z": String(finger.z)
  };
  if (finger.id !== activeTypingFinger.value) return style;
  const target = targets[activeTypingKey.value];
  if (!target) return style;
  const travel = target.x - finger.left;
  const rotate = finger.id === "lt" || finger.id === "rt"
    ? clampNumber(finger.rotate + travel * 0.36, -58, 58)
    : clampNumber(finger.rotate + travel * 0.58, -62, 62);
  const activeHeight = activeTypingFingerHeight(finger, target, rotate);
  const tipHeight = finger.name === "thumb" ? 78 : 94;
  // `target.height` is the reach from the hand baseline to the key center.
  // The short fingertip overlay must instead place its top on the physical
  // key, so use the measured key height rather than adding the reach twice.
  const tipBottom = clampNumber(target.bottom + target.keyHeight - tipHeight + 4, -34, 280);
  style["--finger-active-left"] = `${activeTypingFingerLeft(finger, target.x, activeHeight, rotate)}%`;
  style["--finger-active-bottom"] = `${finger.name === "thumb" ? target.bottom - 16 : target.bottom}px`;
  style["--finger-active-height"] = `${activeHeight}px`;
  style["--finger-active-width"] = `${activeTypingFingerWidth(finger)}px`;
  style["--finger-active-rotate"] = `${rotate}deg`;
  style["--finger-tip-left"] = `${target.x}%`;
  style["--finger-tip-bottom"] = `${tipBottom}px`;
  style["--finger-tip-height"] = `${tipHeight}px`;
  style["--finger-tip-width"] = `${finger.name === "thumb" ? 66 : finger.name === "pinky" ? 38 : 44}px`;
  style["--finger-tip-rotate"] = `${clampNumber(rotate * 0.26, -16, 16)}deg`;
  return style;
}

function measureTypingKeyTargets() {
  const keyboard = typingKeyboardRef.value;
  if (!keyboard) return;
  const keyboardRect = keyboard.getBoundingClientRect();
  if (!keyboardRect.width || !keyboardRect.height) return;
  const measured: Record<string, TypingKeyTarget> = {};
  keyboard.querySelectorAll<HTMLElement>("[data-key-code]").forEach((key) => {
    const code = key.dataset.keyCode;
    if (!code) return;
    const rect = key.getBoundingClientRect();
    const bottom = keyboardRect.bottom - rect.bottom;
    measured[code] = {
      x: ((rect.left + rect.width / 2 - keyboardRect.left) / keyboardRect.width) * 100,
      bottom,
      height: keyboardRect.bottom - (rect.top + rect.height / 2),
      keyHeight: rect.height
    };
  });
  if (Object.keys(measured).length) typingKeyTargets.value = measured;
}

function scheduleTypingKeyMeasurement() {
  void nextTick(() => window.requestAnimationFrame(measureTypingKeyTargets));
}

const activeDictationIndex = computed(() => Math.min(dictationCursor.value, Math.max(0, targetWords.value.length - 1)));
const dictationWordStates = computed<DictationWordState[]>(() =>
  targetWords.value.map((target, index) => {
    const typed = dictationSlots.value[index] || "";
    const targetCanonical = canonicalWord(target);
    const typedCanonical = canonicalWord(typed);
    let status: DictationWordStatus = "empty";
    if (targetCanonical && typedCanonical === targetCanonical) status = "correct";
    else if (!typedCanonical && dictationSubmitted.value) status = "missing";
    else if (!typedCanonical) status = "empty";
    else if (targetCanonical.startsWith(typedCanonical) && !dictationSubmitted.value) status = "partial";
    else status = "wrong";
    return { index, target, typed, status };
  })
);
const dictationWrongStates = computed(() => dictationWordStates.value.filter((state) => state.status !== "correct"));
const dictationAllCorrect = computed(() => Boolean(dictationWordStates.value.length) && dictationWordStates.value.every((state) => state.status === "correct"));
const memberPlans = computed(() => (commerce.value?.plans || bootstrap.value?.plans || []).filter((plan) => plan.key !== "free"));
const latestOrder = computed(() => commerce.value?.orders?.[0]);
const activePaidMembership = computed(() => {
  const membership = commerce.value?.membership;
  if (!membership || membership.status !== "active") return null;
  const plan = memberPlans.value.find((item) => item.key === membership.planKey);
  if (!plan || plan.price <= 0) return null;
  if (membership.expiresAt && new Date(membership.expiresAt).getTime() <= Date.now()) return null;
  return membership;
});
const membershipExpiryLabel = computed(() => {
  const membership = activePaidMembership.value;
  if (!membership) return "当前为免费版";
  if (!membership.expiresAt) return `${membership.planName} 长期有效`;
  return `${membership.planName} 有效期至 ${formatDateTime(membership.expiresAt)}`;
});
const memberStatusLine = computed(() => {
  const membership = commerce.value?.membership;
  const minutes = membership?.dailyMinutes === "unlimited" ? "不限时" : `${membership?.dailyMinutes || 30} 分钟`;
  const assessments = membership?.speechAssessments === "unlimited" ? "不限次" : `${membership?.speechAssessments || 0} 次`;
  return `${membership?.status || "free"} · 每日 ${minutes} · 口语评测 ${assessments}`;
});

function isPlanPurchaseDisabled(planKey: MembershipPlanKey) {
  return commerceBusy.value === planKey || Boolean(activePaidMembership.value);
}

function planButtonHint(planKey: MembershipPlanKey) {
  if (commerceBusy.value === planKey) return "处理中";
  if (activePaidMembership.value) return "有效期内不可重复购买";
  return "立即开通";
}

function extractWordsFromText(text = "") {
  return text
    .toLowerCase()
    .match(/[a-z]+(?:['-][a-z]+)*/g) || [];
}

function uniqueSentenceWords(sentences: Sentence[] = []) {
  const words = new Set<string>();
  sentences.forEach((sentence) => {
    extractWordsFromText(sentence.english).forEach((word) => words.add(word));
  });
  return words;
}

function lessonSentenceCount(lesson: Lesson) {
  return Math.max(lesson.sentenceCount || 0, lesson.sentences?.length || 0);
}

function lessonWordCount(lesson: Lesson) {
  return uniqueSentenceWords(lesson.sentences || []).size || Number(lesson.wordCount || 0);
}

function courseSentenceCount(course: Course) {
  const loadedSentenceCount = course.lessons.reduce((sum, lesson) => sum + lessonSentenceCount(lesson), 0);
  return Math.max(loadedSentenceCount, Number(course.sourceMeta?.sentenceCount || 0));
}

function courseLessonCount(course: Course) {
  return Math.max(course.lessons.length, Number(course.sourceMeta?.lessonCount || 0));
}

function courseWordCount(course: Course) {
  const words = new Set<string>();
  course.lessons.forEach((lesson) => {
    uniqueSentenceWords(lesson.sentences || []).forEach((word) => words.add(word));
  });
  return words.size || Number(course.sourceMeta?.vocabularyCount || course.sourceMeta?.wordTarget || 0);
}

// 词书中的每条练习记录就是一个待掌握词条。这里按词条数展示，
// 不再把单词词书误标为“句子库”。
function wordLessonItemCount(lesson: Lesson) {
  return lessonSentenceCount(lesson);
}

function wordCourseItemCount(course: Course) {
  return course.lessons.reduce((sum, lesson) => sum + wordLessonItemCount(lesson), 0);
}

function wordLessonPreview(lesson: Lesson) {
  const words = lesson.sentences
    .map((sentence) => String(sentence.english || "").trim())
    .filter(Boolean);
  const visibleWords = words.slice(0, 8);
  if (!visibleWords.length) return "词表整理中";
  const remaining = Math.max(0, words.length - visibleWords.length);
  return `${visibleWords.join("、")}${remaining ? ` 等${words.length}词` : ""}`;
}

function wordLessonClassification(course: Course) {
  const parts = [course.title, course.audience, `${course.level}词汇`]
    .map((item) => String(item || "").trim())
    .filter((item, index, values) => item && values.indexOf(item) === index);
  return parts.join(" · ");
}

function courseHasLearningContent(course: Course) {
  return course.lessons.some((lesson) => lesson.sentenceCount > 0 || lesson.sentences?.length);
}

function courseBelongsToCategory(course: Course, categoryId: string) {
  return course.categoryId === categoryId || Boolean(course.categoryPath?.includes(categoryId));
}

function categoryCourses(category: ContentCategory) {
  return publishedCourses.value.filter((course) => courseBelongsToCategory(course, category.id));
}

function categoryCourseCount(category: ContentCategory) {
  const actualCount = categoryCourses(category).length;
  return actualCount || category.stats?.courses || 0;
}

function categorySentenceCount(category: ContentCategory) {
  const actualCount = categoryCourses(category).reduce((sum, course) => sum + courseSentenceCount(course), 0);
  return actualCount || category.stats?.sentences || 0;
}

function categoryWordCount(category: ContentCategory) {
  const actualCount = categoryCourses(category).reduce((sum, course) => sum + courseWordCount(course), 0);
  return actualCount || category.stats?.words || 0;
}

function categoryHasVisibleContent(category: ContentCategory) {
  return categoryCourseCount(category) > 0 || categorySentenceCount(category) > 0;
}

function isWordLibraryCategory(category: ContentCategory) {
  return category.id === "root-wordbooks" || Boolean(category.path?.includes("root-wordbooks"));
}

const sentenceLibraryExcludedRootIds = new Set(["root-wordbooks", "root-tatoeba-corpus"]);

function isExcludedFromSentenceLibraryCategory(category: ContentCategory) {
  return sentenceLibraryExcludedRootIds.has(category.id)
    || Boolean(category.path?.some((id) => sentenceLibraryExcludedRootIds.has(id)));
}

function isExcludedFromSentenceLibraryCourse(course: Course) {
  return Boolean(course.categoryId && sentenceLibraryExcludedRootIds.has(course.categoryId))
    || Boolean(course.categoryPath?.some((id) => sentenceLibraryExcludedRootIds.has(id)));
}

const publishedCourses = computed<Course[]>(() => (bootstrap.value?.courses || []).filter(courseHasLearningContent));
const learnerCategoryList = computed<ContentCategory[]>(() => bootstrap.value?.categoryList?.filter((category) => category.status === "enabled") || []);
const visibleLearnerCategoryList = computed(() => learnerCategoryList.value.filter((category) => categoryHasVisibleContent(category) && !isExcludedFromSentenceLibraryCategory(category)));
const sentenceLibraryCourses = computed(() => publishedCourses.value.filter((course) => !isExcludedFromSentenceLibraryCourse(course)));
const learnerCategoryRoots = computed(() => visibleLearnerCategoryList.value.filter((category) => category.level === 1));
const selectedCategory = computed(() => visibleLearnerCategoryList.value.find((category) => category.id === selectedCategoryId.value) || null);
const activeRootCategory = computed(() => {
  const category = selectedCategory.value;
  if (!category) return null;
  if (category.level === 1) return category;
  return visibleLearnerCategoryList.value.find((item) => item.id === category.path?.[0]) || null;
});
const activeSecondCategory = computed(() => {
  const category = selectedCategory.value;
  if (!category || category.level === 1) return null;
  if (category.level === 2) return category;
  return visibleLearnerCategoryList.value.find((item) => item.id === category.path?.[1]) || null;
});
const activeThirdCategory = computed(() => {
  const category = selectedCategory.value;
  return category?.level === 3 ? category : null;
});
const secondLevelCategories = computed(() => {
  const root = activeRootCategory.value;
  if (!root) return [];
  return visibleLearnerCategoryList.value
    .filter((category) => category.parentId === root.id && category.status === "enabled")
    .sort((a, b) => a.sortOrder - b.sortOrder);
});
const thirdLevelCategories = computed(() => {
  const second = activeSecondCategory.value;
  if (!second) return [];
  return visibleLearnerCategoryList.value
    .filter((category) => category.parentId === second.id && category.status === "enabled")
    .sort((a, b) => a.sortOrder - b.sortOrder);
});
const packageColumnTitle = computed(() => {
  if (!selectedCategory.value) return "全部课包";
  return `${selectedCategory.value.name || "当前分类"} · 全部课包`;
});
const filteredCourses = computed<Course[]>(() => {
  const courses = sentenceLibraryCourses.value;
  const category = selectedCategory.value;
  if (!category) return courses;
  const ids = new Set([category.id, ...learnerCategoryList.value.filter((item) => item.path?.includes(category.id)).map((item) => item.id)]);
  return courses.filter((course) => (course.categoryId && ids.has(course.categoryId)) || course.categoryPath?.some((id) => ids.has(id)));
});
const filteredSentenceCount = computed(() => filteredCourses.value.reduce((sum, course) => sum + courseSentenceCount(course), 0));
const filteredWordCount = computed(() => {
  const words = new Set<string>();
  filteredCourses.value.forEach((course) => {
    course.lessons.forEach((lesson) => {
      uniqueSentenceWords(lesson.sentences || []).forEach((word) => words.add(word));
    });
  });
  // 紧凑初始化不携带句子正文；此时使用服务端返回的真实词汇统计，不能误显示为 0 词。
  return words.size || filteredCourses.value.reduce((sum, course) => sum + courseWordCount(course), 0);
});
const catalogWordItems = computed(() => catalogWordResults.value.length || catalogWordQuery.value
  ? catalogWordResults.value
  : []);
const wordLibraryCategories = computed(() => learnerCategoryList.value.filter(isWordLibraryCategory));
const wordLibraryRoot = computed(() => wordLibraryCategories.value.find((category) => category.id === "root-wordbooks") || null);
const selectedWordCategory = computed(() => wordLibraryCategories.value.find((category) => category.id === selectedWordCategoryId.value) || wordLibraryRoot.value);
const wordSecondLevelCategories = computed(() => wordLibraryCategories.value
  .filter((category) => category.parentId === wordLibraryRoot.value?.id)
  .sort((a, b) => a.sortOrder - b.sortOrder));
// 单词库没有需要逐层钻取的课程树。左侧直接展示全部可学习分类，
// 让用户一眼选择词书方向，不再通过长下拉框查找。
const wordLibraryNavigationCategories = computed(() => wordLibraryCategories.value
  // 单词库没有二、三级钻取：左栏完整列出全部可选分类，
  // 不因暂时没有课包而隐藏入口，避免用户误以为分类不存在。
  .filter((category) => category.id !== wordLibraryRoot.value?.id)
  .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "zh-CN")));
const wordFilteredCourses = computed<Course[]>(() => {
  const category = selectedWordCategory.value || wordLibraryRoot.value;
  if (!category) return [];
  const ids = new Set([category.id, ...wordLibraryCategories.value.filter((item) => item.path?.includes(category.id)).map((item) => item.id)]);
  return publishedCourses.value.filter((course) => (course.categoryId && ids.has(course.categoryId)) || course.categoryPath?.some((id) => ids.has(id)));
});
const wordLibraryTotalCount = computed(() => wordLibraryRoot.value?.stats?.words || wordLibraryRoot.value?.stats?.sentences || wordFilteredCourses.value.reduce((sum, course) => sum + wordCourseItemCount(course), 0));
const wordFilteredItemCount = computed(() => wordFilteredCourses.value.reduce((sum, course) => sum + wordCourseItemCount(course), 0));
const catalogExpandedLesson = computed(() => {
  const course = [...filteredCourses.value, ...wordFilteredCourses.value].find((item) => item.id === catalogSelectedCourseId.value);
  return course?.lessons.find((lesson) => lesson.id === catalogExpandedLessonId.value) || null;
});
const accountInitial = computed(() => {
  const name = currentUser.value?.nickname || currentUser.value?.phone;
  if (!name) return "火";
  return String(name).slice(0, 1).toUpperCase();
});
const personalWordLibrariesForPanel = computed(() => learnerActivity.value?.personalWordLibraries || []);
const personalWordLibraryTotal = computed(() => personalWordLibrariesForPanel.value.reduce((sum, library) => sum + library.items.length, 0));
const smartMistakeLibrary = computed(() => adaptiveHome.value?.libraries.find((item) => item.type === "mistakes") || null);
const smartUnknownWordLibrary = computed(() => adaptiveHome.value?.libraries.find((item) => item.type === "unknown_words") || null);
const activeAccountSmartLibrary = computed(() => accountLibraryView.value === "mistakes"
  ? smartMistakeLibrary.value
  : accountLibraryView.value === "unknown_words" ? smartUnknownWordLibrary.value : null);
const accountLibraryTotal = computed(() => (smartMistakeLibrary.value?.active.length || 0)
  + (smartUnknownWordLibrary.value?.active.length || 0)
  + personalWordLibraryTotal.value
  + personalAiTrainingPacks.value.length);
const cumulativeLearnedWords = computed(() => learnerActivity.value?.learningHistory.learnedWords || 0);
const cumulativeLearnedSentences = computed(() => learnerActivity.value?.learningHistory.learnedContentSentences || 0);
const cumulativeStudyTimeLabel = computed(() => {
  const totalMinutes = Math.max(0, Math.round(bootstrap.value?.study.totalMinutes || 0));
  if (totalMinutes < 60) return `${totalMinutes}分钟`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}小时${minutes}分钟` : `${hours}小时`;
});
const desktopPlatformOptions = computed(() => desktopDownload.value?.platforms || []);
const recommendedDesktopPackage = computed(() => desktopDownload.value?.platforms.find((item) => item.platform === desktopPlatform.value) || desktopDownload.value?.platforms.find((item) => item.platform === desktopDownload.value?.recommendedPlatform) || null);
const recommendedDesktopLabel = computed(() => recommendedDesktopPackage.value?.label || "桌面端");
const desktopInstalled = computed(() => desktopLocalBridgeRunning.value || Boolean(desktopInstallStatus.value?.installed));
const shouldShowDesktopDownload = computed(() => DESKTOP_CLIENT_DOWNLOAD_ENABLED && !isDesktopShell && desktopInstallCheckReady.value && !desktopInstalled.value);
const introDemoVisible = computed(() => introDemoRunning.value || introShortcutVisible.value);
const introDemoTypedText = computed(() => dictationSlots.value.filter(Boolean).join(" "));
const introDemoTargetLength = computed(() => currentSentence.value?.english.replace(/\s/g, "").length || 1);
const introDemoTypedLength = computed(() => dictationSlots.value.join("").replace(/\s/g, "").length);
const introDemoProgress = computed(() => {
  if (introDemoPhase.value === "complete") return 100;
  if (introDemoPhase.value === "listen") return 8;
  if (introDemoPhase.value === "submit") return 96;
  return Math.min(92, Math.max(12, Math.round((introDemoTypedLength.value / introDemoTargetLength.value) * 88)));
});
const introDemoTitle = computed(() => {
  if (introDemoPhase.value === "listen") return "先不看答案，认真听一遍…";
  if (introDemoRunning.value && introDemoPhase.value === "typing") return introDemoTypedText.value || "准备开始输入";
  if (introDemoPhase.value === "submit") return introDemoTypedText.value || "正在提交";
  return `✓ ${currentSentence.value?.english || "听写演示已完成"}`;
});
const fullscreenActive = computed(() => (isDesktopShell ? desktopFullscreen.value : browserFullscreen.value));
const fullscreenButtonLabel = computed(() => (fullscreenActive.value ? "缩小" : "全屏"));
const fullscreenButtonTitle = computed(() => (fullscreenActive.value ? "退出全屏，缩小展示" : "进入全屏学习"));
const theaterClasses = computed(() => ({
  desktop: isDesktopShell,
  "dictation-session": mode.value === "dictation",
  "study-session": mode.value === "learning",
  "drawer-open": sidebarOpen.value,
  [`theme-${settings.value.displayStyle}`]: true,
  [`interface-${settings.value.interfaceMode}`]: true,
  [`input-${settings.value.inputStyle}`]: true,
  [`gesture-${settings.value.gestureMode}`]: true
}));
const theaterStyle = computed(() => ({
  "--learner-font-size": `${settings.value.fontSize}rem`,
  "--learner-font-family": fontOptions.find((font) => font.value === settings.value.fontFamily)?.stack || fontOptions[0].stack
}));
const lessonProgressPercent = computed(() => (sentences.value.length ? ((currentIndex.value + 1) / sentences.value.length) * 100 : 0));
const dailyQuotaPercent = computed(() => {
  const today = bootstrap.value?.study.todayMinutes || 0;
  const quota = bootstrap.value?.study.quotaMinutes || 30;
  return Math.min(100, (today / quota) * 100);
});
const thousandHourPercent = computed(() => ((bootstrap.value?.study.thousandHourProgress || 0) * 100).toFixed(3));
const formattedElapsed = computed(() => formatClock(elapsedSeconds.value));
const grammarChips = computed(() => {
  if (currentSentence.value && !currentIsWordbookSentence.value && displayStructure(currentSentence.value)[0]?.label === "整句阅读") return ["结构标注待校审 · 先理解整句，不记忆推测标签"];
  const tags = currentSentence.value?.grammarTags || [];
  if (currentIsWordbookSentence.value) return tags.slice(0, 6);
  const structureTags = sentenceSegments.value.filter((segment) => segment.explain).map((segment) => `${segment.label}：${segment.explain}`);
  return [...tags, ...structureTags].slice(0, 6);
});
const sentenceStudyDetailsVisible = computed(() => mode.value === "learning" || (mode.value === "dictation" && showAnswer.value));
const dictationReviewVisible = computed(() => mode.value === "dictation" && showAnswer.value);
function studyPartOfSpeechLabel(pos: string) {
  const labels: Record<string, string> = { "pron.": "代词", "v.": "动词", "n.": "名词", "adj.": "形容词", "adv.": "副词", "art.": "冠词", "prep.": "介词", "conj.": "连词", "aux.": "助动词", "modal.": "情态动词", "inf.": "不定式" };
  return labels[pos] || pos;
}
function studyPartOfSpeechColor(pos: string) {
  const colors: Record<string, string> = { "pron.": "#c53ce5", "v.": "#8555eb", "n.": "#e84591", "adj.": "#00a67a", "adv.": "#159fdb", "art.": "#08a6c6", "prep.": "#e58a00", "conj.": "#ef7614", "modal.": "#779c14", "inf.": "#08a6c6" };
  return colors[pos] || "#60748a";
}
const structuredSentenceSegments = computed<SegmentView[]>(() => {
  const sentence = currentSentence.value;
  if (!sentence) return [];
  if (currentIsWordbookSentence.value) {
    return [
      {
        id: `word-${sentence.id}`,
        label: "单词",
        text: sentence.english,
        explain: sentence.chinese,
        phonetic: sentence.phonetic || "",
        color: roleColors[0]
      }
    ];
  }
  const structure = displayStructure(sentence);
  return structure.map((part, index) => ({
    id: `${part.label}-${part.text}-${index}`,
    label: part.label,
    text: part.text,
    explain: part.explain,
    phonetic: phoneticForSegment(sentence, part.text, index),
    color: colorForRole(part.label, index)
  }));
});
const sentenceSegments = computed<SegmentView[]>(() => {
  const sentence = currentSentence.value;
  if (!sentence) return [];
  if (settings.value.segmentPractice || currentIsWordbookSentence.value) return structuredSentenceSegments.value;
  return [
    {
      id: `whole-${sentence.id}`,
      label: "整句",
      text: sentence.english,
      explain: sentence.chinese,
      phonetic: sentence.phonetic || "",
      color: roleColors[0]
    }
  ];
});
const visibleSegments = computed(() => {
  if (!wordView.value) return sentenceSegments.value;
  const sentence = currentSentence.value;
  if (!sentence) return [];
  const grammarRoles = wordGrammarRoles(sentence);
  let wordIndex = 0;
  return wordViewTokens(sentence).map((token, index) => {
    if (token.kind === "punctuation") {
      return {
        id: `punctuation-${token.text}-${index}`,
        label: "固定标点",
        text: token.text,
        explain: "固定标点，无需填写。",
        phonetic: "",
        color: "#94a3b8",
        fixedPunctuation: true
      };
    }
    const currentWordIndex = wordIndex++;
    const grammarRole = grammarRoles[currentWordIndex];
    const dictionaryEntry = localWordEntry(token.text);
    const primaryMeaning = dictionaryEntry?.meanings?.[0];
    const fallback = studyWordDetail(token.text);
    return {
      id: `word-${token.text}-${currentWordIndex}`,
      label: grammarRole?.label || "",
      text: token.text,
      explain: primaryMeaning?.zh || fallback?.zh || keywordExplain(token.text),
      phonetic: dictionaryEntry?.usPhonetic || dictionaryEntry?.ukPhonetic || fallback?.phonetic || "",
      partOfSpeech: primaryMeaning?.partOfSpeech || fallback?.partOfSpeech || "",
      color: grammarRole?.color || roleColors[currentWordIndex % roleColors.length]
    };
  });
});
const studyDictionary = ref<Record<string, StudyDictionaryEntry>>({});
// Cache by exact sentence content, not array index. Grammar arrivals must never
// replace currentSentence (which would reset the learner's typed answer).
const studyGrammarCache = ref<Record<string, GrammarResult>>({});
let studyGrammarRequest = 0;
let studyGrammarTimer: ReturnType<typeof setTimeout> | undefined;
const studyGrammarStatus = computed(() => {
  const sentence = currentSentence.value;
  if (!sentence || hasCompleteGrammar(sentence) || !requiresSentenceGrammar(sentence.english)) return null;
  return studyGrammarCache.value[sentence.english] || { english: sentence.english, status: 'queued', stage: '等待加载语法', attempt: 0 };
});
async function loadCurrentStudyGrammar(retry = false) {
  const request = ++studyGrammarRequest;
  if (studyGrammarTimer) clearTimeout(studyGrammarTimer);
  const current = currentSentence.value;
  if (!current) return;
  const candidates = [current, ...sentences.value.slice(currentIndex.value + 1, currentIndex.value + 5)]
    .filter(s => requiresSentenceGrammar(s.english) && !hasCompleteGrammar(s) && studyGrammarCache.value[s.english]?.status !== 'completed');
  const ids = [...new Set(candidates.map(s => s.id))].slice(0, 5);
  if (!ids.length) return;
  const accept = (results: Array<GrammarResult & { id: string }>) => {
    for (const result of results) if (candidates.some(s => s.id === result.id && s.english === result.english)) studyGrammarCache.value[result.english] = result;
    return results.some(r => r.status === 'queued' || r.status === 'running');
  };
  const failed = () => {
    if (request !== studyGrammarRequest) return;
    for (const sentence of candidates) if (studyGrammarCache.value[sentence.english]?.status !== 'completed') studyGrammarCache.value[sentence.english] = { english: sentence.english, status: 'failed', stage: '语法加载未完成', attempt: 0 };
  };
  const poll = async () => {
    if (request !== studyGrammarRequest) return;
    try {
      const pending = accept(await loadSentenceGrammar(ids, false));
      if (pending && request === studyGrammarRequest) studyGrammarTimer = setTimeout(poll, 2000);
    } catch { failed(); }
  };
  try {
    const pending = accept(await loadSentenceGrammar(ids, true, retry));
    if (pending && request === studyGrammarRequest) studyGrammarTimer = setTimeout(poll, 2000);
  } catch { failed(); }
}
const studyDictionaryLoading = ref(false);
const studyDictionaryError = ref("");
const studyDictionaryFetched = new Set<string>();
let studyDictionaryRequest = 0;
async function loadCurrentStudyDictionary() {
  const request = ++studyDictionaryRequest;
  const current = currentSentence.value;
  if (!current) return;
  const needsCurrentDictionary = sentenceAnnotationIssues(current).length > 0;
  const texts = [current, ...sentences.value.slice(currentIndex.value + 1, currentIndex.value + 4)]
    .filter(s => sentenceAnnotationIssues(s).length > 0).map(s => s.english);
  const words = [...new Set(texts.flatMap((text) => normalizeWords(text).map(studyKey).flatMap(word=>word.endsWith("'s")?[word,word.slice(0,-2)]:[word])))].filter((word) => /^[a-z0-9][a-z0-9'-]{0,63}$/.test(word) && !studyDictionaryFetched.has(word)).slice(0, 100);
  studyDictionaryError.value = "";
  studyDictionaryLoading.value = needsCurrentDictionary && words.length > 0;
  if (!words.length) return;
  try {
    const entries = await getStudyDictionary(words);
    // Cache by exact surface form; an old request may populate the cache but
    // never replace another sentence's tokens or change its loading status.
    for (const entry of entries) studyDictionary.value[studyKey(entry.word)] = entry;
    for (const word of words) studyDictionaryFetched.add(word);
  } catch {
    if (request === studyDictionaryRequest && needsCurrentDictionary) studyDictionaryError.value = "逐词资料未加载成功，请重试。";
  } finally {
    if (request === studyDictionaryRequest) studyDictionaryLoading.value = false;
  }
}
const sentenceStudyWords = computed(() => {
  const sentence = currentSentence.value;
  if (!sentence) return [];
  const cached = studyGrammarCache.value[sentence.english];
  const grammarRoles = studyGrammarRoles(!hasCompleteGrammar(sentence) && cached?.status === 'completed' && cached.structure ? { ...sentence, structure: cached.structure } : sentence);
  const words = grammarTokens(sentence.english);
  const entries: Record<string, StudyDictionaryEntry> = { ...Object.fromEntries((bootstrap.value?.words || []).map((entry) => [studyKey(entry.word), entry])), ...studyDictionary.value };
  const ipa = alignedSentenceIpa(sentence.phonetic || "", words.length);
  const stored=sentence.annotation && sentenceAnnotationIssues(sentence).length===0 ? sentence.annotation.words : undefined;
  return words.map((text, currentWordIndex) => {
      const grammarRole = grammarRoles[currentWordIndex];
      const detail = annotateStudyWord(words, currentWordIndex, entries, ipa[currentWordIndex], grammarRole?.label, { chinese: sentence.chinese, roles: grammarRoles.map(role=>role.label) });
      const fallback = studyWordDetail(text);
      return {
        ...detail,
        id: `study-word-${text}-${currentWordIndex}`,
        index: currentWordIndex,
        text,
        phonetic: stored?.[currentWordIndex]?.phonetic || detail.phonetic || fallback?.phonetic || "",
        explain: stored?.[currentWordIndex]?.meaning || detail.explain || conciseStudyMeaning(fallback?.zh || ""),
        label: stored?.[currentWordIndex]?.pos || detail.label,
        role: grammarRole?.label || "",
        color: grammarRole?.color || detail.color,
      };
    });
});
const sentenceStudyGroups = computed(() => groupStudyWords(sentenceStudyWords.value));
const wordPopupStudy = computed(() => {
  const exact=sentenceStudyWords.value[wordPopupStudyIndex.value];
  return exact && studyKey(exact.text)===studyKey(wordPopupText.value) ? exact : sentenceStudyWords.value.find(w=>studyKey(w.text)===studyKey(wordPopupText.value));
});
const learningDisplaySegments = computed(() => (wordView.value ? visibleSegments.value : structuredSentenceSegments.value));
const puzzleCandidates = computed<PuzzleCandidate[]>(() => {
  const list = targetWords.value.map((word, index) => ({ id: `${index}-${word}`, word, order: index }));
  return [...list].sort((a, b) => stableShuffleScore(a.word, a.order) - stableShuffleScore(b.word, b.order));
});
const waveformBars = computed(() => {
  const source = waveformProfile.value.length ? waveformProfile.value : Array.from({ length: 44 }, () => 4);
  return source.map((height, index) => {
    return {
      id: index,
      height,
      opacity: Number((0.28 + Math.min(0.7, height / 48)).toFixed(2))
    };
  });
});
const celebrationSparks = computed(() =>
  Array.from({ length: 96 }, (_, index) => {
    const isBurst = index < 66;
    const cluster = index % 3;
    const clusterIndex = Math.floor(index / 3);
    const angle = (clusterIndex / 22) * Math.PI * 2;
    const distance = 105 + (clusterIndex % 5) * 25;
    return {
      id: index,
      kind: isBurst ? "burst" : "confetti",
      style: {
        "--burst-left": ["30%", "50%", "70%"][cluster],
        "--burst-top": ["38%", "29%", "40%"][cluster],
        "--spark-x": `${Math.cos(angle) * distance}px`,
        "--spark-y": `${Math.sin(angle) * distance}px`,
        "--confetti-left": `${4 + ((index * 17) % 92)}%`,
        "--confetti-drift": `${((index % 7) - 3) * 38}px`,
        "--confetti-rotate": `${(index * 47) % 360}deg`,
        "--spark-delay": `${(index % 9) * 35}ms`,
        "--spark-color": ["#00e5ff", "#00ff9f", "#ffd447", "#ff4f8b", "#bba7ff", "#ff7b38"][index % 6]
      } as Record<string, string>
    };
  })
);

const checkpointSparks = computed(() =>
  Array.from({ length: 84 }, (_, index) => {
    const angle = (index / 84) * Math.PI * 2;
    const distance = 180 + (index % 7) * 34;
    return {
      id: index,
      style: {
        "--checkpoint-x": `${Math.cos(angle) * distance}px`,
        "--checkpoint-y": `${Math.sin(angle) * distance}px`,
        "--checkpoint-delay": `${(index % 14) * 30}ms`,
        "--checkpoint-color": ["#ffd447", "#ff8a3d", "#00e5ff", "#7cffb2", "#c6a8ff"][index % 5]
      } as Record<string, string>
    };
  })
);

function normalizeWords(sentence: string) {
  return sentence
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .split(/\s+/)
    .map((word) => word.replace(/^[^A-Za-z0-9']+|[^A-Za-z0-9']+$/g, ""))
    .filter(Boolean);
}

function typingPracticeText(sentence: string) {
  // Sentence-ending punctuation is displayed in the source sentence, but does
  // not add a redundant final keystroke to typing practice. Internal commas,
  // apostrophes and hyphens remain part of the exercise.
  return sentence.replace(/[\s,.!?;:，。！？；：]+$/u, "").trimEnd();
}

type WordViewToken = { kind: "word" | "punctuation"; text: string };

function wordViewTokens(sentence: Sentence): WordViewToken[] {
  const english = sentence.english.replace(/[‘’]/g, "'").trim();
  const rawTokens = english.match(/[A-Za-z0-9]+(?:'[A-Za-z0-9]+)*|[,.!?;:]/g) || [];
  const tokens: WordViewToken[] = rawTokens.map((text) => ({
    kind: /^[,.!?;:]$/.test(text) ? "punctuation" : "word",
    text
  }));

  const chineseCommaCount = (sentence.chinese.match(/[，,；;]/g) || []).length;
  const englishCommaCount = tokens.filter((token) => token.kind === "punctuation" && /[,;]/.test(token.text)).length;
  for (let missing = englishCommaCount; missing < chineseCommaCount; missing += 1) {
    const insertionIndex = inferredClauseBoundary(tokens, missing + 1, chineseCommaCount + 1, sentence.chinese);
    tokens.splice(insertionIndex, 0, { kind: "punctuation", text: sentence.chinese.includes("；") ? ";" : "," });
  }

  const lastToken = tokens[tokens.length - 1];
  if (!lastToken || lastToken.kind !== "punctuation" || !/[.!?]/.test(lastToken.text)) {
    const ending = sentence.chinese.trim().slice(-1);
    tokens.push({ kind: "punctuation", text: ending === "？" ? "?" : ending === "！" ? "!" : "." });
  }
  return tokens;
}

function inferredClauseBoundary(tokens: WordViewToken[], boundaryNumber: number, clauseCount: number, chinese: string) {
  const markers = new Set(["let's", "but", "and", "so", "because", "while", "when", "then", "yet", "please"]);
  const markerIndex = tokens.findIndex((token, index) => token.kind === "word"
    && index > 0
    && markers.has(token.text.toLowerCase())
    && tokens[index - 1]?.kind !== "punctuation");
  if (markerIndex > 0) return markerIndex;

  const wordPositions = tokens.map((token, index) => token.kind === "word" ? index : -1).filter((index) => index >= 0);
  const chineseClauses = chinese.split(/[，,；;]/).filter(Boolean);
  const precedingLength = chineseClauses.slice(0, boundaryNumber).join("").length;
  const totalLength = Math.max(1, chineseClauses.join("").length);
  const proportionalWord = Math.max(1, Math.min(wordPositions.length - 1, Math.round((precedingLength / totalLength) * wordPositions.length)));
  return wordPositions[proportionalWord] ?? Math.max(1, Math.floor(tokens.length / clauseCount));
}

function keyCodeForTypingChar(char: string) {
  if (!char) return "ENTER";
  if (char === " ") return "SPACE";
  const upper = char.toUpperCase();
  if (/^[A-Z0-9]$/.test(upper)) return upper;
  const map: Record<string, string> = {
    ".": "PERIOD",
    ",": "COMMA",
    "'": "QUOTE",
    '"': "QUOTE",
    ";": "SEMICOLON",
    ":": "SEMICOLON",
    "/": "SLASH",
    "?": "SLASH",
    "-": "MINUS",
    "_": "MINUS",
    "=": "EQUAL",
    "+": "EQUAL",
    "[": "BRACKETLEFT",
    "{": "BRACKETLEFT",
    "]": "BRACKETRIGHT",
    "}": "BRACKETRIGHT",
    "\\": "BACKSLASH",
    "|": "BACKSLASH",
    "`": "BACKQUOTE",
    "~": "BACKQUOTE",
    "!": "1",
    "@": "2",
    "#": "3",
    "$": "4",
    "%": "5",
    "^": "6",
    "&": "7",
    "*": "8",
    "(": "9",
    ")": "0"
  };
  return map[char] || upper;
}

function typingCharMatches(input: string, target: string) {
  if (!target) return false;
  if (target === " ") return input === " ";
  if (/^[a-z]$/i.test(target)) return input.toLowerCase() === target.toLowerCase();
  // Course sources can contain typographic or full-width hyphens. Learners
  // should always be able to enter them with the physical keyboard Minus key.
  const normalizeTypingChar = (char: string) => char.replace(/[‐‑‒–—−－]/g, "-");
  return normalizeTypingChar(input) === normalizeTypingChar(target);
}

function loadSettings(): LearnerSettings {
  try {
    const raw = localStorage.getItem("huosu_learning_settings");
    if (!raw) return { ...defaultSettings };
    const parsed = JSON.parse(raw) as Partial<LearnerSettings> & { __settingsVersion?: number };
    const merged = { ...defaultSettings, ...parsed };
    if (!parsed.__settingsVersion) merged.autoNext = true;
    if ((parsed.__settingsVersion || 0) < 6) merged.puzzleAutoNext = true;
    if ((parsed.__settingsVersion || 0) < 3) merged.gestureMode = "realistic";
    if (!["slow", "normal", "fast"].includes(String(merged.speechRate))) merged.speechRate = "normal";
    merged.keyboardSoundVolume = Math.min(100, Math.max(0, Number(merged.keyboardSoundVolume) || 0));
    return merged;
  } catch {
    return { ...defaultSettings };
  }
}

function loadGuestTrialState(): GuestTrialState {
  try {
    const raw = localStorage.getItem(GUEST_TRIAL_STORAGE_KEY);
    if (!raw) return { completedSentenceIds: [], completedCount: 0, locked: false, updatedAt: new Date(0).toISOString() };
    const parsed = JSON.parse(raw) as Partial<GuestTrialState>;
    const completedSentenceIds = Array.from(
      new Set((Array.isArray(parsed.completedSentenceIds) ? parsed.completedSentenceIds : []).filter(Boolean).map(String))
    );
    const completedCount = Math.max(Number(parsed.completedCount) || 0, completedSentenceIds.length);
    return {
      completedSentenceIds,
      completedCount,
      locked: Boolean(parsed.locked) || completedCount >= FREE_TRIAL_LIMIT,
      updatedAt: parsed.updatedAt || new Date().toISOString()
    };
  } catch {
    return { completedSentenceIds: [], completedCount: 0, locked: false, updatedAt: new Date(0).toISOString() };
  }
}

function persistGuestTrialState() {
  localStorage.setItem(GUEST_TRIAL_STORAGE_KEY, JSON.stringify(guestTrialState.value));
}

function clearGuestTrialState() {
  guestTrialState.value = { completedSentenceIds: [], completedCount: 0, locked: false, updatedAt: new Date().toISOString() };
  localStorage.removeItem(GUEST_TRIAL_STORAGE_KEY);
}

function markGuestTrialSentence(sentenceId?: string) {
  if (currentUser.value || guestTrialState.value.locked) return;
  const completedSentenceIds = sentenceId
    ? Array.from(new Set([...guestTrialState.value.completedSentenceIds, sentenceId]))
    : guestTrialState.value.completedSentenceIds;
  const completedCount = (Number(guestTrialState.value.completedCount) || 0) + 1;
  guestTrialState.value = {
    completedSentenceIds,
    completedCount,
    locked: completedCount >= FREE_TRIAL_LIMIT,
    updatedAt: new Date().toISOString()
  };
  persistGuestTrialState();
}

function showTrialGate() {
  pausePracticeForAuthentication();
  trialGateOpen.value = true;
  accountPanelOpen.value = false;
  settingsOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
}

function openTrialLoginPanel() {
  trialGateOpen.value = false;
  accountPanelOpen.value = true;
  settingsOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
}

function dismissTrialGate() {
  trialGateOpen.value = false;
  accountPanelOpen.value = false;
  authNotice.value = "";
  landingVisible.value = true;
}

function persistSettings() {
  localStorage.setItem("huosu_learning_settings", JSON.stringify({ ...settings.value, __settingsVersion: 7 }));
}

function toggleSettingMenu(key: string) {
  activeSettingMenu.value = activeSettingMenu.value === key ? null : key;
}

function closeFloatingPanels() {
  settingsOpen.value = false;
  accountPanelOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
}

function closeAllTransientPanels() {
  sidebarOpen.value = false;
  pwaInstallGuideOpen.value = false;
  closeFloatingPanels();
}

function detectPwaGuideTarget(): PwaGuideTarget {
  const userAgent = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  if (/Android|MicroMessenger/i.test(userAgent)) return "android";
  if (/Macintosh/.test(userAgent) && /Safari/.test(userAgent) && !/(Chrome|Chromium|Edg|OPR)/.test(userAgent)) return "safari";
  return "chrome";
}

function isRunningAsPwa() {
  const pwaNavigator = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || Boolean(pwaNavigator.standalone);
}

function openPwaInstallGuide() {
  pwaGuideTarget.value = detectPwaGuideTarget();
  pwaInstallStatus.value = pwaStandalone.value
    ? "当前已在独立桌面应用中运行。"
    : pwaInstallReady.value && pwaGuideTarget.value === "chrome"
      ? "当前浏览器已准备好一键安装。"
      : "按下方步骤操作，即可生成桌面或主屏图标。";
  closeFloatingPanels();
  pwaInstallGuideOpen.value = true;
}

function closePwaInstallGuide() {
  pwaInstallGuideOpen.value = false;
}

async function requestPwaInstall() {
  const prompt = deferredPwaInstallPrompt;
  if (!prompt) {
    pwaInstallStatus.value = "当前浏览器没有提供一键安装入口，请按上方步骤从浏览器菜单完成添加。";
    return;
  }
  try {
    await prompt.prompt();
    const choice = await prompt.userChoice;
    pwaInstallStatus.value = choice.outcome === "accepted"
      ? "浏览器正在创建桌面应用，完成后可从桌面或应用列表启动。"
      : "安装已取消；你可以随时再次打开本指引。";
  } catch {
    pwaInstallStatus.value = "浏览器未能完成安装，请按上方步骤从浏览器菜单添加。";
  }
}

function handleBeforePwaInstallPrompt(event: Event) {
  event.preventDefault();
  deferredPwaInstallPrompt = event as DeferredPwaInstallPrompt;
  pwaInstallReady.value = true;
}

function handlePwaInstalled() {
  deferredPwaInstallPrompt = null;
  pwaInstallReady.value = false;
  pwaStandalone.value = true;
  pwaInstallStatus.value = "已添加为桌面应用，可从桌面或应用列表直接启动。";
}

async function registerPwaServiceWorker() {
  if (isDesktopShell || !("serviceWorker" in navigator)) return;
  try {
    await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    // A browser may disable service workers; the online learning flow still works.
  }
}

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value;
  closeFloatingPanels();
}

function toggleSettingsPanel() {
  settingsOpen.value = !settingsOpen.value;
  accountPanelOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
}

function toggleAccountPanel() {
  accountPanelOpen.value = !accountPanelOpen.value;
  if (accountPanelOpen.value && accountTab.value === "history") accountTab.value = "overview";
  settingsOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
}

function openLearningHistory() {
  accountTab.value = "history";
  accountPanelOpen.value = true;
  settingsOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
  void refreshLearnerActivity();
}

function toggleDesktopDownloadPanel() {
  desktopDownloadOpen.value = !desktopDownloadOpen.value;
  settingsOpen.value = false;
  accountPanelOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  activeSettingMenu.value = null;
}

function toggleModeMenu() {
  modeMenuOpen.value = !modeMenuOpen.value;
  settingsOpen.value = false;
  accountPanelOpen.value = false;
  desktopDownloadOpen.value = false;
  affiliateIntroOpen.value = false;
  activeSettingMenu.value = null;
}

function setDisplayStyle(value: DisplayStyle) {
  settings.value.displayStyle = value;
  activeSettingMenu.value = null;
}

function setInterfaceMode(value: InterfaceMode) {
  settings.value.interfaceMode = value;
  activeSettingMenu.value = null;
}

function setVoice(value: VoiceCode) {
  // 真人录音句子包只可使用其审核后的原声；设置面板已隐藏音色选择，
  // 这里再做一层保护，避免快捷路径或旧弹层改变后续的播放逻辑。
  if (currentLessonUsesHumanRecording.value) return;
  // Keep the browser's playback permission attached to this click. The newly
  // selected voice may need one server lookup before its MP3 can be reused.
  unlockSpeechAudioContext();
  stopActiveSpeech();
  settings.value.voiceCode = value;
  ttsUnavailableUntilByKey.clear();
  activeSettingMenu.value = null;
}

function setSpeechRate(value: SpeechRateValue) {
  unlockSpeechAudioContext();
  stopActiveSpeech();
  settings.value.speechRate = value;
  ttsUnavailableUntilByKey.clear();
  activeSettingMenu.value = null;
}

function toggleBoolean(key: BooleanSettingKey) {
  settings.value = { ...settings.value, [key]: !settings.value[key] };
}

function repeatLabel(value: RepeatCount) {
  return value === "manual" ? "手动" : `${value}次`;
}

function restoreSettings() {
  settings.value = { ...defaultSettings };
  activeSettingMenu.value = null;
}

function getSelectValue(event: Event) {
  return (event.target as HTMLSelectElement).value;
}

function resetCatalogPreview() {
  catalogSelectedCourseId.value = "";
  catalogExpandedLessonId.value = "";
}

function setCatalogContentType(type: "words" | "sentences") {
  catalogContentType.value = type;
  activeCatalogDropdown.value = "";
  if (type === "sentences" && !activeRootCategory.value && learnerCategoryRoots.value[0]) {
    selectSentenceRoot(learnerCategoryRoots.value[0].id);
  }
  if (type === "words" && !selectedWordCategory.value && wordLibraryRoot.value) {
    selectedWordCategoryId.value = wordLibraryRoot.value.id;
  }
  resetCatalogPreview();
}

async function searchCatalogWordLibrary() {
  if (catalogWordBusy.value) return;
  catalogWordBusy.value = true;
  try {
    catalogWordResults.value = await searchWords(catalogWordQuery.value);
  } finally {
    catalogWordBusy.value = false;
  }
}

function selectCategoryFilter(categoryId: string) {
  selectedCategoryId.value = categoryId;
  activeCatalogDropdown.value = "";
  resetCatalogPreview();
}

function catalogCategoryLabel(category: ContentCategory, contentType: "words" | "sentences" = "sentences") {
  if (contentType === "words") {
    const wordCount = category.stats?.words || category.stats?.sentences || categorySentenceCount(category);
    return `${category.name}（${categoryCourseCount(category)}包 / ${wordCount}词）`;
  }
  return `${category.name}（${categoryCourseCount(category)}课 / ${categorySentenceCount(category)}句 / ${categoryWordCount(category)}词）`;
}

function toggleCatalogDropdown(level: "root" | "second" | "third" | "word-root" | "word-second") {
  activeCatalogDropdown.value = activeCatalogDropdown.value === level ? "" : level;
}

function selectWordCategoryFromMenu(categoryId: string) {
  selectWordLibraryCategory(categoryId);
}

function selectWordLibraryCategory(categoryId: string) {
  selectedWordCategoryId.value = categoryId || wordLibraryRoot.value?.id || "root-wordbooks";
  activeCatalogDropdown.value = "";
  resetCatalogPreview();
}

function sentenceThirdCategories(secondCategoryId: string) {
  return visibleLearnerCategoryList.value
    .filter((category) => category.parentId === secondCategoryId && category.status === "enabled")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "zh-CN"));
}

function sentenceSecondExpanded(secondCategoryId: string) {
  return expandedSentenceSecondCategoryIds.value.includes(secondCategoryId);
}

function toggleSentenceSecond(secondCategoryId: string) {
  expandedSentenceSecondCategoryIds.value = sentenceSecondExpanded(secondCategoryId) ? [] : [secondCategoryId];
}

function selectSentenceRoot(categoryId: string) {
  const root = learnerCategoryRoots.value.find((item) => item.id === categoryId);
  if (!root) return;
  const firstSecond = visibleLearnerCategoryList.value
    .filter((item) => item.parentId === root.id && item.status === "enabled")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "zh-CN"))[0];
  expandedSentenceSecondCategoryIds.value = firstSecond ? [firstSecond.id] : [];
  selectCategoryFilter(root.id);
}

function selectSentenceSecond(categoryId: string) {
  const category = secondLevelCategories.value.find((item) => item.id === categoryId);
  if (!category) return;
  expandedSentenceSecondCategoryIds.value = sentenceThirdCategories(categoryId).length ? [categoryId] : [];
  selectCategoryFilter(categoryId);
}

function selectSentenceThird(categoryId: string) {
  const category = visibleLearnerCategoryList.value.find((item) => item.id === categoryId);
  if (!category) return;
  if (category.parentId) expandedSentenceSecondCategoryIds.value = [category.parentId];
  selectCategoryFilter(categoryId);
}

function selectRootCategory(categoryId: string) {
  selectSentenceRoot(categoryId);
}

function selectRootCategoryFromMenu(categoryId: string) {
  selectRootCategory(categoryId);
}

function selectSecondCategory(categoryId: string) {
  if (categoryId) selectSentenceSecond(categoryId);
  else if (activeRootCategory.value) selectSentenceRoot(activeRootCategory.value.id);
}

function selectSecondCategoryFromMenu(categoryId: string) {
  selectSecondCategory(categoryId);
}

function selectThirdCategory(categoryId: string) {
  if (categoryId) selectSentenceThird(categoryId);
  else if (activeSecondCategory.value) selectSentenceSecond(activeSecondCategory.value.id);
  else if (activeRootCategory.value) selectSentenceRoot(activeRootCategory.value.id);
}

function selectThirdCategoryFromMenu(categoryId: string) {
  selectThirdCategory(categoryId);
}

async function selectCourse(courseId: string) {
  if (catalogSelectedCourseId.value === courseId) {
    catalogSelectedCourseId.value = "";
    catalogExpandedLessonId.value = "";
    return;
  }
  catalogSelectedCourseId.value = courseId;
  catalogExpandedLessonId.value = "";
  const currentCourse = bootstrap.value?.courses.find((course) => course.id === courseId);
  if (!currentCourse || currentCourse.lessons.length >= courseLessonCount(currentCourse)) return;
  catalogCourseLoadingId.value = courseId;
  try {
    const detail = await getCourseDetail(courseId);
    if (bootstrap.value) {
      bootstrap.value = {
        ...bootstrap.value,
        courses: bootstrap.value.courses.map((course) => course.id === courseId ? detail : course)
      };
    }
  } catch (error) {
    practiceSyncNotice.value = error instanceof Error ? error.message : "课包加载失败，请重试。";
  } finally {
    if (catalogCourseLoadingId.value === courseId) catalogCourseLoadingId.value = "";
  }
}

function previewCatalogLesson(courseId: string, lessonId: string) {
  catalogSelectedCourseId.value = courseId;
  catalogExpandedLessonId.value = catalogExpandedLessonId.value === lessonId ? "" : lessonId;
}

async function enterCatalogLesson(lessonId: string) {
  // Keep the browser's sound permission tied to this deliberate entry click.
  // The lesson may await OSS preloading before automatic read begins.
  unlockSpeechAudioContext();
  continuousCourseId.value = "";
  continuousNextLessonAudioSerial += 1;
  continuousNextLessonAudioKey = "";
  await loadLesson(lessonId);
}

async function startWholeCourse(course: Course) {
  if (!course.lessons.length || wholeCourseStartingId.value) return;
  unlockSpeechAudioContext();
  wholeCourseStartingId.value = course.id;
  continuousCourseId.value = course.id;
  continuousNextLessonAudioSerial += 1;
  continuousNextLessonAudioKey = "";
  try {
    await loadLesson(course.lessons[0].id);
  } finally {
    wholeCourseStartingId.value = "";
  }
}

async function selectCourseGroup(lessonId: string) {
  unlockSpeechAudioContext();
  courseGroupProgressExpanded.value = false;
  await loadLesson(lessonId);
}

function readCachedPracticeLesson(lessonId: string): { course: Course; lesson: Lesson } | null {
  try {
    const cached = JSON.parse(localStorage.getItem(PRACTICE_LESSON_CACHE_KEY) || "null") as {
      userId?: string;
      lessonId?: string;
      cachedAt?: number;
      course?: Course;
      lesson?: Lesson;
    } | null;
    if (
      cached?.userId !== practiceCacheOwnerId()
      || cached.lessonId !== lessonId
      || !cached.course
      || !cached.lesson
      || Date.now() - Number(cached.cachedAt || 0) > 12 * 60 * 60 * 1000
    ) return null;
    return { course: cached.course, lesson: cached.lesson };
  } catch {
    return null;
  }
}

function cachePracticeLesson(course: Course, lesson: Lesson) {
  try {
    localStorage.setItem(PRACTICE_LESSON_CACHE_KEY, JSON.stringify({
      userId: practiceCacheOwnerId(),
      lessonId: lesson.id,
      cachedAt: Date.now(),
      course,
      lesson
    }));
  } catch {
    // Storage quota or privacy mode must not block learning.
  }
}

function cachePracticePosition() {
  if (!currentLesson.value || !currentSentence.value) return;
  try {
    localStorage.setItem(PRACTICE_POSITION_CACHE_KEY, JSON.stringify({
      userId: practiceCacheOwnerId(),
      lessonId: currentLesson.value.id,
      sentenceId: currentSentence.value.id,
      mode: mode.value,
      cachedAt: Date.now()
    }));
  } catch {
    // Position caching is only a first-paint optimization.
  }
}

function restoreCachedPracticeImmediately() {
  try {
    const cached = JSON.parse(localStorage.getItem(PRACTICE_LESSON_CACHE_KEY) || "null") as {
      userId?: string;
      lessonId?: string;
      cachedAt?: number;
      course?: Course;
      lesson?: Lesson;
    } | null;
    if (
      cached?.userId !== practiceCacheOwnerId()
      || !cached.course
      || !cached.lesson
      || Date.now() - Number(cached.cachedAt || 0) > 12 * 60 * 60 * 1000
    ) return false;
    const position = JSON.parse(localStorage.getItem(PRACTICE_POSITION_CACHE_KEY) || "null") as {
      userId?: string;
      lessonId?: string;
      sentenceId?: string;
      mode?: PracticeMode;
    } | null;
    currentCourse.value = cached.course;
    currentLesson.value = cached.lesson;
    catalogSelectedCourseId.value = cached.course.id;
    catalogExpandedLessonId.value = cached.lesson.id;
    const sentenceIndex = position?.userId === practiceCacheOwnerId() && position.lessonId === cached.lesson.id
      ? cached.lesson.sentences.findIndex((item) => item.id === position.sentenceId)
      : -1;
    currentIndex.value = sentenceIndex >= 0 ? sentenceIndex : 0;
    if (position?.mode && practiceModeKeys.includes(position.mode)) {
      mode.value = normalizeAvailablePracticeMode(position.mode, currentIndex.value);
    }
    sidebarOpen.value = false;
    return true;
  } catch {
    return false;
  }
}

async function loadLesson(lessonId: string, options?: { sentenceId?: string; mode?: PracticeMode }) {
  const inheritedAutoReadSuppression = suppressAutoRead;
  suppressAutoRead = true;
  // Every entry path (course navigation, history resume, AI plan, review
  // preview) converges here.  Stop the previous source before binding a new
  // recording package so its asynchronous media events cannot leak into it.
  stopActiveSpeech();
  courseCompletionOpen.value = false;
  visibilityResumeOpen.value = false;
  activeGeneratedPack.value = null;
  try {
    const cachedLesson = readCachedPracticeLesson(lessonId);
    const { course, lesson } = cachedLesson || await getPracticeLesson(lessonId);
    if (!cachedLesson) cachePracticeLesson(course, lesson);
    currentCourse.value = course;
    currentLesson.value = lesson;
    catalogSelectedCourseId.value = course.id;
    catalogExpandedLessonId.value = lesson.id;
    const restoredIndex = options?.sentenceId ? lesson.sentences.findIndex((item) => item.id === options.sentenceId) : -1;
    currentIndex.value = restoredIndex >= 0 ? restoredIndex : 0;
    if (options?.mode) mode.value = normalizeAvailablePracticeMode(options.mode, currentIndex.value);
    trialGateOpen.value = !currentUser.value && guestTrialState.value.locked;
    sidebarOpen.value = false;
    resetForCurrentSentence();
    await prepareLessonAudioBatch(lesson, currentIndex.value);
  } finally {
    suppressAutoRead = inheritedAutoReadSuppression;
  }
  if (!inheritedAutoReadSuppression) void playCurrentSentenceAutomatically(sentenceResetSerial);
  if (continuousCourseId.value === currentCourse.value?.id) {
    void preloadNextContinuousLesson();
  }
}

async function openReviewPreviewCourse() {
  if (!reviewPreviewCourseId) return false;
  const course = bootstrap.value?.courses.find((candidate) => candidate.id === reviewPreviewCourseId);
  const lesson = course?.lessons[0];
  if (!course || !lesson) {
    practiceSyncNotice.value = "该真人录音句子包暂不可用于前端验收，请返回录入台确认发布状态。";
    return false;
  }
  landingVisible.value = false;
  await loadLesson(lesson.id, { mode: "learning" });
  // 审核说明不是同步失败，不能再复用带“重试同步”按钮的状态条。
  reviewPreviewNotice.value = `审核预览：${course.title}（播放真人原声；学习完成会计入当前账号）`;
  return true;
}

let sentenceResetSerial = 0;
let automaticReadRequestSerial = 0;
let lastAutomaticReadKey = "";

async function playCurrentSentenceAutomatically(expectedResetSerial = sentenceResetSerial) {
  const requestSerial = ++automaticReadRequestSerial;
  await nextTick();
  // Let the blocking audio-loading layer finish its DOM transition before
  // playback. This also keeps the first waveform frame in sync with sound.
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  const sentence = currentSentence.value;
  if (
    requestSerial !== automaticReadRequestSerial
    || expectedResetSerial !== sentenceResetSerial
    || !sentence?.english
    || suppressAutoRead
    || lessonAudioPreparingBlocking.value
    || landingVisible.value
    || trialGateOpen.value
    || onboardingOpen.value
    || adaptiveChoiceOpen.value
    || dailyRecommendationOpen.value
    || !settings.value.autoRead
  ) return;
  const readKey = `${expectedResetSerial}:${sentence.id}:${settings.value.voiceCode}:${settings.value.speechRate}`;
  if (lastAutomaticReadKey === readKey) return;
  lastAutomaticReadKey = readKey;
  const repeatCount = settings.value.autoPlayCount === "manual" ? 1 : settings.value.autoPlayCount;
  // Safari treats the requestAnimationFrame above as outside the user click.
  // It must therefore use the Web Audio context unlocked by the learner's
  // preceding interaction, rather than asking a new HTML media element to
  // autoplay. If that context is unavailable, we show a clear ready-to-play
  // prompt instead of incorrectly reporting a broken audio file.
  await handleSpeak(sentence.english, repeatCount, "automatic");
}

function resetForCurrentSentence() {
  const resetSerial = ++sentenceResetSerial;
  preparedSpeechAudio = undefined;
  if (currentIsSingleWord.value && mode.value === "puzzle") mode.value = "learning";
  cancelSuccessFlow();
  typedAnswer.value = "";
  typingIndex.value = 0;
  typingErrorFlash.value = false;
  resetDictationSlots();
  selectedPuzzleIds.value = [];
  puzzleFailureActive.value = false;
  if (puzzleFailureTimerId) window.clearTimeout(puzzleFailureTimerId);
  practiceSubmitInFlight.value = false;
  feedback.value = null;
  closeWordPopup();
  showAnswer.value = false;
  dictationSubmitted.value = false;
  dictationCompletionConfirmed.value = false;
  answerErrorCount.value = 0;
  practiceActiveMs = 0;
  practiceHintUsed = false;
  practiceMistakeSlots.clear();
  practiceVisitId = crypto.randomUUID();
  attemptPayloads.clear();
  dictationLearningSuggestionOpen.value = false;
  answerCompareView.value = mode.value === "dictation" ? "sentence" : "word";
  isRecording.value = false;
  speechAssessment.value = null;
  stopSpeechStream();
  sessionStartedAt.value = Date.now();
  elapsedSeconds.value = 0;
  void nextTick(() => {
    if (resetSerial !== sentenceResetSerial) return;
    focusTyping();
    void playCurrentSentenceAutomatically(resetSerial);
  });
}

function switchMode(nextMode: PracticeMode) {
  // The following automatic read happens after Vue has rendered the new mode.
  // Unlock Web Audio while this mode-selection click is still a user gesture so
  // Safari can play the ready OSS MP3 instead of treating it as autoplay.
  unlockSpeechAudioContext();
  mode.value = nextMode;
  persistPreferredPracticeMode(nextMode);
  resetForCurrentSentence();
}

function switchToLearningFromSuggestion() {
  dictationLearningSuggestionOpen.value = false;
  switchMode("learning");
}

function chooseMode(nextMode: PracticeMode) {
  modeMenuOpen.value = false;
  if (nextMode === "puzzle" && currentIsSingleWord.value) return;
  if (mode.value === nextMode) return;
  switchMode(nextMode);
}

function landingCharToKey(char: string) {
  if (char === " ") return "Space";
  if (/[a-z]/i.test(char)) return char.toUpperCase();
  if (char === ".") return "Enter";
  return "";
}

function stopLandingTypingDemo() {
  if (landingTypingTimer) window.clearTimeout(landingTypingTimer);
  landingTypingTimer = undefined;
}

function scheduleLandingTypingDemo(delay = 760) {
  stopLandingTypingDemo();
  landingTypingTimer = window.setTimeout(tickLandingTypingDemo, delay);
}

function tickLandingTypingDemo() {
  if (!landingVisible.value) return;
  if (landingTypingIndex.value >= landingDemoSentence.length) {
    landingTypingFlash.value = true;
    landingTypingTimer = window.setTimeout(() => {
      landingTypingFlash.value = false;
      landingTypingIndex.value = 0;
      scheduleLandingTypingDemo(760);
    }, 1350);
    return;
  }
  const typedChar = landingDemoSentence[landingTypingIndex.value];
  landingTypingIndex.value += 1;
  const nextDelay = typedChar === " " ? 360 : /[,.!?]/.test(typedChar) ? 460 : 140 + Math.round(Math.random() * 90);
  scheduleLandingTypingDemo(nextDelay);
}

function pickStarterCourse() {
  const courses = bootstrap.value?.courses || [];
  return courses.find((course) => course.lessons.some((lesson) => (lesson.sentenceCount || lesson.sentences.length) > 0 && (lesson.sentenceCount || lesson.sentences.length) <= 6)) || courses[0];
}

function scrollLandingTo(sectionId: string) {
  if (sectionId === "landing-pricing") void loadLandingPlans();
  const target = document.getElementById(sectionId);
  if (!target) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  window.history.replaceState(window.history.state, "", `#${sectionId}`);
}

async function startLandingTrial(nextMode: PracticeMode = "learning") {
  landingVisible.value = false;
  if (!currentUser.value && guestTrialState.value.locked) {
    showTrialGate();
    return;
  }
  trialGateOpen.value = false;
  suppressAutoRead = true;
  try {
    switchMode(nextMode);
    if (!currentLesson.value) {
      const starterCourse = pickStarterCourse();
      if (starterCourse?.lessons[0]) await loadLesson(starterCourse.lessons[0].id);
    }
  } finally {
    suppressAutoRead = false;
  }
  await nextTick();
  focusTyping();
  const needsIntroDemo = localStorage.getItem("huosu_intro_dictation_demo_v2") !== "done";
  if (needsIntroDemo && nextMode !== "typing") {
    await maybeStartIntroDemo();
  } else if (currentSentence.value && nextMode !== "typing") {
    await handleSpeak(currentSentence.value.english, 1);
  }
}

function openLoginFromLanding() {
  landingVisible.value = false;
  openTrialLoginPanel();
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function maybeStartIntroDemo(force = false) {
  if (landingVisible.value) return;
  if (!force && localStorage.getItem("huosu_intro_dictation_demo_v2") === "done") return;
  if (!currentSentence.value || introDemoRunning.value || introShortcutVisible.value) return;
  mode.value = "dictation";
  suppressAutoRead = true;
  resetForCurrentSentence();
  await nextTick();
  await sleep(420);
  suppressAutoRead = false;
  introDemoRunning.value = true;
  practiceHintUsed = true;
  introShortcutVisible.value = false;
  introDemoPhase.value = "listen";
  introDemoHint.value = "先听一遍目标句子。";
  await handleSpeak(currentSentence.value.english, 1);
  await sleep(700);
  introDemoPhase.value = "typing";
  introDemoHint.value = "看文字如何随键盘逐字输入。";
  resetDictationSlots();
  const words = targetWords.value;
  for (let wordIndex = 0; wordIndex < words.length; wordIndex += 1) {
    dictationCursor.value = wordIndex;
    let typed = "";
    for (const char of words[wordIndex]) {
      introDemoActiveKey.value = /[a-z]/i.test(char) ? char.toUpperCase() : char;
      typed += char;
      setDictationSlot(wordIndex, typed);
      playKeyboardClick("key");
      await sleep(char.length > 1 ? 140 : 105);
    }
    if (wordIndex < words.length - 1) {
      introDemoActiveKey.value = "Space";
      playKeyboardClick("advance");
      await sleep(190);
    }
  }
  introDemoActiveKey.value = "Enter";
  introDemoPhase.value = "submit";
  introDemoHint.value = "按 Enter 提交，系统正在核对答案…";
  playKeyboardClick("advance");
  await sleep(800);
  introDemoPhase.value = "complete";
  introDemoHint.value = "✓ 听写正确！现在再听一遍整句节奏。";
  await handleSpeak(currentSentence.value.english, 1);
  introDemoRunning.value = false;
  introDemoActiveKey.value = "";
  introShortcutVisible.value = true;
  introDemoHint.value = "演示完成。可以重播，或开始自己练。";
}

function finishIntroDemo() {
  introDemoRunning.value = false;
  introShortcutVisible.value = false;
  introDemoActiveKey.value = "";
  localStorage.setItem("huosu_intro_dictation_demo_v2", "done");
  resetForCurrentSentence();
  focusTyping();
}

async function replayIntroDemo() {
  introShortcutVisible.value = false;
  introDemoPhase.value = "listen";
  resetForCurrentSentence();
  await nextTick();
  await maybeStartIntroDemo(true);
}

function handlePracticeClick(event?: MouseEvent) {
  const target = event?.target instanceof HTMLElement ? event.target : null;
  // The practice canvas listens in capture phase. Do not clear the armed
  // second-click state before a word button or its dictionary popover handles
  // the click, otherwise every click is incorrectly treated as the first one.
  if (target?.closest(".word-view .sentence-segment, .study-word-trigger, .word-meaning-popover")) return;
  closeAllTransientPanels();
  closeWordPopup();
  focusTyping();
}

function handleTheaterOutsideClick(event: MouseEvent) {
  if (!courseGroupProgressExpanded.value) return;
  const target = event.target instanceof HTMLElement ? event.target : null;
  if (target?.closest(".course-group-progress-card")) return;
  courseGroupProgressExpanded.value = false;
}

function focusTyping() {
  if (mode.value === "dictation" || mode.value === "typing") {
    typingInputRef.value?.focus();
  }
}

function handleGhostInput(event: Event) {
  const input = event.target as HTMLInputElement;
  input.value = "";
}

function resetDictationSlots() {
  dictationSlots.value = targetWords.value.map(() => "");
  dictationCursor.value = 0;
  dictationCharacterCursor.value = 0;
  dictationInputNotice.value = "";
}

function selectDictationSlot(index: number, characterIndex?: number) {
  const lastIndex = Math.max(0, targetWords.value.length - 1);
  const nextIndex = Math.min(lastIndex, Math.max(0, index));
  dictationCursor.value = nextIndex;
  const value = dictationSlots.value[nextIndex] || "";
  dictationCharacterCursor.value = Math.min(value.length, Math.max(0, characterIndex ?? value.length));
  dictationInputNotice.value = "";
  focusTyping();
}

function syncDictationAnswer() {
  typedAnswer.value = dictationSlots.value.join(" ").trim();
}

function setDictationSlot(index: number, value: string) {
  const limit = dictationWordInputLimit(targetWords.value[index] || "");
  const limitedValue = value.slice(0, limit);
  if (limitedValue && !canonicalWord(targetWords.value[index] || "").startsWith(canonicalWord(limitedValue)) && !practiceMistakeSlots.has(index)) {
    practiceMistakeSlots.add(index);
    answerErrorCount.value += 1;
  }
  const nextSlots = targetWords.value.map((_, slotIndex) => (slotIndex === index ? limitedValue : dictationSlots.value[slotIndex] || ""));
  dictationSlots.value = nextSlots;
  if (index === dictationCursor.value) {
    dictationCharacterCursor.value = Math.min(limitedValue.length, dictationCharacterCursor.value);
  }
  syncDictationAnswer();
}

function canonicalWord(word: string) {
  // Apostrophes are spelling characters in contractions: let's !== lets.
  // Curly and straight apostrophes are typographic variants of the same character.
  // Hyphens are spelling characters too: part-time !== parttime.
  return word.toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[‐‑‒–—−－]/g, "-")
    .replace(/[^a-z0-9'-]+/g, "");
}

function canonicalSentence(sentence: string) {
  return normalizeWords(sentence).map(canonicalWord).filter(Boolean).join(" ");
}

function dictationWordInputLimit(targetWord: string) {
  const targetLength = canonicalWord(targetWord).length;
  return Math.min(DICTATION_ABSOLUTE_WORD_LIMIT, Math.max(targetLength + 3, 8));
}

function isDictationSlotCorrect(index: number) {
  const target = canonicalWord(targetWords.value[index] || "");
  const typed = canonicalWord(dictationSlots.value[index] || "");
  return Boolean(target && typed && target === typed);
}

function isDictationSlotMismatch(index: number, targetWord: string) {
  const target = canonicalWord(targetWord);
  const typed = canonicalWord(dictationSlots.value[index] || "");
  return Boolean(typed && target && !target.startsWith(typed) && target !== typed);
}

function firstIncorrectDictationIndex() {
  return dictationWordStates.value.find((state) => state.status !== "correct")?.index ?? -1;
}

function buildDictationFeedback(): PracticeFeedback {
  const wrongStates = dictationWrongStates.value;
  if (!wrongStates.length) {
    return { correct: true, score: 100, feedback: "全部正确，可以提交成功。" };
  }
  const wrongWords = wrongStates.map((state) => state.target);
  return {
    correct: false,
    score: Math.round(((targetWords.value.length - wrongStates.length) / Math.max(1, targetWords.value.length)) * 100),
    feedback: `发现 ${wrongStates.length} 个单词需要修正。`,
    wrongWords
  };
}

function registerIncorrectAttempt() {
  answerErrorCount.value += 1;
  if (mode.value === "dictation" && answerErrorCount.value >= 3) {
    dictationLearningSuggestionOpen.value = true;
  }
  if (answerErrorCount.value < Math.max(1, settings.value.errorPopupCount)) return false;
  if (mode.value === "dictation") {
    revealDictationAnswer();
  } else {
    showAnswer.value = true;
    answerCompareView.value = "word";
  }
  return true;
}

function revealDictationAnswer() {
  practiceHintUsed = true;
  dictationSubmitted.value = true;
  dictationCompletionConfirmed.value = false;
  showAnswer.value = true;
  answerCompareView.value = "sentence";
  feedback.value = buildDictationFeedback();
  const firstWrong = firstIncorrectDictationIndex();
  if (firstWrong >= 0) selectDictationSlot(firstWrong);
}

function persistDictationResultInBackground() {
  if (currentSentence.value) queuePractice(dictationSlots.value.join(" ").trim(), mode.value);
}

function finishDictationImmediately(options: { persist?: boolean } = {}) {
  if (!currentSentence.value) return false;
  dictationSubmitted.value = true;
  showAnswer.value = true;
  answerCompareView.value = "sentence";
  feedback.value = buildDictationFeedback();

  if (!dictationAllCorrect.value) {
    dictationCompletionConfirmed.value = false;
    const firstWrong = firstIncorrectDictationIndex();
    if (firstWrong >= 0) selectDictationSlot(firstWrong);
    playKeyboardClick("key");
    registerIncorrectAttempt();
    return false;
  }

  dictationLearningSuggestionOpen.value = false;
  dictationCompletionConfirmed.value = true;
  if (options.persist) persistDictationResultInBackground();
  if (!successFlowRunning.value) void runSuccessFlow();
  return true;
}

function stopActiveSpeech() {
  speakingSerial += 1;
  isSpeaking.value = false;
  waveformRunId += 1;
  waveformActive.value = false;
  if (waveformAnimationFrameId) window.cancelAnimationFrame(waveformAnimationFrameId);
  waveformAnimationFrameId = undefined;
  stopPlayingAudioSources();
}

function pausePracticeForAuthentication() {
  automaticReadRequestSerial += 1;
  lessonAudioPreparationSerial += 1;
  continuousNextLessonAudioSerial += 1;
  continuousNextLessonAudioKey = "";
  lessonAudioPreparingBlocking.value = false;
  lessonAudioPreparationVisible.value = false;
  if (lessonAudioCompletionTimer) window.clearTimeout(lessonAudioCompletionTimer);
  lessonAudioCompletionTimer = undefined;
  if (practiceWindowPreloadTimer) window.clearTimeout(practiceWindowPreloadTimer);
  practiceWindowPreloadTimer = undefined;
  activeBackgroundAudioPreloads.forEach((audio) => {
    audio.pause();
    audio.removeAttribute("src");
    audio.load();
  });
  activeBackgroundAudioPreloads.clear();
  stopActiveSpeech();
  cancelSuccessFlow();
  speechNotice.value = "";
  speechNoticeIsError.value = false;
}

watch(trialGateOpen, (open) => {
  if (open) pausePracticeForAuthentication();
});

function stopPlayingAudioSources() {
  try {
    activeSpeechBufferSource?.stop();
  } catch {
    // The source may already have ended.
  }
  activeSpeechBufferSource = undefined;
  if (activeSpeechHtmlAudio) {
    const previous = activeSpeechHtmlAudio;
    previous.pause();
    previous.currentTime = 0;
    activeSpeechHtmlAudio = undefined;
    htmlPlaybackFinishers.get(previous)?.();
  }
}

function cancelSuccessFlow() {
  successFlowSerial += 1;
  successFlowRunning.value = false;
  celebrationActive.value = false;
  puzzleFailureActive.value = false;
  if (celebrationTimerId) window.clearTimeout(celebrationTimerId);
  if (puzzleFailureTimerId) window.clearTimeout(puzzleFailureTimerId);
}

function practiceCanAdvanceByShortcut() {
  return Boolean(feedback.value?.correct || showAnswer.value);
}

function persistPracticeResultInBackground(answer: string, answerMode: PracticeMode) {
  if (currentSentence.value) queuePractice(answer, answerMode);
}

async function triggerSuccessCelebration(flowSerial: number) {
  if (!settings.value.answerSmoke) {
    await sleep(1000);
    return;
  }
  if (celebrationTimerId) window.clearTimeout(celebrationTimerId);
  celebrationActive.value = false;
  await sleep(20);
  if (flowSerial !== successFlowSerial) return;
  celebrationActive.value = true;
  celebrationTimerId = window.setTimeout(() => {
    if (flowSerial === successFlowSerial) celebrationActive.value = false;
  }, 2100);
  await sleep(2100);
  if (flowSerial !== successFlowSerial) return;
  await sleep(1000);
}

async function runSuccessFlow(options: { autoAdvance?: boolean } = {}) {
  if (!currentSentence.value || successFlowRunning.value) return;
  const flowSerial = ++successFlowSerial;
  successFlowRunning.value = true;
  showAnswer.value = true;
  answerCompareView.value = mode.value === "dictation" ? "sentence" : "word";
  const sentenceText = currentSentence.value.english;
  const sentenceId = currentSentence.value.id;
  try {
    playSuccessFanfare();
    void triggerSuccessCelebration(flowSerial);
    const replayPromise = handleSpeak(sentenceText, settings.value.answerPlayCount);
    const autoAdvance = options.autoAdvance ?? settings.value.autoNext;
    if (flowSerial === successFlowSerial && currentSentence.value?.id === sentenceId && autoAdvance) {
      // Keep the learner on the current sentence until its complete feedback
      // audio has finished. Advancing after a fixed timeout cuts off longer
      // sentences and overlaps them with the next question.
      await replayPromise;
      if (flowSerial !== successFlowSerial || currentSentence.value?.id !== sentenceId) return;
      nextSentence({ completedAudio: true });
      return;
    }
    await replayPromise;
  } finally {
    if (flowSerial === successFlowSerial) {
      successFlowRunning.value = false;
    }
  }
}

function playPuzzleFailureTone() {
  try {
    const context = unlockFeedbackAudioContext();
    if (!context || context.state !== "running") return;
    const now = context.currentTime;
    const master = context.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.14, now + 0.015);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.62);
    master.connect(context.destination);
    [260, 196].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = now + index * 0.16;
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.24, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
      oscillator.connect(gain).connect(master);
      oscillator.start(start);
      oscillator.stop(start + 0.34);
    });
  } catch {
    // Game feedback sound is optional and must not block correction.
  }
}

async function runPuzzleFailureFlow(sentenceText: string) {
  if (puzzleFailureTimerId) window.clearTimeout(puzzleFailureTimerId);
  puzzleFailureActive.value = false;
  await nextTick();
  puzzleFailureActive.value = true;
  playPuzzleFailureTone();
  puzzleFailureTimerId = window.setTimeout(() => {
    puzzleFailureActive.value = false;
  }, 1500);
  await handleSpeak(sentenceText, settings.value.answerPlayCount);
}

function playSuccessFanfare() {
  try {
    const context = unlockFeedbackAudioContext();
    if (!context || context.state !== "running") return;
    const now = context.currentTime;
    const master = context.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.2, now + 0.018);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 1.12);
    master.connect(context.destination);

    [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = now + index * 0.085;
      oscillator.type = index < 3 ? "triangle" : "sine";
      oscillator.frequency.setValueAtTime(frequency, start);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.012, start + 0.12);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25 - index * 0.025, start + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.42);
      oscillator.connect(gain).connect(master);
      oscillator.start(start);
      oscillator.stop(start + 0.46);
    });

    [1318.5, 1567.98].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = now + 0.42 + index * 0.05;
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
      oscillator.connect(gain).connect(master);
      oscillator.start(start);
      oscillator.stop(start + 0.58);
    });
  } catch {
    // Success audio is celebratory only; never block the learning flow.
  }
}

function advanceDictationCursor(force = false) {
  const lastIndex = Math.max(0, targetWords.value.length - 1);
  if (force || isDictationSlotCorrect(dictationCursor.value)) {
    selectDictationSlot(Math.min(lastIndex, dictationCursor.value + 1), 0);
  }
}

function isDictationAtLastWord() {
  return dictationCursor.value >= Math.max(0, targetWords.value.length - 1);
}

function hasCurrentDictationInput() {
  return Boolean(canonicalWord(dictationSlots.value[dictationCursor.value] || ""));
}

function resetEnterSubmitStreak() {
  enterSubmitStreak.value = 0;
  lastEnterSubmitAt.value = 0;
}

function handleEnterSubmitShortcut() {
  if (mode.value === "dictation" && dictationCompletionConfirmed.value) {
    nextSentence();
    return;
  }
  // Correcting a previously wrong word moves the caret back to that slot.
  // Completion must depend on the whole answer, not on the caret being in the
  // last slot, otherwise a fully corrected sentence cannot be submitted.
  if (mode.value === "dictation" && dictationAllCorrect.value) {
    finishDictationImmediately({ persist: true });
    return;
  }
  if (mode.value !== "dictation" && practiceCanAdvanceByShortcut()) {
    nextSentence();
    return;
  }
  const now = Date.now();
  enterSubmitStreak.value = now - lastEnterSubmitAt.value < 2200 ? enterSubmitStreak.value + 1 : 1;
  lastEnterSubmitAt.value = now;
  if (mode.value === "dictation" && !isDictationAtLastWord()) {
    handleSubmit();
    return;
  }
  if (enterSubmitStreak.value >= 3) {
    if (mode.value === "dictation") revealDictationAnswer();
    else {
      showAnswer.value = true;
      feedback.value = {
        correct: true,
        score: 100,
        feedback: "已连续提交 3 次，系统自动展开正确答案。"
      };
    }
    resetEnterSubmitStreak();
    void handleSpeak(currentSentence.value?.english || "");
    return;
  }
  void handleSubmit();
}

function handleTypingKeydown(event: KeyboardEvent) {
  if (event.isComposing) return;
  if (introDemoRunning.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  // Hidden input is intentionally focused throughout dictation. Handle the
  // answer shortcut here as well as in the window listener so Ctrl/Command+A
  // cannot fall through to the browser's select-all behaviour.
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
    event.preventDefault();
    event.stopPropagation();
    toggleAnswer();
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    event.stopPropagation();
    handleEnterSubmitShortcut();
    return;
  }
  resetEnterSubmitStreak();
  if (mode.value === "typing") {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();

    if (typingCompleted.value && (event.key === " " || event.key === "Spacebar")) {
      if (practiceCanAdvanceByShortcut()) nextSentence();
      else void handleSubmit();
      return;
    }

    if (event.key === "Backspace") {
      playKeyboardClick("key");
      typingErrorFlash.value = false;
      typingIndex.value = Math.max(0, typingIndex.value - 1);
      typedAnswer.value = typingChars.value.slice(0, typingIndex.value).join("");
      return;
    }

    if (typingCompleted.value) return;
    if (event.key.length !== 1 && event.key !== " " && event.key !== "Spacebar") return;

    const input = event.key === "Spacebar"
      ? " "
      : event.code === "Minus" || event.code === "NumpadSubtract"
        ? "-"
        : event.key;
    const target = activeTypingChar.value;
    if (typingCharMatches(input, target)) {
      typingErrorFlash.value = false;
      typingIndex.value = Math.min(typingChars.value.length, typingIndex.value + 1);
      typedAnswer.value = typingChars.value.slice(0, typingIndex.value).join("");
      playKeyboardClick(typingCompleted.value ? "advance" : "key");
      return;
    }

    playKeyboardClick("key");
    typingErrorFlash.value = true;
    window.setTimeout(() => {
      typingErrorFlash.value = false;
    }, 160);
    return;
  }
  if (mode.value !== "dictation") return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  const currentSlot = () => dictationSlots.value[dictationCursor.value] || "";
  if (["ArrowLeft", "ArrowRight", "Home", "End", "Delete"].includes(event.key)) {
    event.preventDefault();
    event.stopPropagation();
    dictationInputNotice.value = "";
    if (event.key === "Home") {
      dictationCharacterCursor.value = 0;
      return;
    }
    if (event.key === "End") {
      dictationCharacterCursor.value = currentSlot().length;
      return;
    }
    if (event.key === "ArrowLeft") {
      if (dictationCharacterCursor.value > 0) dictationCharacterCursor.value -= 1;
      else if (dictationCursor.value > 0) selectDictationSlot(dictationCursor.value - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      if (dictationCharacterCursor.value < currentSlot().length) dictationCharacterCursor.value += 1;
      else if (dictationCursor.value < targetWords.value.length - 1) selectDictationSlot(dictationCursor.value + 1, 0);
      return;
    }
    if (event.key === "Delete" && dictationCharacterCursor.value < currentSlot().length) {
      const value = currentSlot();
      setDictationSlot(dictationCursor.value, `${value.slice(0, dictationCharacterCursor.value)}${value.slice(dictationCharacterCursor.value + 1)}`);
      if (dictationSubmitted.value) feedback.value = buildDictationFeedback();
      playKeyboardClick("key");
    }
    return;
  }

  if (event.key === " " || event.key === "Spacebar") {
    event.preventDefault();
    event.stopPropagation();
    if (dictationCompletionConfirmed.value) {
      nextSentence();
      return;
    }
    if (!isDictationAtLastWord() && hasCurrentDictationInput()) {
      playKeyboardClick("advance");
      advanceDictationCursor(true);
    } else if (isDictationAtLastWord()) {
      finishDictationImmediately({ persist: dictationAllCorrect.value });
    } else {
      playKeyboardClick("key");
    }
    syncDictationAnswer();
    return;
  }

  if (event.key === "Backspace") {
    event.preventDefault();
    event.stopPropagation();
    playKeyboardClick("key");
    const current = dictationSlots.value[dictationCursor.value] || "";
    dictationInputNotice.value = "";
    if (current && dictationCharacterCursor.value > 0) {
      const nextCursor = dictationCharacterCursor.value - 1;
      setDictationSlot(dictationCursor.value, `${current.slice(0, nextCursor)}${current.slice(dictationCharacterCursor.value)}`);
      dictationCharacterCursor.value = nextCursor;
      if (dictationSubmitted.value) feedback.value = buildDictationFeedback();
      return;
    }
    if (dictationCursor.value > 0) {
      selectDictationSlot(dictationCursor.value - 1);
    }
    return;
  }

  if (event.key.length !== 1) return;
  event.preventDefault();
  event.stopPropagation();

  const typedCharCanonical = canonicalWord(event.key);
  if (!typedCharCanonical) {
    playKeyboardClick("key");
    return;
  }

  const current = dictationSlots.value[dictationCursor.value] || "";
  const targetWord = targetWords.value[dictationCursor.value] || "";
  const inputLimit = dictationWordInputLimit(targetWord);
  if (current.length >= inputLimit) {
    dictationInputNotice.value = `当前单词最多输入 ${inputLimit} 个字符，请检查拼写或按退格键修改。`;
    playKeyboardClick("key");
    return;
  }
  dictationInputNotice.value = "";
  const nextCursor = dictationCharacterCursor.value + event.key.length;
  setDictationSlot(
    dictationCursor.value,
    `${current.slice(0, dictationCharacterCursor.value)}${event.key}${current.slice(dictationCharacterCursor.value)}`
  );
  dictationCharacterCursor.value = nextCursor;
  const correct = isDictationSlotCorrect(dictationCursor.value);
  playKeyboardClick(correct ? "advance" : "key");
  if (dictationSubmitted.value) feedback.value = buildDictationFeedback();
}

function appendPuzzleWord(id: string) {
  if (selectedPuzzleIds.value.includes(id) || practiceSubmitInFlight.value) return;
  selectedPuzzleIds.value.push(id);
  syncPuzzleAnswer();
  if (selectedPuzzleIds.value.length === puzzleCandidates.value.length) {
    const sentenceId = currentSentence.value?.id;
    void nextTick(() => {
      if (mode.value !== "puzzle" || currentSentence.value?.id !== sentenceId) return;
      if (selectedPuzzleIds.value.length !== puzzleCandidates.value.length || practiceSubmitInFlight.value) return;
      void handleSubmit();
    });
  }
}

function undoPuzzleWord() {
  selectedPuzzleIds.value.pop();
  syncPuzzleAnswer();
}

function clearPuzzle() {
  selectedPuzzleIds.value = [];
  syncPuzzleAnswer();
}

function syncPuzzleAnswer() {
  typedAnswer.value = selectedPuzzleIds.value.map(wordForPuzzleId).join(" ");
}

function wordForPuzzleId(id: string) {
  return puzzleCandidates.value.find((item) => item.id === id)?.word || "";
}

function blankWidth(word: string) {
  return Math.max(72, Math.min(220, word.length * 24));
}

function isDictationTokenOverfilled(index: number, targetWord: string) {
  const typedWord = dictationSlots.value[index] || "";
  return typedWord.length >= dictationWordInputLimit(targetWord);
}

async function handleSubmit() {
  if (!currentSentence.value || practiceSubmitInFlight.value) return;
  if (mode.value === "speaking") {
    resumeNotice.value = "跟读模式暂不提供录音评测；可使用下方“朗读”听示范后自行跟读，或切换听写检验记忆。";
    return;
  }
  if (successFlowRunning.value) {
    if (practiceCanAdvanceByShortcut()) nextSentence();
    return;
  }
  if (mode.value === "typing" && !typingCompleted.value) {
    feedback.value = {
      correct: false,
      score: Math.round((typingIndex.value / Math.max(1, typingChars.value.length)) * 100),
      feedback: `还差 ${typingChars.value.length - typingIndex.value} 个字符，继续按键盘提示完成。`,
      wrongWords: []
    };
    registerIncorrectAttempt();
    return;
  }
  if (mode.value === "typing" && typingCompleted.value) {
    const answer = currentSentence.value.english;
    feedback.value = {
      correct: true,
      score: 100,
      feedback: "很好，句子拼写和语序都正确。",
      wrongWords: []
    };
    persistPracticeResultInBackground(answer, mode.value);
    void runSuccessFlow();
    return;
  }
  if (mode.value === "dictation") {
    if (isDictationAtLastWord()) {
      finishDictationImmediately({ persist: dictationAllCorrect.value });
    } else {
      feedback.value = {
        correct: false,
        score: Math.round((dictationCursor.value / Math.max(1, targetWords.value.length)) * 100),
        feedback: "请先按空格完成整句输入，最后一个单词后再按 Enter 或空格提交。"
      };
      registerIncorrectAttempt();
    }
    return;
  }
  if (mode.value === "puzzle") {
    const submittedSentenceId = currentSentence.value.id;
    const submittedAnswer = typedAnswer.value;
    const correct = canonicalSentence(submittedAnswer) === canonicalSentence(currentSentence.value.english);
    feedback.value = {
      correct,
      score: correct ? 100 : 60,
      feedback: correct ? "很好，单词顺序完全正确。" : "单词顺序还需要调整，听完正确句子再试一次。",
      wrongWords: correct ? [] : normalizeWords(currentSentence.value.english)
    };
    if (correct) {
      showAnswer.value = true;
      answerCompareView.value = "word";
      persistPracticeResultInBackground(submittedAnswer, "puzzle");
      void runSuccessFlow({ autoAdvance: settings.value.puzzleAutoNext });
    } else {
      registerIncorrectAttempt();
      persistPracticeResultInBackground(submittedAnswer, "puzzle");
      void runPuzzleFailureFlow(currentSentence.value.english);
    }
    if (currentSentence.value?.id !== submittedSentenceId) return;
    return;
  }
  queuePractice(currentSentence.value.english, mode.value);
  feedback.value = { correct: true, score: 0, feedback: "已完成本次阅读练习；建议切换听写，不看答案检验是否记住。", wrongWords: [] };
  await handleSpeak(currentSentence.value.english, settings.value.answerPlayCount);
}

function syncLearningStatsAfterPractice(activity?: LearnerActivityDashboard) {
  // 累计统计走独立请求，不能被课程目录、推荐或商业信息刷新阻塞。
  // 服务端提交响应直接携带同一时刻的最新快照；旧服务端兼容场景才补拉。
  if (activity) {
    learnerActivity.value = activity;
    if (currentUser.value) {
      localStorage.setItem(`huosu_learner_activity_${currentUser.value.id}`, JSON.stringify({
        userId: currentUser.value.id,
        value: activity
      }));
    }
  } else {
    void refreshLearnerActivity();
  }
  void refreshLearningState({ skipLearnerActivity: true });
}

let learningStateRefreshInFlight: Promise<void> | undefined;

async function refreshLearningState(options?: { skipAdaptiveHome?: boolean; skipLearnerActivity?: boolean }) {
  // 同一轮登录恢复、支付回调或练习提交只允许一个全局同步任务。后续触发
  // 直接复用结果，避免重复网络请求与状态互相覆盖。
  if (learningStateRefreshInFlight) return learningStateRefreshInFlight;
  const task = refreshLearningStateInternal(options);
  learningStateRefreshInFlight = task;
  try {
    await task;
  } finally {
    if (learningStateRefreshInFlight === task) learningStateRefreshInFlight = undefined;
  }
}

async function refreshLearningStateInternal(options?: { skipAdaptiveHome?: boolean; skipLearnerActivity?: boolean }) {
  const learnerActivityRefresh = options?.skipLearnerActivity ? Promise.resolve(true) : refreshLearnerActivity();
  // 学习目录、复习摘要和推荐彼此没有依赖，过去串行等待会把一次点击的
  // 感知时长叠加。关键学习数据并行加载，商业信息在后台完成即可。
  const bootstrapRequest = getBootstrap();
  const reviewRequest = getReviewSummary();
  const adaptiveRequest = options?.skipAdaptiveHome ? Promise.resolve() : refreshAdaptiveHome();
  const commerceRequest = refreshCommerceState().catch(() => undefined);
  const [nextBootstrap, nextReviewSummary] = await Promise.all([bootstrapRequest, reviewRequest]);
  // Cumulative counters are monotonic. A slower request must not overwrite a
  // newer answer response with an older snapshot.
  const previousStudy = bootstrap.value?.study;
  bootstrap.value = previousStudy ? {
    ...nextBootstrap,
    study: {
      ...nextBootstrap.study,
      totalMinutes: Math.max(previousStudy.totalMinutes || 0, nextBootstrap.study.totalMinutes || 0),
      completedSentences: Math.max(previousStudy.completedSentences || 0, nextBootstrap.study.completedSentences || 0),
      thousandHourProgress: Math.max(previousStudy.thousandHourProgress || 0, nextBootstrap.study.thousandHourProgress || 0)
    }
  } : nextBootstrap;
  reviewSummary.value = nextReviewSummary;
  await Promise.all([learnerActivityRefresh, adaptiveRequest]);
  try {
    openApiStatus.value = await getOpenApiStatus();
  } catch {
    openApiStatus.value = null;
  }
  // 权益卡片不影响本次练习或登录完成，放到后台同步，不能拖住按钮和输入框。
  void commerceRequest;
}

async function refreshAdaptiveHome() {
  try {
    adaptiveHome.value = await getAdaptiveLearningHome();
    if (currentUser.value) {
      localStorage.setItem(`huosu_adaptive_home_${currentUser.value.id}`, JSON.stringify({
        date: new Date().toLocaleDateString("en-CA"),
        value: adaptiveHome.value
      }));
    }
    if (currentUser.value && adaptiveHome.value.needsOnboarding) {
      stopActiveSpeech();
      onboardingOpen.value = true;
    }
  } catch {
    adaptiveHome.value = null;
  }
}

function restoreCachedAdaptiveHome() {
  if (!currentUser.value) return false;
  try {
    const raw = localStorage.getItem(`huosu_adaptive_home_${currentUser.value.id}`);
    if (!raw) return false;
    const cached = JSON.parse(raw) as { date?: string; value?: AdaptiveLearningHome };
    if (cached.date !== new Date().toLocaleDateString("en-CA") || !cached.value) return false;
    adaptiveHome.value = cached.value;
    return true;
  } catch {
    // 缓存只用于加速首屏，损坏时直接走实时接口。
    return false;
  }
}

function openDailyRecommendationIfAvailable() {
  if (dailyRecommendationChoiceCommitted.value || dailyRecommendationAction.value) return;
  if (!currentUser.value || !adaptiveHome.value || adaptiveHome.value.needsOnboarding) return;
  stopActiveSpeech();
  dailyRecommendationOpen.value = true;
}

function toggleWeakSkill(skill: LearningSkill) {
  const current = [...(profileForm.value.weakSkills || [])];
  profileForm.value.weakSkills = current.includes(skill) ? current.filter((item) => item !== skill) : [...current, skill];
}

function openGoalEditor() {
  const profile = adaptiveHome.value?.profile;
  if (profile) {
    profileForm.value = {
      currentLevel: profile.currentLevel,
      targetLevel: profile.targetLevel,
      goalType: profile.goalType,
      examDate: profile.examDate,
      dailyMinutes: profile.dailyMinutes,
      weakSkills: [...profile.weakSkills],
      preferredTopics: [...profile.preferredTopics]
    };
  }
  goalEditorOpen.value = true;
}

async function saveGlobalLearningGoal() {
  if (goalSaving.value) return;
  goalSaving.value = true;
  try {
    adaptiveHome.value = await saveLearnerProfile(profileForm.value);
    goalEditorOpen.value = false;
    adaptiveTrainingBatch.value = null;
    await refreshLearnerActivity();
  } finally {
    goalSaving.value = false;
  }
}

function enterPlacementAssessment() {
  stopActiveSpeech();
  onboardingNotice.value = "";
  onboardingStep.value = 2;
}

function openAdaptiveChoices() {
  stopActiveSpeech();
  adaptiveChoiceOpen.value = true;
}

async function completeOnboarding() {
  if (adaptiveBusy.value) return;
  adaptiveBusy.value = true;
  onboardingNotice.value = "正在根据自评选择学习起点，请稍候…";
  try {
    await saveLearnerProfile(profileForm.value);
    adaptiveHome.value = await submitPlacementAssessment(placementScores.value);
    const recommendation = adaptiveHome.value.recommendations?.find((item) => item.sentenceId);
    if (!recommendation) throw new Error("暂未找到匹配课程，请从课程分类中手动选择。");
    const started = await startAdaptivePlanItem(recommendation);
    if (!started) throw new Error("匹配课程暂时无法打开，请从课程分类中手动选择。");
    onboardingOpen.value = false;
    onboardingStep.value = 1;
    onboardingNotice.value = "";
  } catch (error) {
    onboardingNotice.value = error instanceof Error ? error.message : "课程匹配失败，请重试或手动选课。";
  } finally {
    adaptiveBusy.value = false;
  }
}

function openManualCourseSelection() {
  onboardingOpen.value = false;
  onboardingStep.value = 1;
  onboardingNotice.value = "";
  sidebarOpen.value = true;
}

function adaptiveSentenceText(sentenceId?: string) {
  if (!sentenceId) return "智能复习项目";
  const planned = adaptiveHome.value?.plan?.items.find((item) => item.sentenceId === sentenceId)?.displayText;
  if (planned) return planned;
  for (const course of bootstrap.value?.courses || []) for (const lesson of course.lessons) {
    const sentence = lesson.sentences.find((item) => item.id === sentenceId);
    if (sentence) return sentence.english;
  }
  return "智能复习项目";
}

function modeLabel(value: PracticeMode) {
  return modes.find((item) => item.key === value)?.label || "学习";
}

function normalizeAvailablePracticeMode(value: unknown, index = 0): PracticeMode {
  if (value === "translate") {
    const fallbackModes = currentIsSingleWord.value ? (["typing", "dictation"] as PracticeMode[]) : (["typing", "dictation", "puzzle"] as PracticeMode[]);
    return fallbackModes[index % fallbackModes.length];
  }
  if (value === "puzzle" && currentIsSingleWord.value) return "learning";
  return availableModes.value.some((item) => item.key === value) ? value as PracticeMode : "learning";
}

function formatAdaptiveEventTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "刚刚" : date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

function formatAdaptiveBatchTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间未知";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date).replace("/", "-");
}

function formatAdaptiveLibraryTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "历史生成";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date).replaceAll("/", "-");
}

function adaptiveTrainingPackDisplayName(entry: PersonalAiTrainingPack) {
  const topic = entry.pack.focusWords.slice(0, 3).join("、")
    || entry.pack.title.replace(/^目标专项连续训练(?:\s*·\s*第\s*\d+\s*组)?$/, "")
    || "目标专项";
  const packLabel = entry.batch.packs.length > 1 ? ` · ${entry.packIndex + 1}组` : "";
  return `AI训练 · ${topic} · ${formatAdaptiveLibraryTime(entry.batch.createdAt)}${packLabel}`;
}

function adaptiveTrainingPackEntry(pack: AdaptiveTrainingPack) {
  for (const batch of adaptiveTrainingBatches.value) {
    const packIndex = batch.packs.findIndex((item) => item.id === pack.id);
    if (packIndex >= 0) return { pack, batch, packIndex } satisfies PersonalAiTrainingPack;
  }
  return null;
}

function stopAdaptiveTrainingPolling() {
  if (adaptiveTrainingPollTimer) window.clearTimeout(adaptiveTrainingPollTimer);
  adaptiveTrainingPollTimer = undefined;
}

function upsertAdaptiveTrainingBatch(batch: AdaptiveTrainingBatch) {
  adaptiveTrainingBatches.value = [
    batch,
    ...adaptiveTrainingBatches.value.filter((item) => item.id !== batch.id)
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

async function pollAdaptiveTrainingBatch(batchId: string) {
  stopAdaptiveTrainingPolling();
  const userId = currentUser.value?.id;
  if (!userId) return;
  try {
    const batch = await getAdaptiveTrainingPackBatch(batchId);
    if (currentUser.value?.id !== userId) return;
    adaptiveTrainingPollError.value = 0;
    adaptiveTrainingBatch.value = batch;
    upsertAdaptiveTrainingBatch(batch);
    adaptiveTrainingPackBusy.value = ["queued", "running"].includes(batch.status);
    if (batch.status === "completed" && batch.id === recentGeneratedBatchId.value) {
      void nextTick(() => adaptivePackListRef.value?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
    if (["completed", "failed", "blocked"].includes(batch.status)) {
      void getAdaptiveTrainingGenerationPricing().then((pricing) => { adaptiveGenerationPricing.value = pricing; });
    }
    if (adaptiveTrainingPackBusy.value) {
      adaptiveTrainingPollTimer = window.setTimeout(() => void pollAdaptiveTrainingBatch(batchId), 1200);
    }
  } catch {
    if (currentUser.value?.id !== userId) return;
    adaptiveTrainingPollError.value += 1;
    adaptiveTrainingPackBusy.value = true;
    adaptiveTrainingPollTimer = window.setTimeout(() => void pollAdaptiveTrainingBatch(batchId), Math.min(5000, 1000 + adaptiveTrainingPollError.value * 500));
  }
}

async function loadLatestAdaptiveTrainingBatch() {
  if (!currentUser.value) return;
  const userId = currentUser.value.id;
  try {
    const [batch, batches, pricing] = await Promise.all([
      getLatestAdaptiveTrainingPackBatch(),
      getAdaptiveTrainingPackBatches(),
      getAdaptiveTrainingGenerationPricing()
    ]);
    if (currentUser.value?.id !== userId) return;
    adaptiveGenerationPricing.value = pricing;
    if (!pricing.options.some((option) => option.contentCount === adaptivePackCount.value)) {
      adaptivePackCount.value = pricing.options[0]?.contentCount || 60;
    }
    adaptiveTrainingBatches.value = batches;
    aiHistoryOffset = batches.length;
    aiHistoryHasMore.value = batches.length === 50;
    adaptiveTrainingBatch.value = batch;
    if (batch && ["queued", "running"].includes(batch.status)) {
      adaptiveTrainingPackBusy.value = true;
      void pollAdaptiveTrainingBatch(batch.id);
    }
  } catch {
    if (currentUser.value?.id !== userId) return;
    aiHistoryError.value = "训练包读取失败，可重新打开个人库重试。";
    adaptiveTrainingBatch.value = null;
  }
}

async function initializeDailyRecommendation() {
  if (!currentUser.value || adaptiveHome.value?.needsOnboarding) return;
  try {
    const daily = await openDailyAdaptiveTraining();
    if (daily.batch) {
      adaptiveTrainingBatch.value = daily.batch;
      upsertAdaptiveTrainingBatch(daily.batch);
      adaptiveTrainingPackBusy.value = ["queued", "running"].includes(daily.batch.status);
      if (adaptiveTrainingPackBusy.value) void pollAdaptiveTrainingBatch(daily.batch.id);
    }
    if (daily.plan) {
      if (adaptiveHome.value) {
        adaptiveHome.value = {
          ...adaptiveHome.value,
          plan: daily.plan,
          recommendations: daily.plan.items.filter((item) => item.status === "pending" && item.sentenceId).slice(0, 3)
        };
      }
    }
    return daily;
  } catch (error) {
    await loadLatestAdaptiveTrainingBatch();
    throw error;
  }
}

function updateDailyRecommendationPreparation(percent: number, label: string) {
  dailyRecommendationPreparationPercent.value = Math.max(dailyRecommendationPreparationPercent.value, Math.min(100, percent));
  dailyRecommendationPreparationLabel.value = label;
}

async function continuePreviousLearning() {
  if (!dailyRecommendationReady.value) return;
  unlockAudioFromUserGesture();
  dailyRecommendationChoiceCommitted.value = true;
  dailyRecommendationAction.value = "previous";
  dailyRecommendationOpen.value = false;
  dailyRecommendationError.value = "";
  dailyRecommendationPreparing.value = true;
  dailyRecommendationPreparationPercent.value = 15;
  dailyRecommendationPreparationLabel.value = "正在读取上次学习进度";
  try {
    const preferredMode = readPreferredPracticeMode(currentUser.value?.id);
    updateDailyRecommendationPreparation(35, "正在恢复上次学习位置");
    let restored = await restoreLearningCheckpoint(preferredMode);
    if (!restored && currentUser.value) {
      updateDailyRecommendationPreparation(45, "正在同步最近学习记录");
      await refreshLearnerActivity();
      restored = await restoreLearningCheckpoint(preferredMode);
    }
    if (!restored) {
      const starterCourse = pickStarterCourse();
      if (!starterCourse?.lessons[0]) throw new Error("暂无可继续的学习记录，请先选择课包。");
      await loadLesson(starterCourse.lessons[0].id, { mode: preferredMode });
    }
    updateDailyRecommendationPreparation(100, "之前学习已加载完成");
    dailyRecommendationOpen.value = false;
    focusTyping();
    await playCurrentSentenceAutomatically(sentenceResetSerial);
  } catch (error) {
    dailyRecommendationChoiceCommitted.value = false;
    dailyRecommendationError.value = error instanceof Error ? error.message : "之前学习加载失败，请重试。";
    dailyRecommendationOpen.value = true;
  } finally {
    dailyRecommendationPreparing.value = false;
    dailyRecommendationAction.value = null;
  }
}

async function useTodayRecommendation() {
  if (!dailyRecommendationReady.value) return;
  unlockAudioFromUserGesture();
  dailyRecommendationChoiceCommitted.value = true;
  dailyRecommendationAction.value = "today";
  dailyRecommendationOpen.value = false;
  dailyRecommendationError.value = "";
  dailyRecommendationPreparing.value = true;
  dailyRecommendationPreparationPercent.value = 15;
  dailyRecommendationPreparationLabel.value = "正在读取今日推荐任务";
  try {
    const daily = await initializeDailyRecommendation();
    updateDailyRecommendationPreparation(55, "正在加载今日学习包");
    const tryPlanItems = async (items: AdaptivePlanItem[]) => {
      for (const item of items) {
        if (item.status === "pending" && item.sentenceId && await startAdaptivePlanItem(item)) return true;
      }
      return false;
    };
    let opened = await tryPlanItems(daily?.plan?.items || adaptiveHome.value?.recommendations || []);
    if (!opened) {
      updateDailyRecommendationPreparation(70, "推荐内容已更新，正在重新匹配课节");
      const rebuiltPlan = await regenerateAdaptivePlan();
      if (adaptiveHome.value) {
        adaptiveHome.value = {
          ...adaptiveHome.value,
          plan: rebuiltPlan,
          recommendations: rebuiltPlan.items.filter((item) => item.status === "pending" && item.sentenceId).slice(0, 3)
        };
      }
      opened = await tryPlanItems(rebuiltPlan.items);
    }
    if (!opened) throw new Error("今日推荐内容暂时没有可用课节，请稍后重试或选择其他课包。");
    updateDailyRecommendationPreparation(100, "今日推荐任务已加载完成");
    dailyRecommendationOpen.value = false;
  } catch (error) {
    dailyRecommendationChoiceCommitted.value = false;
    dailyRecommendationError.value = error instanceof Error ? error.message : "今日推荐任务加载失败，请重试。";
    dailyRecommendationOpen.value = true;
  } finally {
    dailyRecommendationPreparing.value = false;
    dailyRecommendationAction.value = null;
  }
}

async function generateAdaptiveTrainingPacks() {
  if (!currentUser.value || adaptiveTrainingPackBusy.value) return;
  if (adaptiveCreditInsufficient.value) return;
  const option = selectedAdaptiveGenerationOption.value;
  if (!option) return;
  if (option.contentCount >= 500 && !window.confirm(`将生成约 ${option.contentCount} 条内容并消耗 ${option.creditCost} AI积分，任务会分批执行且可以刷新页面继续。确认开始吗？`)) return;
  adaptiveTrainingPackBusy.value = true;
  try {
    const batch = await startAdaptiveTrainingPackGeneration(adaptivePackCount.value);
    recentGeneratedBatchId.value = batch.id;
    adaptiveTrainingBatch.value = batch;
    if (adaptiveGenerationPricing.value) adaptiveGenerationPricing.value.wallet.balance = Math.max(0, adaptiveGenerationPricing.value.wallet.balance - (batch.creditCost || 0));
    upsertAdaptiveTrainingBatch(batch);
    void pollAdaptiveTrainingBatch(batch.id);
  } catch (error) {
    adaptiveTrainingPackBusy.value = false;
    adaptiveTrainingBatch.value = {
      id: "adaptive-pack-client-error", userId: currentUser.value.id, status: "failed", stage: "profile",
      totalItems: adaptivePackCount.value, requestedContentCount: adaptivePackCount.value, processedItems: 0, completedItems: 0, failedItems: adaptivePackCount.value, percent: 0, packs: [], events: [],
      error: error instanceof Error ? error.message : "训练包生成失败", nextAction: "稍后重新生成",
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    };
  }
}

function adaptivePackProgress(packId: string) {
  return learnerActivity.value?.learningHistory.packages.find((item) => item.courseId === `adaptive-pack:${packId}`);
}

function toggleSmartPlanDetail(detail: "history") {
  smartPlanDetail.value = smartPlanDetail.value === detail ? null : detail;
}

function selectAccountLibrary(view: "ai_packs" | "mistakes" | "unknown_words" | "personal_words") {
  accountLibraryVisibleCount.value = 12;
  accountLibraryView.value = view;
  if (view === "ai_packs" && currentUser.value && !adaptiveTrainingBatches.value.length) void loadLatestAdaptiveTrainingBatch();
}

function isMemoryDue(value: string) {
  return Date.parse(value) <= Date.now();
}

function memoryDueLabel(value: string) {
  if (isMemoryDue(value)) return "待复习";
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return "今天稍后";
  return `${date.getMonth() + 1}月${date.getDate()}日复习`;
}

function learningSkillLabel(skill: string) {
  return ({ spelling: "拼写", listening: "听辨", speaking: "口语", vocabulary: "词汇", reading: "阅读", writing: "表达" } as Record<string, string>)[skill] || "综合练习";
}

async function startGeneratedTrainingPack(pack: AdaptiveTrainingPack, options?: { sentenceId?: string; mode?: PracticeMode }) {
  const inheritedAutoReadSuppression = suppressAutoRead;
  suppressAutoRead = true;
  stopActiveSpeech();
  const libraryEntry = adaptiveTrainingPackEntry(pack);
  const normalizedPack: AdaptiveTrainingPack = libraryEntry
    ? { ...pack, title: adaptiveTrainingPackDisplayName(libraryEntry) }
    : pack;
  activeGeneratedPack.value = normalizedPack;
  const lesson: Lesson = {
    id: `generated-lesson-${normalizedPack.id}`,
    title: normalizedPack.title,
    description: normalizedPack.description,
    sentenceCount: normalizedPack.items.length,
    isFreeTrial: false,
    sentences: normalizedPack.items.map((item) => ({
      id: item.id,
      english: item.english,
      chinese: item.chinese,
      phonetic: item.phonetic,
      grammarTags: [item.angle, item.reason],
      structure: item.structure || [],
      annotation: item.annotation,
      keywords: [item.focusWord],
      sourceMeta: { adaptiveTrainingPackId: normalizedPack.id, itemType: item.itemType }
    }))
  };
  currentCourse.value = {
    id: `generated-course-${normalizedPack.id}`,
    title: "AI 动态训练包",
    subtitle: normalizedPack.description,
    category: "AI 个性化",
    audience: "当前用户",
    level: ["B2", "C1"].includes(adaptiveHome.value?.profile.currentLevel || "") ? "高级" : ["B1"].includes(adaptiveHome.value?.profile.currentLevel || "") ? "中级" : "初级",
    joinedCount: 1,
    isMemberOnly: false,
    cover: "",
    tags: normalizedPack.focusWords,
    lessons: [lesson]
  };
  currentLesson.value = lesson;
  const restoredIndex = options?.sentenceId ? normalizedPack.items.findIndex((item) => item.id === options.sentenceId) : -1;
  currentIndex.value = restoredIndex >= 0 ? restoredIndex : 0;
  if (options?.mode) mode.value = options.mode;
  adaptiveTrainingActive.value = false;
  smartPlanOpen.value = false;
  adaptiveChoiceOpen.value = false;
  resetForCurrentSentence();
  try {
    await prepareLessonAudioBatch(lesson, currentIndex.value);
  } finally {
    suppressAutoRead = inheritedAutoReadSuppression;
  }
  if (!inheritedAutoReadSuppression) void playCurrentSentenceAutomatically(sentenceResetSerial);
}

async function openPersonalAiTrainingPack(entry: PersonalAiTrainingPack) {
  const courseId = `adaptive-pack:${entry.pack.id}`;
  const history = learnerActivity.value?.learningHistory.packages.find((item) => item.courseId === courseId);
  if (history) {
    await continueHistoryPackage(history);
    return;
  }
  if (historyContinueBusyId.value) return;
  historyContinueBusyId.value = courseId;
  unlockSpeechAudioContext();
  try {
    await startGeneratedTrainingPack(entry.pack);
    accountPanelOpen.value = false;
    resumeNotice.value = `开始学习 · ${adaptiveTrainingPackDisplayName(entry)}`;
  } catch (error) {
    resumeNotice.value = error instanceof Error ? error.message : "暂时无法打开该 AI 训练包，请稍后重试";
  } finally {
    historyContinueBusyId.value = "";
  }
}

async function restoreLearningCheckpoint(preferredMode?: PracticeMode) {
  const checkpoint = learnerActivity.value?.learningResume;
  if (!currentUser.value || !checkpoint) return false;
  stopActiveSpeech();
  suppressAutoRead = true;
  try {
    if (checkpoint.sourceType === "adaptive" && checkpoint.adaptivePackId) {
      if (!adaptiveTrainingBatches.value.length) await loadLatestAdaptiveTrainingBatch();
      const pack = adaptiveTrainingBatches.value.flatMap((batch) => batch.packs).find((item) => item.id === checkpoint.adaptivePackId) || await getSavedTrainingPack(checkpoint.adaptivePackId!);
      if (!pack) return false;
      await startGeneratedTrainingPack(pack, { sentenceId: checkpoint.sentenceId, mode: preferredMode || checkpoint.mode });
    } else {
      await loadLesson(checkpoint.lessonId, { sentenceId: checkpoint.sentenceId, mode: preferredMode || checkpoint.mode });
    }
    const action = checkpoint.reason === "retry" ? "继续巩固上次未掌握的句子" : checkpoint.completedPreviousPackage ? "上一包已完成，已进入下一项学习" : "已恢复到上次学习进度";
    resumeNotice.value = `${action} · ${checkpoint.courseTitle} · 第 ${checkpoint.sentenceIndex + 1} 句`;
    return true;
  } catch {
    return false;
  } finally {
    suppressAutoRead = false;
  }
}

async function continueHistoryPackage(item: LearnerActivityDashboard["learningHistory"]["packages"][number]) {
  if (historyContinueBusyId.value) return;
  historyContinueBusyId.value = item.courseId;
  // Keep the browser's audio permission attached to the user's click before
  // course and OSS audio preparation cross asynchronous boundaries.
  unlockSpeechAudioContext();
  stopActiveSpeech();
  suppressAutoRead = true;
  let loaded = false;
  try {
    const records = (learnerActivity.value?.learningHistory.recent || [])
      .filter((record) => record.courseId === item.courseId)
      .sort((a, b) => b.studiedAt.localeCompare(a.studiedAt));
    const latest = records[0];

    if (item.courseId.startsWith("adaptive-pack:")) {
      if (!adaptiveTrainingBatches.value.length) await loadLatestAdaptiveTrainingBatch();
      const packId = item.courseId.slice("adaptive-pack:".length);
      const pack = adaptiveTrainingBatches.value.flatMap((batch) => batch.packs).find((entry) => entry.id === packId) || await getSavedTrainingPack(packId);
      if (!pack) throw new Error("未找到该 AI 学习包");
      const latestIndex = latest ? pack.items.findIndex((entry) => entry.id === latest.sentenceId) : -1;
      const nextIndex = item.progressPercent >= 100 ? 0 : Math.min(Math.max(0, latestIndex + 1), Math.max(0, pack.items.length - 1));
      await startGeneratedTrainingPack(pack, { sentenceId: pack.items[nextIndex]?.id, mode: latest?.mode as PracticeMode | undefined });
    } else {
      const course = bootstrap.value?.courses.find((entry) => entry.id === item.courseId);
      if (!course?.lessons.length) throw new Error("未找到该学习包");
      let lesson = latest ? course.lessons.find((entry) => entry.id === latest.lessonId) : course.lessons[0];
      let sentenceId = latest?.sentenceId;
      if (item.progressPercent >= 100) {
        lesson = course.lessons[0];
        sentenceId = lesson.sentences[0]?.id;
      } else if (lesson && latest) {
        if (!lesson.sentences.length) {
          const detail = await getPracticeLesson(lesson.id);
          lesson = detail.lesson;
          cachePracticeLesson(detail.course, detail.lesson);
        }
        const sentenceIndex = lesson.sentences.findIndex((entry) => entry.id === latest.sentenceId);
        if (sentenceIndex >= 0 && sentenceIndex < lesson.sentences.length - 1) {
          sentenceId = lesson.sentences[sentenceIndex + 1]?.id;
        } else {
          const lessonIndex = course.lessons.findIndex((entry) => entry.id === lesson?.id);
          lesson = course.lessons[Math.min(lessonIndex + 1, course.lessons.length - 1)] || lesson;
          sentenceId = lesson.sentences[0]?.id;
        }
      }
      await loadLesson(lesson?.id || course.lessons[0].id, { sentenceId, mode: latest?.mode as PracticeMode | undefined });
    }
    accountPanelOpen.value = false;
    resumeNotice.value = `${item.progressPercent >= 100 ? "开始复习" : "继续学习"} · ${item.courseTitle}`;
    loaded = true;
  } catch (error) {
    resumeNotice.value = error instanceof Error ? error.message : "暂时无法打开该学习包，请稍后重试";
  } finally {
    suppressAutoRead = false;
    historyContinueBusyId.value = "";
  }
  if (loaded) await playCurrentSentenceAutomatically(sentenceResetSerial);
}

async function startAdaptivePlanItem(item: AdaptivePlanItem) {
  if (!item.sentenceId) return false;
  activeGeneratedPack.value = null;
  for (const course of bootstrap.value?.courses || []) for (const lesson of course.lessons) {
    if (!lesson.sentences.some((sentence) => sentence.id === item.sentenceId)) continue;
    await loadLesson(lesson.id, { sentenceId: item.sentenceId, mode: normalizeAvailablePracticeMode(item.mode) });
    smartPlanOpen.value = false;
    adaptiveChoiceOpen.value = false;
    adaptiveTrainingActive.value = true;
    return true;
  }
  try {
    const location = await getPracticeLocationBySentenceId(item.sentenceId);
    await loadLesson(location.lessonId, { sentenceId: item.sentenceId, mode: normalizeAvailablePracticeMode(item.mode) });
    smartPlanOpen.value = false;
    adaptiveChoiceOpen.value = false;
    adaptiveTrainingActive.value = true;
    return true;
  } catch {
    return false;
  }
}

async function chooseAdaptiveRecommendation(item: AdaptivePlanItem) {
  await startAdaptivePlanItem(item);
}

async function refreshLearnerActivity(throwOnError = false) {
  try {
    learnerActivity.value = await getLearnerActivity();
    if (currentUser.value) {
      localStorage.setItem(`huosu_learner_activity_${currentUser.value.id}`, JSON.stringify({
        userId: currentUser.value.id,
        value: learnerActivity.value
      }));
    }
    return true;
  } catch (error) {
    // Keep the last valid in-memory/cached snapshot on transient failures.
    // Clearing it made learned sentence counts flash to zero until the next
    // successful answer submission returned a fresh activity payload.
    if (throwOnError) throw error;
    return false;
  }
}

function restoreCachedLearnerActivity() {
  if (!currentUser.value) return false;
  try {
    const raw = localStorage.getItem(`huosu_learner_activity_${currentUser.value.id}`);
    if (!raw) return false;
    const cached = JSON.parse(raw) as { userId?: string; value?: LearnerActivityDashboard };
    if (cached.userId !== currentUser.value.id || !cached.value) return false;
    learnerActivity.value = cached.value;
    return true;
  } catch {
    return false;
  }
}

async function handleLearnerCheckin() {
  learnerActivityBusy.value = "checkin";
  try {
    const result = await learnerCheckin();
    learnerActivity.value = result.activity;
  } finally {
    learnerActivityBusy.value = "";
  }
}

async function handleAskAiAssistant() {
  if (!currentSentence.value || !aiQuestion.value.trim()) return;
  learnerActivityBusy.value = "ai";
  try {
    aiReply.value = await askAiAssistant({
      sentenceId: currentSentence.value.id,
      question: aiQuestion.value.trim()
    });
  } finally {
    learnerActivityBusy.value = "";
  }
}

async function startFollowClip(clip: { sentenceIds: string[] }) {
  const sentenceId = clip.sentenceIds[0];
  if (!sentenceId) return;
  try {
    const location = await getPracticeLocationBySentenceId(sentenceId);
    await loadLesson(location.lessonId, { sentenceId, mode: "speaking" });
    accountPanelOpen.value = false;
    focusTyping();
  } catch { resumeNotice.value = "跟读内容暂不可用，请稍后重试。"; }
}

async function refreshCommerceState() {
  try {
    commerce.value = await getCommerceMe();
    affiliatePayoutForm.value = {
      alipayAccount: commerce.value.affiliate.alipayAccount || affiliatePayoutForm.value.alipayAccount,
      alipayRealName: commerce.value.affiliate.alipayRealName || affiliatePayoutForm.value.alipayRealName
    };
    if (inviteCodeInput.value === commerce.value.affiliate.inviteCode) inviteCodeInput.value = "";
  } catch {
    commerce.value = null;
  }
}

async function handleSendCode() {
  if (!phoneValid.value) {
    authNotice.value = "请输入正确的 11 位中国大陆手机号。";
    return;
  }
  if (!await recognizeLoginPhone()) return;
  if (loginNeedsNickname.value && !nicknameValid.value) {
    authNotice.value = "新用户请输入2-30个字的昵称或姓名。";
    return;
  }
  authBusy.value = "send";
  authNotice.value = "";
  try {
    const payload = await sendCode(normalizedLoginPhone.value);
    loginPhone.value = payload.phone || normalizedLoginPhone.value;
    if (payload.devCode) loginCode.value = payload.devCode;
    authNotice.value = payload.message || "验证码已发送。";
    startSmsCountdown(payload.waitSeconds || 60);
  } catch (error) {
    authNotice.value = error instanceof Error ? error.message : "验证码发送失败，请稍后再试。";
  } finally {
    authBusy.value = "";
  }
}

async function handleLogin() {
  const code = loginCode.value.trim();
  if (!phoneValid.value) {
    authNotice.value = "请输入正确的 11 位中国大陆手机号。";
    return;
  }
  if (!await recognizeLoginPhone()) return;
  if (loginNeedsNickname.value && !nicknameValid.value) {
    authNotice.value = "新用户请输入2-30个字的昵称或姓名。";
    return;
  }
  if (!code) {
    authNotice.value = "请输入短信验证码。";
    return;
  }
  authBusy.value = "login";
  authNotice.value = "";
  try {
    const result = await loginByPhone(normalizedLoginPhone.value, code, loginNeedsNickname.value ? loginNickname.value : undefined);
    if (!result.success) {
      authNotice.value = result.error || "登录失败，请重新获取验证码。";
      return;
    }
    currentUser.value = result.data.user;
    cacheAuthenticatedUser(result.data.user);
    restorePreferredPracticeMode();
    clearGuestTrialState();
    trialGateOpen.value = false;
    accountPanelOpen.value = false;
    authNotice.value = result.data.isNewUser ? "已为你自动注册并登录。" : "登录成功，学习进度已同步。";
    await refreshLearningState();
    dailyRecommendationChoiceCommitted.value = false;
    dailyRecommendationBaseReady.value = true;
    dailyRecommendationPreparing.value = false;
    dailyRecommendationAction.value = null;
    dailyRecommendationError.value = "";
    openDailyRecommendationIfAvailable();
    await nextTick();
    focusTyping();
  } catch (error) {
    authNotice.value = error instanceof Error ? error.message : "登录失败，请稍后再试。";
  } finally {
    authBusy.value = "";
  }
}

async function handleLoginFromTrialGate() {
  await handleLogin();
  if (!currentUser.value) return;
  trialGateOpen.value = false;
  accountPanelOpen.value = false;
}

async function handleLogout() {
  try {
    await logout();
  } catch {
    // The user must still be able to leave the authenticated UI when offline.
  } finally {
    stopActiveSpeech();
    cancelSuccessFlow();
    currentUser.value = null;
    cacheAuthenticatedUser(null);
    localStorage.removeItem(PRACTICE_LESSON_CACHE_KEY);
    localStorage.removeItem(PRACTICE_POSITION_CACHE_KEY);
    commerce.value = null;
    guestTrialState.value = loadGuestTrialState();
    restorePreferredPracticeMode();
    trialGateOpen.value = false;
    dailyRecommendationOpen.value = false;
    onboardingOpen.value = false;
    adaptiveChoiceOpen.value = false;
    closeAllTransientPanels();
    authNotice.value = "";
    landingVisible.value = true;
    window.scrollTo({ top: 0, behavior: "auto" });
  }
}

function startSmsCountdown(waitSeconds = 60) {
  if (smsCountdownTimerId) window.clearInterval(smsCountdownTimerId);
  smsCountdown.value = Math.max(0, Math.floor(waitSeconds));
  if (!smsCountdown.value) return;
  smsCountdownTimerId = window.setInterval(() => {
    smsCountdown.value = Math.max(0, smsCountdown.value - 1);
    if (smsCountdown.value <= 0 && smsCountdownTimerId) {
      window.clearInterval(smsCountdownTimerId);
      smsCountdownTimerId = undefined;
    }
  }, 1000);
}

async function handleClaimCoupon(templateCode: string) {
  commerceBusy.value = "coupon";
  try {
    await claimCoupon(templateCode);
    await refreshCommerceState();
  } finally {
    commerceBusy.value = "";
  }
}

async function handleAiCreditRecharge(packageKey: string) {
  commerceBusy.value = `ai-credit-${packageKey}`;
  wechatPayPreparing.value = "正在创建积分订单，请稍候…";
  commerceNotice.value = "";
  try {
    const order = await createAiCreditRechargeOrder(packageKey);
    wechatPayPreparing.value = "订单已创建，正在获取支付二维码…";
    const paymentOrder = await payAiCreditRechargeOrder(order.orderNo);
    commerceNotice.value = `请扫码支付 ¥${order.payAmount}，支付成功后积分自动到账。`;
    await openWechatPayment(paymentOrder);
  } catch (error) {
    commerceNotice.value = error instanceof Error ? error.message : "AI积分充值失败，请稍后再试。";
  } finally {
    wechatPayPreparing.value = "";
    commerceBusy.value = "";
  }
}

async function handleBuyPlan(planKey: MembershipPlanKey) {
  commerceNotice.value = "";
  if (activePaidMembership.value) {
    commerceNotice.value = `${membershipExpiryLabel.value}，有效期内无需重复购买。`;
    return;
  }
  commerceBusy.value = planKey;
  wechatPayPreparing.value = "正在创建会员订单，请稍候…";
  try {
    const usableCoupon = commerce.value?.coupons?.find((coupon) => coupon.status === "unused");
    const order = await createMembershipOrder({
      planKey,
      couponCode: usableCoupon?.couponCode,
      inviteCode: inviteCodeInput.value.trim() || undefined
    });
    commerceNotice.value = order.status === "PENDING_PAY" ? `已创建或复用待支付订单 ${order.orderNo}。` : `订单 ${order.orderNo} 已完成。`;
    if (order.status === "PENDING_PAY") {
      wechatPayPreparing.value = "订单已创建，正在获取支付二维码…";
      const paymentOrder = await payMembershipOrder(order.orderNo, "wechat");
      await openWechatPayment(paymentOrder);
      commerceNotice.value = "请使用微信扫描二维码完成支付。";
      return;
    }
    await refreshLearningState();
  } catch (error) {
    commerceNotice.value = error instanceof Error ? error.message : "购买失败，请稍后再试。";
    await refreshCommerceState();
  } finally {
    wechatPayPreparing.value = "";
    commerceBusy.value = "";
  }
}

function stopWechatPayPolling() {
  if (wechatPayPollTimerId) window.clearInterval(wechatPayPollTimerId);
  wechatPayPollTimerId = undefined;
}

async function openWechatPayment(order: OrderRecord) {
  if (!order.wechatCodeUrl) throw new Error("微信支付二维码创建失败，请稍后重试。");
  onboardingOpen.value = false;
  smartPlanOpen.value = false;
  goalEditorOpen.value = false;
  aiCreditRechargeOpen.value = false;
  adaptiveChoiceOpen.value = false;
  dailyRecommendationOpen.value = false;
  desktopDownloadOpen.value = false;
  sidebarOpen.value = false;
  settingsOpen.value = false;
  accountPanelOpen.value = false;
  affiliateIntroOpen.value = false;
  modeMenuOpen.value = false;
  shortcutModalOpen.value = false;
  moreSettingsOpen.value = false;
  personalModalOpen.value = false;
  trialGateOpen.value = false;
  courseCompletionOpen.value = false;
  visibilityResumeOpen.value = false;
  const qrDataUrl = await QRCode.toDataURL(order.wechatCodeUrl, {
    width: 200,
    margin: 1,
    errorCorrectionLevel: "M"
  });
  wechatPayModal.value = {
    orderNo: order.orderNo,
    planName: order.planName,
    amount: order.payAmount,
    qrDataUrl,
    statusText: "等待微信支付结果…",
    refreshing: false,
    productType: order.productType
  };
  stopWechatPayPolling();
  wechatPayPollTimerId = window.setInterval(() => void refreshWechatPaymentStatus(false), 2500);
}

async function refreshWechatPaymentStatus(showBusy = true) {
  const modal = wechatPayModal.value;
  if (!modal || modal.refreshing) return;
  if (showBusy) modal.refreshing = true;
  try {
    const order = await getMembershipOrder(modal.orderNo);
    if (order.status === "PAID") {
      stopWechatPayPolling();
      commerceNotice.value = modal.productType === "ai_credit" ? "支付成功，积分已自动到账。" : "支付成功，会员权益已生效。";
      wechatPayModal.value = null;
      await refreshLearningState();
      return;
    }
    if (order.status === "CLOSED") {
      stopWechatPayPolling();
      modal.statusText = "订单已关闭，请重新发起购买。";
      return;
    }
    modal.statusText = showBusy ? "尚未查询到支付结果，请完成支付后重试。" : "等待微信支付结果…";
  } catch (error) {
    modal.statusText = error instanceof Error ? error.message : "支付状态查询失败，请稍后重试。";
  } finally {
    if (wechatPayModal.value?.orderNo === modal.orderNo) modal.refreshing = false;
  }
}

async function cancelWechatPayment() {
  const modal = wechatPayModal.value;
  if (!modal || modal.refreshing) return;
  modal.refreshing = true;
  stopWechatPayPolling();
  try {
    if (modal.productType === "ai_credit") await closeAiCreditRechargeOrder(modal.orderNo);
    else await closeMembershipOrder(modal.orderNo);
    commerceNotice.value = "支付订单已关闭。";
    wechatPayModal.value = null;
    await refreshCommerceState();
  } catch (error) {
    modal.statusText = error instanceof Error ? error.message : "订单关闭失败，请稍后重试。";
    modal.refreshing = false;
  }
}

async function copyAffiliateInvite() {
  const inviteCode = commerce.value?.affiliate?.inviteCode;
  if (!inviteCode) {
    commerceNotice.value = "当前还没有可复制的邀请码。";
    return;
  }
  try {
    await navigator.clipboard.writeText(inviteCode);
    commerceNotice.value = `邀请码 ${inviteCode} 已复制。`;
  } catch {
    commerceNotice.value = `邀请码：${inviteCode}`;
  }
}

async function handleSavePayout() {
  commerceBusy.value = "payout";
  try {
    await updateAffiliatePayout(affiliatePayoutForm.value);
    await refreshCommerceState();
  } finally {
    commerceBusy.value = "";
  }
}

async function handleRequestWithdrawal() {
  commerceBusy.value = "withdraw";
  try {
    const amount = commerce.value?.affiliate.availableBalance || 0;
    if (amount > 0) await requestWithdrawal(amount);
    await refreshCommerceState();
  } finally {
    commerceBusy.value = "";
  }
}

function openOpenApiDocs() {
  if (!openApiStatus.value?.docsUrl) return;
  window.open(openApiStatus.value.docsUrl, "_blank", "noopener,noreferrer");
}

async function detectDesktopPlatform(): Promise<DesktopPlatform> {
  const desktop = window.huosuLearningDesktop as any;
  if (desktop?.platformKey === "win_x64" || desktop?.platformKey === "mac_x64" || desktop?.platformKey === "mac_arm64") return desktop.platformKey;
  const userAgentData = (navigator as any).userAgentData;
  const platformText = `${userAgentData?.platform || navigator.platform || ""} ${navigator.userAgent || ""}`;
  if (/mac/i.test(platformText)) {
    try {
      const highEntropy = userAgentData?.getHighEntropyValues ? await userAgentData.getHighEntropyValues(["architecture"]) : null;
      if (String(highEntropy?.architecture || "").toLowerCase().includes("x86")) return "mac_x64";
    } catch {
      // 浏览器可能不开放芯片架构，默认推荐 Apple 芯片版本，同时保留 Intel 入口。
    }
    return "mac_arm64";
  }
  return "win_x64";
}

function getBrowserDeviceId() {
  const key = "huosu_browser_device_id";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const generated = crypto.randomUUID?.() || `browser-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  localStorage.setItem(key, generated);
  return generated;
}

async function detectRunningDesktopBridge() {
  // 普通网页不应主动请求 localhost，否则未安装桌面端时 Chrome 必然记录 ERR_CONNECTION_REFUSED。
  // 桌面壳通过 preload 注入对象即可确认运行状态，网页端则使用服务端的安装登记。
  return isDesktopShell;
}

async function refreshDesktopInstallationStatus(platform: DesktopPlatform) {
  desktopInstallStatus.value = null;
  desktopLocalBridgeRunning.value = await detectRunningDesktopBridge();
  if (desktopLocalBridgeRunning.value) return;
  try {
    desktopInstallStatus.value = await getDesktopInstallationStatus(platform, getBrowserDeviceId());
  } catch {
    desktopInstallStatus.value = { installed: false, platform, source: "none", message: "安装状态检测失败，保留下载入口。" };
  }
}

function isDesktopPlatform(value: unknown): value is DesktopPlatform {
  return value === "win_x64" || value === "mac_x64" || value === "mac_arm64";
}

async function registerCurrentDesktopInstallation() {
  const desktop = window.huosuLearningDesktop;
  if (!desktop) return;
  try {
    const metadata = (await desktop.getMetadata?.()) || {};
    const platform = isDesktopPlatform(metadata.platformKey) ? metadata.platformKey : await detectDesktopPlatform();
    await registerDesktopInstallation({
      installId: String(metadata.installId || ""),
      platform,
      appVersion: String(metadata.version || metadata.appVersion || desktop.appVersion || "0.0.1"),
      arch: String(metadata.arch || desktop.arch || ""),
      os: String(metadata.os || desktop.platform || ""),
      browserDeviceId: getBrowserDeviceId(),
      deviceInfo: metadata
    });
  } catch {
    // 安装登记失败不影响学习主流程，下一次打开会继续尝试。
  }
}

async function refreshDesktopDownloadCenter() {
  if (!DESKTOP_CLIENT_DOWNLOAD_ENABLED) {
    desktopDownloadOpen.value = false;
    desktopDownload.value = null;
    desktopInstallCheckReady.value = true;
    return;
  }
  if (isDesktopShell) {
    desktopInstallCheckReady.value = true;
    return;
  }
  desktopInstallCheckReady.value = false;
  try {
    desktopPlatform.value = await detectDesktopPlatform();
    await refreshDesktopInstallationStatus(desktopPlatform.value);
    desktopDownload.value = desktopInstalled.value ? null : await getDesktopDownloadCenter(desktopPlatform.value);
  } catch {
    desktopDownload.value = null;
  } finally {
    desktopInstallCheckReady.value = true;
  }
}

async function selectDesktopPlatform(platform: DesktopPlatform) {
  desktopPlatform.value = platform;
  try {
    await refreshDesktopInstallationStatus(platform);
    desktopDownload.value = desktopInstalled.value ? null : await getDesktopDownloadCenter(platform);
  } catch {
    desktopDownload.value = null;
  }
}

async function chooseDesktopDownload(platform: DesktopPlatform) {
  await selectDesktopPlatform(platform);
  const selected = desktopDownload.value?.platforms.find((item) => item.platform === platform);
  if (selected?.available) {
    downloadDesktopPackage();
    window.setTimeout(() => {
      desktopDownloadOpen.value = false;
    }, 1400);
  }
}

function normalizeApiAssetUrl(url: string) {
  if (!url.startsWith("/")) return url;
  const base = import.meta.env.VITE_API_BASE || window.location.origin;
  return `${String(base).replace(/\/$/, "")}${url}`;
}

/**
 * 真人切片不再让浏览器直接跨域读取 OSS。学习端改用同源受控媒体地址，
 * 既避免不同 OSS/CDN 节点 CORS 策略不一致，也确保只能读取已发布句子
 * 对应的录音片段。
 */
function recordingSourcePlaybackUrl(ossKey?: string) {
  const sourceKey = String(ossKey || "").replace(/^oss:\/\//, "");
  const source = sourceKey.match(/^recordings\/packages\/(recording-package-[A-Za-z0-9_-]+)\/source\/([a-f0-9A-Za-z_-]{16,128}\.(?:mp3|wav|m4a|mp4|m4v|mov|webm|ogg))$/i);
  if (source) {
    return `/api/v1/media/recording/${encodeURIComponent(source[1])}/source/${encodeURIComponent(source[2])}`;
  }
  return "";
}

type HumanRecordingRange = { startMs: number; endMs: number; derivedFromLegacySegments: boolean };

/**
 * New recording packages persist source windows directly.  Some older packages
 * still point at retired per-sentence MP3 objects, while retaining the original
 * source file and exact sequential clip durations.  Reconstruct their windows
 * only when every prior sibling belongs to the same source and has a valid
 * duration; otherwise keep the legacy asset instead of guessing a wrong line.
 */
function humanRecordingRange(sentence?: Sentence, asset?: ClientAudioAsset, lesson: Lesson | null = currentLesson.value): HumanRecordingRange | undefined {
  const configuredStart = Number(asset?.startMs);
  const configuredEnd = Number(asset?.endMs);
  if (Number.isFinite(configuredStart) && Number.isFinite(configuredEnd) && configuredStart >= 0 && configuredEnd > configuredStart) {
    return { startMs: configuredStart, endMs: configuredEnd, derivedFromLegacySegments: false };
  }
  const sourceKey = String(sentence?.sourceMeta?.sourceAudioOssKey || "");
  const duration = Number(asset?.durationMs || 0);
  if (!sourceKey || !recordingSourcePlaybackUrl(sourceKey) || !lesson || !sentence || !Number.isFinite(duration) || duration < 250) return undefined;
  const siblings = lesson.sentences.filter((candidate) => String(candidate.sourceMeta?.sourceAudioOssKey || "") === sourceKey);
  const index = siblings.findIndex((candidate) => candidate.id === sentence.id);
  if (index < 0) return undefined;
  let startMs = 0;
  for (const candidate of siblings.slice(0, index)) {
    const candidateAsset = candidate.audioAssets?.human_recording as unknown as ClientAudioAsset | undefined;
    // A source-range sibling makes this a mixed legacy/new manifest.  Do not
    // infer through it: its real silence gaps are meaningful playback data.
    if (Number.isFinite(Number(candidateAsset?.startMs)) || Number.isFinite(Number(candidateAsset?.endMs))) return undefined;
    const candidateDuration = Number(candidateAsset?.durationMs || 0);
    if (!Number.isFinite(candidateDuration) || candidateDuration < 250) return undefined;
    startMs += candidateDuration;
  }
  return { startMs, endMs: startMs + duration, derivedFromLegacySegments: true };
}

function recordingPlaybackUrl(asset?: ClientAudioAsset, sentence?: Sentence, lesson: Lesson | null = currentLesson.value) {
  const sourceUrl = recordingSourcePlaybackUrl(String(sentence?.sourceMeta?.sourceAudioOssKey || asset?.ossKey || ""));
  // A verified source time window is safer than a historical segment URL.
  // It also makes a package switch a one-time source load followed by seeks.
  if (sourceUrl && humanRecordingRange(sentence, asset, lesson)) return sourceUrl;
  const ossKey = String(asset?.ossKey || "").replace(/^oss:\/\//, "");
  const matched = ossKey.match(/^recordings\/packages\/(recording-package-[A-Za-z0-9_-]+)\/segments\/([^/]+\.mp3)$/i);
  if (matched) {
    return `/api/v1/media/recording/${encodeURIComponent(matched[1])}/${encodeURIComponent(matched[2])}`;
  }
  return asset?.url || "";
}

function playableAudioAssetUrl(asset?: ClientAudioAsset, sentence?: Sentence, lesson: Lesson | null = currentLesson.value) {
  if (!asset) return "";
  return String(asset.voiceCode) === "human_recording" || asset.ossKey?.startsWith("oss://recordings/packages/")
    ? recordingPlaybackUrl(asset, sentence, lesson)
    : asset.url;
}

/**
 * Course covers are generated as WebP, but older Safari and a few embedded
 * Chromium browsers cannot decode every WebP variant reliably. Some legacy
 * records also point straight to OSS where the object is delivered as an
 * attachment. Production publishes a same-origin JPEG compatibility copy for
 * every cover, so never expose either of those failure modes to a learner.
 */
function courseCoverDisplayUrl(url?: string) {
  const source = url ? normalizeApiAssetUrl(url) : "";
  if (!source || !/\.webp(?:[?#]|$)/i.test(source)) return source;

  try {
    const parsed = new URL(source, window.location.origin);
    const isProductionOrigin = window.location.hostname === "en.figo.cn";
    const isDirectCourseCover = parsed.hostname === "huosu-english-audio-prod.oss-cn-guangzhou.aliyuncs.com"
      && parsed.pathname.startsWith("/images/course-covers/");
    const isMirroredCourseCover = parsed.origin === window.location.origin
      && parsed.pathname.startsWith("/media/course-covers/");

    if (!isProductionOrigin || (!isDirectCourseCover && !isMirroredCourseCover)) return source;

    const relativePath = isDirectCourseCover
      ? parsed.pathname.slice("/images/course-covers/".length)
      : parsed.pathname.slice("/media/course-covers/".length);
    return `${window.location.origin}/media/course-covers/compat/${relativePath.replace(/\.webp$/i, ".jpg")}`;
  } catch {
    return source;
  }
}

function markCourseCoverFailed(courseId: string) {
  // A branded fallback is better than a broken-image glyph. This is only a
  // final safety net; normal production paths use the JPEG compatibility copy.
  if (courseCoverFailedIds.value[courseId]) return;
  courseCoverFailedIds.value = { ...courseCoverFailedIds.value, [courseId]: true };
}

function openDownloadUrl(url: string, fileName?: string) {
  const targetUrl = normalizeApiAssetUrl(url);
  const opener = window.huosuLearningDesktop?.openExternal;
  if (opener) {
    void opener(targetUrl);
    return;
  }
  const link = document.createElement("a");
  link.href = targetUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  if (fileName) link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function downloadDesktopPackage() {
  const latest = recommendedDesktopPackage.value?.latestVersion;
  if (!latest?.downloadUrl) return;
  if (desktopDownloadBusy.value === latest.id) return;
  desktopDownloadBusy.value = latest.id;
  desktopDownloadNotice.value = "已开始下载，请查看浏览器下载栏。";
  try {
    openDownloadUrl(latest.downloadUrl, latest.fileName);
    void trackDesktopDownload(latest.id).catch(() => undefined);
    window.setTimeout(() => {
      if (desktopDownloadBusy.value === latest.id) desktopDownloadBusy.value = "";
    }, 2200);
  } catch {
    desktopDownloadNotice.value = "下载窗口未打开，请再点一次。";
    desktopDownloadBusy.value = "";
  }
}

function nextSentence(options?: { completedAudio?: boolean }) {
  // “上一句/下一句”本身就是用户的明确操作；必须在此刻创建或恢复
  // Web Audio，等响应式切句后的自动朗读触发时，浏览器仍允许直接播放。
  if (options?.completedAudio !== true) unlockAudioFromUserGesture();
  // 答题后的配置播报是当前题流程的一部分。播报未结束时，任何 Enter、
  // 空格或按钮连击都不能提前切题；只有成功流程自身可以在播完后放行。
  if (successFlowRunning.value && options?.completedAudio !== true) return;
  if (!currentUser.value) {
    if (guestTrialState.value.locked) {
      showTrialGate();
      return;
    }
    markGuestTrialSentence(currentSentence.value?.id);
    if (guestTrialState.value.locked) {
      showTrialGate();
      return;
    }
  }
  recordLearningProgressBeforeLeaving();
  if (currentIndex.value < sentences.value.length - 1) {
    stopActiveSpeech();
    cancelSuccessFlow();
    currentIndex.value += 1;
    return;
  }
  if (adaptiveTrainingActive.value) {
    stopActiveSpeech();
    cancelSuccessFlow();
    adaptiveTrainingActive.value = false;
    openAdaptiveChoices();
    return;
  }
  if (activeGeneratedPack.value && availableAdaptivePacks.value.length > 1) {
    stopActiveSpeech();
    cancelSuccessFlow();
    smartPlanDetail.value = null;
    smartPlanOpen.value = true;
    return;
  }
  if (continuousCourseId.value === currentCourse.value?.id && nextLessonInCurrentCourse.value) {
    stopActiveSpeech();
    cancelSuccessFlow();
    void continueNextLesson();
    return;
  }
  stopActiveSpeech();
  cancelSuccessFlow();
  courseCompletionOpen.value = true;
}

function restartCurrentLesson() {
  courseCompletionOpen.value = false;
  resumeNotice.value = "";
  stopActiveSpeech();
  currentIndex.value = 0;
  resetForCurrentSentence();
}

async function continueNextLesson() {
  const lesson = nextLessonInCurrentCourse.value;
  if (!lesson || continuousLessonAdvancing.value) return;
  continuousLessonAdvancing.value = true;
  courseCompletionOpen.value = false;
  try {
    await loadLesson(lesson.id);
  } finally {
    continuousLessonAdvancing.value = false;
  }
}

function openSmartPlanAfterCompletion() {
  courseCompletionOpen.value = false;
  continuousCourseId.value = "";
  continuousNextLessonAudioSerial += 1;
  continuousNextLessonAudioKey = "";
  smartPlanOpen.value = true;
}

function openCourseCatalogAfterCompletion() {
  courseCompletionOpen.value = false;
  continuousCourseId.value = "";
  continuousNextLessonAudioSerial += 1;
  continuousNextLessonAudioKey = "";
  sidebarOpen.value = true;
}

function handleVisibilityChange() {
  if (document.hidden) {
    if (!landingVisible.value && currentSentence.value) {
      visibilityPausedAt = Date.now();
      stopActiveSpeech();
      cancelSuccessFlow();
      stopLandingTypingDemo();
    }
    return;
  }
  if (!visibilityPausedAt) return;
  sessionStartedAt.value += Date.now() - visibilityPausedAt;
  visibilityPausedAt = 0;
  elapsedSeconds.value = Math.floor((Date.now() - sessionStartedAt.value) / 1000);
  visibilityResumeOpen.value = true;
}

function dismissVisibilityPause(resume: boolean) {
  visibilityResumeOpen.value = false;
  if (resume && feedback.value?.correct && settings.value.autoNext) {
    void runSuccessFlow();
  } else if (resume && currentSentence.value && settings.value.autoRead) {
    void handleSpeak(currentSentence.value.english, 1);
  }
  focusTyping();
}

function prevSentence() {
  // 和下一句保持一致，避免返回上一句后真人原声又被浏览器当成无手势播放。
  unlockAudioFromUserGesture();
  if (!currentUser.value && guestTrialState.value.locked) {
    showTrialGate();
    return;
  }
  if (currentIndex.value > 0) {
    stopActiveSpeech();
    cancelSuccessFlow();
    currentIndex.value -= 1;
  }
}

async function lookupWord(word: string) {
  if (!word) return;
  const result = await searchWords(word);
  activeWord.value = exactWordEntry(result, word);
}

function clearPendingWordSave() {
  pendingWordSave.value = "";
  if (pendingWordSaveTimerId) window.clearTimeout(pendingWordSaveTimerId);
  pendingWordSaveTimerId = undefined;
}

function exactWordEntry(entries: WordEntry[], rawWord: string) {
  const target = canonicalWord(rawWord);
  return entries.find((entry) => canonicalWord(entry.word) === target) || null;
}

function personalLibraryDisplayName(library: LearnerActivityDashboard["personalWordLibraries"][number]) {
  if (library.source !== "system_seed") return library.name;
  return "个人生词库";
}

function closeWordPopup() {
  wordPopupOpen.value = false;
  wordPopupLoading.value = false;
  wordPopupText.value = "";
  wordSaveNotice.value = "";
  activeWord.value = null;
  clearPendingWordSave();
}

function positionWordPopup(target: HTMLElement) {
  const rect = target.getBoundingClientRect();
  const popupWidth = 292;
  const left = Math.max(12, Math.min(window.innerWidth - popupWidth - 12, rect.left + rect.width / 2 - popupWidth / 2));
  const roomBelow = window.innerHeight - rect.bottom;
  const top = roomBelow >= 220 ? rect.bottom + 10 : Math.max(12, rect.top - 210);
  wordPopupStyle.value = { left: `${left}px`, top: `${top}px` };
}

async function handleLearningWordClick(rawWord: string, event: MouseEvent, wordIndex = -1) {
  const word = canonicalWord(rawWord);
  if (!word || wordSaveBusy.value) return;
  const target = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
  if (target) positionWordPopup(target);
  wordPopupOpen.value = true;
  wordPopupText.value = rawWord;
  wordPopupStudyIndex.value = wordIndex;

  if (pendingWordSave.value !== word) {
    clearPendingWordSave();
    pendingWordSave.value = word;
    wordSaveNotice.value = `再点一次“${rawWord}”即可加入个人生词库`;
    activeWord.value = null;
    wordPopupLoading.value = true;
    const requestedWord = word;
    void searchWords(rawWord)
      .then((dictionary) => {
        if (!wordPopupOpen.value || canonicalWord(wordPopupText.value) !== requestedWord) return;
        activeWord.value = exactWordEntry(dictionary, rawWord);
      })
      .finally(() => {
        if (wordPopupOpen.value && canonicalWord(wordPopupText.value) === requestedWord) wordPopupLoading.value = false;
      });
    pendingWordSaveTimerId = window.setTimeout(() => {
      if (pendingWordSave.value === word) clearPendingWordSave();
      if (wordSaveNotice.value.includes(rawWord)) wordSaveNotice.value = "再次点击该单词可加入个人生词库";
    }, 3500);
    return;
  }

  clearPendingWordSave();
  wordSaveBusy.value = word;
  try {
    if (!learnerActivity.value) await refreshLearnerActivity(true);
    const library = personalWordLibrariesForPanel.value.find((item) => item.source === "system_seed")
      || personalWordLibrariesForPanel.value[0];
    if (!library) throw new Error("个人生词库暂不可用，请登录后重试");
    const dictionary = activeWord.value ? [activeWord.value] : await searchWords(rawWord);
    const entry = exactWordEntry(dictionary, rawWord);
    const existed = library.items.some((item) => canonicalWord(item.text) === word);
    const savedItem = await addPersonalLibraryItem(library.id, {
      sourceId: entry?.id,
      text: entry?.word || rawWord,
      translation: wordPopupStudy.value?.explain || entry?.meanings?.[0]?.zh,
      note: `${currentLesson.value?.title || "当前课节"} · 单词视图双击加入`,
      tags: ["unknown_word", "double_click_added"]
    });
    // Update the visible personal library immediately from the confirmed API
    // response, then reconcile with the server without blanking the panel if a
    // follow-up dashboard refresh is temporarily slow.
    if (!existed && !library.items.some((item) => item.id === savedItem.id)) {
      library.items.unshift(savedItem);
    }
    accountPanelOpen.value = true;
    accountTab.value = "library";
    wordSaveNotice.value = existed ? `“${rawWord}”已在个人生词库中` : `已将“${rawWord}”加入个人生词库`;
    await refreshLearnerActivity(false);
  } catch (error) {
    wordSaveNotice.value = error instanceof Error ? error.message : "加入个人生词库失败，请稍后重试";
  } finally {
    wordSaveBusy.value = "";
  }
}

function resumeDictation() {
  showAnswer.value = false;
  dictationLearningSuggestionOpen.value = false;
  nextTick(() => typingInputRef.value?.focus({ preventScroll: true }));
}

function toggleAnswer() {
  if (dictationReviewVisible.value) {
    resumeDictation();
    return;
  }
  if (mode.value === "dictation" && !showAnswer.value) {
    revealDictationAnswer();
    if (currentSentence.value) void handleSpeak(currentSentence.value.english, settings.value.answerPlayCount);
    return;
  }
  if (!showAnswer.value) practiceHintUsed = true;
  showAnswer.value = !showAnswer.value;
  if (showAnswer.value && currentSentence.value) {
    void handleSpeak(currentSentence.value.english, settings.value.answerPlayCount);
  }
}

async function handleSpeak(text: string, repeat: RepeatCount = 1, origin: SpeechPlaybackOrigin = "manual") {
  if (!text || trialGateOpen.value || onboardingOpen.value || adaptiveChoiceOpen.value || dailyRecommendationOpen.value) return;
  // Resume/create the audio context while the user gesture is still active.
  // This keeps playback available after an on-demand TTS request finishes.
  unlockSpeechAudioContext();
  stopPlayingAudioSources();
  const times = repeat === "manual" ? 1 : repeat;
  const serial = ++speakingSerial;
  let waitingForManualPlayback = false;
  isSpeaking.value = true;
  speechNotice.value = "";
  speechNoticeIsError.value = false;
  try {
    for (let index = 0; index < times; index += 1) {
      let waveRun: number | undefined;
      const notifyPlaybackStarted = (analyser?: AnalyserNode) => {
        if (serial !== speakingSerial) return;
        // 媒体真正开始播放即代表本句成功；任何旧请求残留的错误提示都
        // 不能继续留在当前句的界面上。
        speechNotice.value = "";
        speechNoticeIsError.value = false;
        if (waveRun === undefined) waveRun = startVoiceWaveform(analyser);
      };
      try {
        await speakOnce(text, serial, notifyPlaybackStarted, origin);
      } finally {
        if (waveRun !== undefined) stopVoiceWaveform(waveRun);
      }
      if (index < times - 1) await sleep(140);
    }
  } catch (error) {
    // 上一句被停止后可能才完成其异步回调；它不属于当前播放，绝不能
    // 用旧错误覆盖当前已正常发声的句子。
    if (serial !== speakingSerial) return;
    if (origin === "automatic" && isAutomaticPlaybackGestureError(error)) {
      waitingForManualPlayback = true;
      speechNotice.value = "音频已准备好，点击“朗读”即可播放。";
      speechNoticeIsError.value = false;
    } else {
      speechNotice.value = friendlySpeechError(error);
      speechNoticeIsError.value = true;
    }
  } finally {
    if (speakingSerial === serial) {
      window.setTimeout(() => {
        if (speakingSerial === serial) isSpeaking.value = false;
      }, 260);
      if (!speechNoticeIsError.value) {
        window.setTimeout(() => {
          if (speakingSerial === serial) speechNotice.value = "";
        }, waitingForManualPlayback ? 6000 : 1200);
      }
    }
  }
}

function friendlySpeechError(error: unknown) {
  const message = error instanceof Error ? error.message : "";
  if (/timeout|timed out|ECONNABORTED/i.test(message)) {
    return "语音连接超时，请点击“朗读”重试";
  }
  if (/network|fetch|connection|ERR_NETWORK/i.test(message)) {
    return "语音服务网络暂时不稳定，请稍后重试";
  }
  if (message && !/AxiosError|status code|\b\d{3}\b/i.test(message)) return message;
  return "语音播放失败，请稍后点击朗读重试";
}

function isHumanRecordingSentence(sentence?: Sentence) {
  return sentence?.sourceMeta?.audioSourceType === "human_recording" || Boolean(sentence?.audioAssets?.human_recording);
}

function isFilmVideoSentence(sentence?: Sentence) {
  return sentence?.sourceMeta?.mediaSourceType === "film_video";
}

async function getGeneratedTtsAudio(text: string, forceVerified = false): Promise<ClientAudioAsset | undefined> {
  const sentence = currentSentence.value;
  if (!sentence || text !== sentence.english) return undefined;
  return ensureCurrentSentenceTtsAudio(sentence, forceVerified);
}

function currentAudioLoadKey(sentence: Sentence) {
  return `${sentence.id}:${audioAssetKey(settings.value.voiceCode, settings.value.speechRate)}:${settings.value.speechRate}`;
}

function beginCurrentAudioLoad(sentence: Sentence) {
  const key = currentAudioLoadKey(sentence);
  currentAudioLoadCounts.value = {
    ...currentAudioLoadCounts.value,
    [key]: (currentAudioLoadCounts.value[key] || 0) + 1
  };
  return key;
}

function finishCurrentAudioLoad(key: string) {
  const count = currentAudioLoadCounts.value[key] || 0;
  if (!count) return;
  const next = { ...currentAudioLoadCounts.value };
  if (count <= 1) delete next[key];
  else next[key] = count - 1;
  currentAudioLoadCounts.value = next;
}

async function ensureCurrentSentenceTtsAudio(sentence: Sentence, forceVerified = false) {
  if (isHumanRecordingSentence(sentence)) {
    const humanAsset = sentence.audioAssets?.human_recording as unknown as ClientAudioAsset | undefined;
    if (!humanAsset?.url || humanAsset.status !== "generated") throw new Error("真人录音正在加载，审核通过后会自动使用原声播放。");
    return humanAsset;
  }
  const key = beginCurrentAudioLoad(sentence);
  try {
    try {
      return await ensureSentenceTtsAudio(sentence, forceVerified, "interactive");
    } catch (error) {
      // A brief transport hiccup must not turn into a manual-only recovery
      // path. The retry rejoins the same server job when it is still running.
      if (!isTransientTtsError(error)) throw error;
      await sleep(900);
      return await ensureSentenceTtsAudio(sentence, true, "interactive");
    }
  } finally {
    finishCurrentAudioLoad(key);
  }
}

function isTransientTtsError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  return /timeout|timed out|ECONNABORTED|network|connection|ERR_NETWORK|音频连接暂时不稳定/i.test(message);
}

async function ensureSentenceTtsAudio(sentence: Sentence, forceVerified = false, priority: "interactive" | "prefetch" = "interactive"): Promise<ClientAudioAsset | undefined> {
  // 真人录音包只能播放其审核后的原始句子片段。无论用户切换何种 AI
  // 音色或语速，都绝不回退到 TTS，避免原声内容被悄然替换。
  if (isHumanRecordingSentence(sentence)) {
    const humanAsset = sentence.audioAssets?.human_recording as unknown as ClientAudioAsset | undefined;
    if (humanAsset?.url && humanAsset.status === "generated") return humanAsset;
    throw new Error("真人录音正在加载，暂不能调用 AI 配音替代。");
  }
  const assetKey = audioAssetKey(settings.value.voiceCode, settings.value.speechRate);
  const existing = sentence.audioAssets?.[assetKey] as unknown as ClientAudioAsset | undefined;
  if (existing?.url && !forceVerified) return existing;

  const cacheKey = `${sentence.id}:${assetKey}:${sentence.english}`;
  if (!forceVerified && Date.now() < (ttsUnavailableUntilByKey.get(cacheKey) || 0)) return undefined;
  const pending = ttsRequestCache.get(cacheKey);
  if (pending) return pending;

  const request = ensureTtsAudio({
    sentenceId: sentence.id,
    text: sentence.english,
    voiceCode: settings.value.voiceCode,
    speechRate: settings.value.speechRate,
    verifyCached: forceVerified,
    priority
  })
    .then((result) => {
      const generatedAsset = result.audioAsset as unknown as ClientAudioAsset | undefined;
      if (generatedAsset?.url) {
        ttsUnavailableUntilByKey.delete(cacheKey);
        sentence.audioAssets = {
          ...(sentence.audioAssets || {}),
          [assetKey]: generatedAsset
        };
      }
      return generatedAsset;
    })
    .catch((error) => {
      // Keep background workers from hammering one failing asset, without
      // preventing the next word (or an explicit verified retry) from loading.
      ttsUnavailableUntilByKey.set(cacheKey, Date.now() + 8_000);
      throw error instanceof Error ? error : new Error("语音生成失败，请稍后重试");
    })
    .finally(() => {
      ttsRequestCache.delete(cacheKey);
    });
  ttsRequestCache.set(cacheKey, request);
  return request;
}

function preloadAudioUrl(url: string, serial: number) {
  return new Promise<boolean>((resolve) => {
    if (serial !== lessonAudioPreparationSerial) return resolve(false);
    const audio = document.createElement("audio");
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      audio.removeEventListener("canplaythrough", onReady);
      audio.removeEventListener("loadeddata", onReady);
      audio.removeEventListener("error", onError);
      activeBackgroundAudioPreloads.delete(audio);
      resolve(ready);
    };
    const onReady = () => finish(true);
    const onError = () => finish(false);
    const timeoutId = window.setTimeout(() => finish(audio.readyState >= 2), 10_000);
    audio.preload = "auto";
    audio.addEventListener("canplaythrough", onReady, { once: true });
    audio.addEventListener("loadeddata", onReady, { once: true });
    audio.addEventListener("error", onError, { once: true });
    audio.src = normalizeApiAssetUrl(url);
    activeBackgroundAudioPreloads.add(audio);
    audio.load();
  });
}

function getHumanRecordingAudio(url: string) {
  const absoluteUrl = new URL(url, window.location.href).href;
  if (humanRecordingAudio && humanRecordingAudioUrl === absoluteUrl) return humanRecordingAudio;
  if (humanRecordingAudio) {
    const previous = humanRecordingAudio;
    // A package switch invalidates the old source.  Settle its own playback
    // promise before releasing the element; otherwise its delayed `error`
    // event can paint “真人录音加载失败” over the newly selected package.
    previous.pause();
    htmlPlaybackFinishers.get(previous)?.();
    previous.removeAttribute("src");
    previous.load();
  }
  const audio = document.createElement("audio");
  audio.preload = "auto";
  audio.setAttribute("playsinline", "true");
  audio.src = absoluteUrl;
  audio.load();
  humanRecordingAudio = audio;
  humanRecordingAudioUrl = absoluteUrl;
  return audio;
}

function getFilmRecordingVideo(url: string) {
  const absoluteUrl = new URL(url, window.location.href).href;
  const rendered = filmVideoRef.value;
  if (rendered && new URL(rendered.currentSrc || rendered.src || absoluteUrl, window.location.href).href === absoluteUrl) {
    filmRecordingVideo = rendered;
    filmRecordingVideoUrl = absoluteUrl;
    return rendered;
  }
  if (filmRecordingVideo && filmRecordingVideoUrl === absoluteUrl) return filmRecordingVideo;
  if (filmRecordingVideo) { filmRecordingVideo.pause(); filmRecordingVideo.removeAttribute("src"); filmRecordingVideo.load(); }
  const video = document.createElement("video");
  video.preload = "auto";
  video.setAttribute("playsinline", "true");
  video.src = absoluteUrl;
  video.load();
  filmRecordingVideo = video;
  filmRecordingVideoUrl = absoluteUrl;
  return video;
}

/** Warm one source file once.  We intentionally reuse this native element
 * rather than create one preload per sentence; browser byte-range caching can
 * then seek between sentence windows without duplicated downloads. */
function preloadHumanRecordingSource(url: string, serial: number) {
  return new Promise<boolean>((resolve) => {
    if (serial !== lessonAudioPreparationSerial) return resolve(false);
    const audio = getHumanRecordingAudio(url);
    if (audio.readyState >= 2) return resolve(true);
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      audio.removeEventListener("loadeddata", onReady);
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("error", onError);
      resolve(ready);
    };
    const onReady = () => finish(true);
    const onError = () => finish(false);
    const timeoutId = window.setTimeout(() => finish(audio.readyState >= 2), 10_000);
    audio.addEventListener("loadeddata", onReady, { once: true });
    audio.addEventListener("canplay", onReady, { once: true });
    audio.addEventListener("error", onError, { once: true });
  });
}

function preloadFilmRecordingSource(url: string, serial: number) {
  return new Promise<boolean>((resolve) => {
    if (serial !== lessonAudioPreparationSerial) return resolve(false);
    const video = getFilmRecordingVideo(url);
    if (video.readyState >= 1) return resolve(true);
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      video.removeEventListener("loadedmetadata", onReady);
      video.removeEventListener("error", onError);
      resolve(ready);
    };
    const onReady = () => finish(true);
    const onError = () => finish(false);
    const timeoutId = window.setTimeout(() => finish(video.readyState >= 1), 12_000);
    video.addEventListener("loadedmetadata", onReady, { once: true });
    video.addEventListener("error", onError, { once: true });
  });
}

async function prepareSentenceAudioForBatch(sentence: Sentence, serial: number) {
  if (trialGateOpen.value || serial !== lessonAudioPreparationSerial || !sentence.english) return false;
  const asset = await ensureSentenceTtsAudio(sentence, false, "prefetch");
  if (trialGateOpen.value || !asset?.url || serial !== lessonAudioPreparationSerial) return false;
  if (isFilmVideoSentence(sentence)) return preloadFilmRecordingSource(playableAudioAssetUrl(asset, sentence), serial);
  if (isHumanRecordingSentence(sentence)) return preloadHumanRecordingSource(playableAudioAssetUrl(asset, sentence), serial);
  return preloadAudioUrl(playableAudioAssetUrl(asset, sentence), serial);
}

async function runLessonAudioWorkers(sentences: Sentence[], serial: number, label: string) {
  let cursor = 0;
  lessonAudioPreparationLabel.value = label;
  const worker = async () => {
    while (!trialGateOpen.value && serial === lessonAudioPreparationSerial) {
      const index = cursor;
      cursor += 1;
      if (index >= sentences.length) return;
      const ready = await prepareSentenceAudioForBatch(sentences[index], serial).catch(() => false);
      if (serial !== lessonAudioPreparationSerial) return;
      if (!ready) lessonAudioFailed.value += 1;
      lessonAudioPrepared.value += 1;
    }
  };
  // Edge/OSS generation is a shared external channel. Serial background
  // warming keeps it responsive for the learner's current sentence.
  await worker();
}

async function prepareLessonAudioBatch(lesson: Lesson, startIndex = 0) {
  if (trialGateOpen.value) return;
  const sentences = lesson.sentences.filter((sentence) => Boolean(sentence.english));
  const serial = ++lessonAudioPreparationSerial;
  if (lessonAudioCompletionTimer) window.clearTimeout(lessonAudioCompletionTimer);
  lessonAudioTotal.value = Math.min(INITIAL_AUDIO_BATCH_SIZE, sentences.length);
  lessonAudioPrepared.value = 0;
  lessonAudioFailed.value = 0;
  if (!sentences.length) return;

  const safeStart = Math.min(Math.max(0, startIndex), sentences.length - 1);
  // First paint only prepares the item the learner is currently viewing.
  // Later audio is scheduled after the learning/typing screen is interactive.
  const priority = sentences.slice(safeStart, safeStart + INITIAL_AUDIO_BATCH_SIZE);
  // 课节内容就绪后立即进入练习，音频在后台准备。TTS/OSS 超时
  // 不得阻塞学习页，用户仍可在朗读时按需重试。
  lessonAudioPreparingBlocking.value = false;
  lessonAudioPreparationVisible.value = true;
  const completePreparation = async () => {
    try {
      await runLessonAudioWorkers(priority, serial, `正在后台准备首批 ${priority.length} 条音频`);
      if (serial !== lessonAudioPreparationSerial) return;
      lessonAudioPreparationLabel.value = lessonAudioFailed.value ? "已进入练习，当前音频将在朗读时重试" : `当前音频已就绪，正在后台预热后续 ${BACKGROUND_AUDIO_WINDOW_SIZE} 条内容`;
    } catch {
      if (serial !== lessonAudioPreparationSerial) return;
      lessonAudioFailed.value = Math.max(lessonAudioFailed.value, priority.length - lessonAudioPrepared.value);
      lessonAudioPreparationLabel.value = "已进入练习，音频将在朗读时重试";
    } finally {
      if (serial !== lessonAudioPreparationSerial) return;
      lessonAudioPreparingBlocking.value = false;
      lessonAudioCompletionTimer = window.setTimeout(() => { lessonAudioPreparationVisible.value = false; }, lessonAudioFailed.value ? 6000 : 2200);
      scheduleCurrentPracticeWindowPreload();
    }
  };
  void completePreparation();
}

async function preloadCurrentPracticeWindow() {
  if (trialGateOpen.value) return;
  const lesson = currentLesson.value;
  if (!lesson) return;
  const serial = lessonAudioPreparationSerial;
  // The current item is handled by prepareLessonAudioBatch / prepareCurrentSentenceAudio.
  // Keep the following four items warm while the learner is practising. The
  // window moves forward on every sentence change, rather than fetching an
  // entire lesson and delaying the first playable item.
  const windowSentences = lesson.sentences.slice(currentIndex.value + 1, currentIndex.value + 1 + BACKGROUND_AUDIO_WINDOW_SIZE);
  for (const sentence of windowSentences) {
    if (serial !== lessonAudioPreparationSerial) return;
    await prepareSentenceAudioForBatch(sentence, serial).catch(() => false);
  }
}

function scheduleCurrentPracticeWindowPreload() {
  if (trialGateOpen.value) return;
  if (practiceWindowPreloadTimer) window.clearTimeout(practiceWindowPreloadTimer);
  practiceWindowPreloadTimer = window.setTimeout(() => {
    practiceWindowPreloadTimer = undefined;
    void preloadCurrentPracticeWindow();
  }, 360);
}

function restartAudioPreloadForCurrentPractice() {
  void loadCurrentStudyGrammar();
  if (trialGateOpen.value) return;
  // A mode/voice/rate change starts a new queue. Existing network requests may
  // finish harmlessly and remain cached, but their old playback preloads must
  // not dictate what the learner hears next.
  lessonAudioPreparationSerial += 1;
  if (lessonAudioCompletionTimer) window.clearTimeout(lessonAudioCompletionTimer);
  lessonAudioCompletionTimer = undefined;
  if (practiceWindowPreloadTimer) window.clearTimeout(practiceWindowPreloadTimer);
  practiceWindowPreloadTimer = undefined;
  activeBackgroundAudioPreloads.forEach((audio) => {
    audio.pause();
    audio.removeAttribute("src");
    audio.load();
  });
  activeBackgroundAudioPreloads.clear();

  const lesson = currentLesson.value;
  if (!lesson) return;
  void prepareLessonAudioBatch(lesson, currentIndex.value);
  scheduleCurrentPracticeWindowPreload();
}

function preloadContinuousAudioUrl(url: string, serial: number) {
  return new Promise<void>((resolve) => {
    if (serial !== continuousNextLessonAudioSerial) return resolve();
    const audio = document.createElement("audio");
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      audio.removeEventListener("canplaythrough", finish);
      audio.removeEventListener("loadeddata", finish);
      audio.removeEventListener("error", finish);
      activeBackgroundAudioPreloads.delete(audio);
      resolve();
    };
    const timeoutId = window.setTimeout(finish, 10_000);
    audio.preload = "auto";
    audio.addEventListener("canplaythrough", finish, { once: true });
    audio.addEventListener("loadeddata", finish, { once: true });
    audio.addEventListener("error", finish, { once: true });
    audio.src = normalizeApiAssetUrl(url);
    activeBackgroundAudioPreloads.add(audio);
    audio.load();
  });
}

async function preloadNextContinuousLesson() {
  const course = currentCourse.value;
  const currentLessonId = currentLesson.value?.id;
  const nextLesson = nextLessonInCurrentCourse.value;
  if (trialGateOpen.value || !course || !currentLessonId || !nextLesson || continuousCourseId.value !== course.id) return;

  const preloadKey = `${course.id}:${nextLesson.id}:${settings.value.voiceCode}:${settings.value.speechRate}`;
  if (continuousNextLessonAudioKey === preloadKey) return;
  continuousNextLessonAudioKey = preloadKey;
  const serial = ++continuousNextLessonAudioSerial;

  try {
    const detail = await getPracticeLesson(nextLesson.id);
    if (
      serial !== continuousNextLessonAudioSerial
      || continuousCourseId.value !== course.id
      || currentLesson.value?.id !== currentLessonId
    ) return;
    const sentencesToPrepare = detail.lesson.sentences.filter((sentence) => Boolean(sentence.english)).slice(0, 3);
    let cursor = 0;
    const worker = async () => {
      while (serial === continuousNextLessonAudioSerial) {
        const sentence = sentencesToPrepare[cursor++];
        if (!sentence) return;
        try {
          const asset = await ensureSentenceTtsAudio(sentence, false, "prefetch");
          if (asset?.url && serial === continuousNextLessonAudioSerial) {
            await preloadContinuousAudioUrl(playableAudioAssetUrl(asset, sentence, detail.lesson), serial);
          }
        } catch {
          // 下一小包预加载失败不阻塞当前练习；进入该包时会按正常流程重试。
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, sentencesToPrepare.length) }, () => worker()));
  } catch {
    if (continuousNextLessonAudioKey === preloadKey) continuousNextLessonAudioKey = "";
  }
}

async function prepareCurrentSentenceAudio() {
  void loadCurrentStudyGrammar();
  if (trialGateOpen.value) return;
  const sentence = currentSentence.value;
  if (!sentence?.english) return;
  const sentenceId = sentence.id;
  try {
    // Refresh restoration should prefer the existing asset and must never
    // compete with auto-read by forcing a second TTS verification request.
    const asset = await getGeneratedTtsAudio(sentence.english, false);
    if (!asset?.url || currentSentence.value?.id !== sentenceId) return;
    const url = normalizeApiAssetUrl(playableAudioAssetUrl(asset, sentence));
    if (isFilmVideoSentence(sentence)) {
      getFilmRecordingVideo(url);
      return;
    }
    if (isHumanRecordingSentence(sentence)) {
      preparedSpeechAudio = getHumanRecordingAudio(url);
      return;
    }
    const audio = document.createElement("audio");
    audio.preload = "auto";
    // 真人原声位于 OSS。明确使用 CORS 模式后，可同时作为 HTML
    // fallback 播放，且在已解锁的 AudioContext 中解码自动朗读。
    if (new URL(url, window.location.href).origin !== window.location.origin) audio.crossOrigin = "anonymous";
    audio.src = url;
    audio.load();
    if (currentSentence.value?.id === sentenceId) preparedSpeechAudio = audio;
  } catch {
    // Preloading is best-effort. A transient refresh/network failure is retried
    // by the real playback path and must not become an unhandled page error.
    if (currentSentence.value?.id === sentenceId) preparedSpeechAudio = undefined;
  }
}

const AUTOMATIC_PLAYBACK_GESTURE_REQUIRED = "automatic playback needs unlocked audio context";

function isAutomaticPlaybackGestureError(error: unknown) {
  return error instanceof Error && error.message === AUTOMATIC_PLAYBACK_GESTURE_REQUIRED;
}

function playSpeechAsset(
  url: string,
  absoluteUrl: string,
  onPlaybackStart: (analyser?: AnalyserNode) => void,
  origin: SpeechPlaybackOrigin
) {
  if (origin === "automatic") {
    // A running context is created on the learner's pointer/key action. Safari
    // permits a buffer source from that context after the asynchronous TTS
    // lookup, while it rejects an HTMLMediaElement.play() started this late.
    if (!speechAudioContext || speechAudioContext.state !== "running") {
      // 首次从“去学习”打开新页面时，浏览器可能没有把上个页面的
      // 点击授权带过来。仍先尝试已预热的真人音频；只有浏览器明确
      // 拒绝时才降级为提示，而不是无条件要求再点一次。
      return playHtmlAudioUrl(url, absoluteUrl, onPlaybackStart).catch((error) => {
        if (isAutoplayDenied(error)) throw new Error(AUTOMATIC_PLAYBACK_GESTURE_REQUIRED);
        throw error;
      });
    }
    // 真人切片有时可被浏览器原生媒体播放器解码、却不被 Web Audio 解码。
    // 自动朗读也必须走同一条原生播放器兜底，不能只让手动“朗读”可播。
    // 只有原生播放也被浏览器明确拒绝时，才提示需要用户操作。
    return playAudioUrl(url, onPlaybackStart, true).catch((error) => {
      if (isAutoplayDenied(error)) throw new Error(AUTOMATIC_PLAYBACK_GESTURE_REQUIRED);
      throw error;
    });
  }
  // The explicit “朗读” action unlocks Web Audio before the asynchronous TTS
  // lookup begins. Prefer that context: Safari otherwise loses the gesture by
  // the time a newly created HTMLAudioElement is ready to play.
  return playAudioUrl(url, onPlaybackStart);
}

async function speakOnce(
  text: string,
  runSerial: number,
  onPlaybackStart: (analyser?: AnalyserNode) => void,
  origin: SpeechPlaybackOrigin
) {
  if (runSerial !== speakingSerial) return;
  // 新页首句可能尚未创建 AudioContext。不能先行拒绝自动朗读：若用户
  // 刚通过“去学习”导航进入，浏览器仍可能允许 HTMLAudioElement 直接播放。
  const asset = text === currentSentence.value?.english ? selectedAudioAsset.value : undefined;
  if (asset?.url) {
    try {
      if (runSerial !== speakingSerial) return;
      const url = normalizeApiAssetUrl(playableAudioAssetUrl(asset, currentSentence.value));
      // 真人切片已经在当前课节加载时被原生 audio 预热。对 MP3 直接走
      // 浏览器媒体播放器，避免个别 Chromium 版本对同一文件的 Web Audio
      // 解码失败后把后续句子都标成“加载失败”。
      if (currentSentence.value && isHumanRecordingSentence(currentSentence.value)) {
        await playHumanRecordingRange(asset, url, onPlaybackStart);
        return;
      }
      await playSpeechAsset(url, new URL(url, window.location.href).href, onPlaybackStart, origin);
      return;
    } catch (error) {
      if (currentSentence.value && isHumanRecordingSentence(currentSentence.value)) {
        if (isAutomaticPlaybackGestureError(error) || (origin === "automatic" && isAutoplayDenied(error))) throw new Error(AUTOMATIC_PLAYBACK_GESTURE_REQUIRED);
        throw new Error("真人录音加载失败，请稍后点击朗读重试。");
      }
      // Continue by requesting a freshly verified server MP3.
    }
  }

  if (currentSentence.value && isHumanRecordingSentence(currentSentence.value)) {
    throw new Error("真人录音正在加载或不可用，系统不会调用 AI 配音替代。");
  }

  // First reuse the normal cache/generation path. Forcing remote verification
  // on every cache miss added a second storage check before users heard sound.
  // We still verify below when the returned URL genuinely fails to play.
  const generatedAsset = await getGeneratedTtsAudio(text, false);
  // More than one initial auto-read path can wait for the same first MP3.
  // Only the newest request may continue once that asynchronous request resolves.
  if (runSerial !== speakingSerial) return;
  if (generatedAsset?.url) {
    try {
      const url = normalizeApiAssetUrl(generatedAsset.url);
      await playSpeechAsset(url, new URL(url, window.location.href).href, onPlaybackStart, origin);
      return;
    } catch {
      // A just-restored page can briefly race OSS/CDN readiness. Verify once
      // only after the normal cached URL has failed, then retry that URL.
      if (runSerial !== speakingSerial) return;
      const verifiedAsset = await getGeneratedTtsAudio(text, true).catch(() => generatedAsset);
      if (runSerial !== speakingSerial) return;
      try {
        const retryUrl = appendAudioRetryQuery(normalizeApiAssetUrl(verifiedAsset?.url || generatedAsset.url));
        await playSpeechAsset(retryUrl, new URL(retryUrl, window.location.href).href, onPlaybackStart, origin);
        return;
      } catch {
        // The desktop client may still use its native bridge below.
      }
    }
  }

  if (window.huosuLearningDesktop) {
    try {
      const spoken = await window.huosuLearningDesktop.speak(text);
      if (spoken !== false) {
        onPlaybackStart();
        await sleep(estimateSpeechDuration(text));
        return;
      }
    } catch {
      // Do not replace the configured voice with Chrome's system voice.
    }
  }
  throw new Error("语音加载失败，请稍后重新播放");
}

function appendAudioRetryQuery(rawUrl: string) {
  try {
    const url = new URL(rawUrl, window.location.href);
    url.searchParams.set("huosu_retry", String(Date.now()));
    return url.href;
  } catch {
    return rawUrl;
  }
}

function estimateSpeechDuration(text: string) {
  const words = normalizeWords(text).length || Math.max(1, Math.round(text.length / 5));
  const multiplier = selectedSpeechRate.value?.rateMultiplier || 1;
  return Math.min(7000, Math.max(1000, Math.round((words * 360) / multiplier)));
}

function startVoiceWaveform(analyser?: AnalyserNode) {
  const runId = ++waveformRunId;
  if (waveformAnimationFrameId) window.cancelAnimationFrame(waveformAnimationFrameId);
  waveformProfile.value = Array.from({ length: 44 }, () => 4);
  waveformActive.value = true;
  if (analyser) {
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.72;
  }
  const samples = analyser ? new Uint8Array(analyser.fftSize) : undefined;
  const startedAt = performance.now();
  const render = () => {
    if (runId !== waveformRunId) return;
    if (analyser && samples) analyser.getByteTimeDomainData(samples);
    const previous = waveformProfile.value;
    const barCount = previous.length;
    waveformProfile.value = Array.from({ length: barCount }, (_, index) => {
      let peak: number;
      if (samples) {
        const from = Math.floor((index / barCount) * samples.length);
        const to = Math.max(from + 1, Math.floor(((index + 1) / barCount) * samples.length));
        peak = 0;
        for (let sampleIndex = from; sampleIndex < to; sampleIndex += 1) {
          peak = Math.max(peak, Math.abs(samples[sampleIndex] - 128) / 128);
        }
      } else {
        const elapsed = (performance.now() - startedAt) / 1000;
        const pulse = Math.sin(elapsed * 8 + index * 0.72) * 0.34;
        const ripple = Math.sin(elapsed * 13 - index * 0.31) * 0.18;
        peak = Math.max(0.08, Math.min(0.88, 0.38 + pulse + ripple));
      }
      const liveHeight = 4 + Math.pow(peak, 0.72) * 40;
      return Math.round((previous[index] || 4) * 0.46 + liveHeight * 0.54);
    });
    waveformAnimationFrameId = window.requestAnimationFrame(render);
  };
  waveformAnimationFrameId = window.requestAnimationFrame(render);
  return runId;
}

function stopVoiceWaveform(runId: number) {
  if (runId !== waveformRunId) return;
  if (waveformAnimationFrameId) window.cancelAnimationFrame(waveformAnimationFrameId);
  waveformAnimationFrameId = undefined;
  waveformProfile.value = waveformProfile.value.map(() => 4);
  waveformActive.value = false;
}

function playAudioUrl(url: string, onPlaybackStart: (analyser?: AnalyserNode) => void, allowHtmlFallback = true) {
  const absoluteUrl = new URL(url, window.location.href).href;
  const context = unlockSpeechAudioContext();
  if (context) {
    return (async () => {
      await speechAudioResumePromise;
      if (context.state !== "running") throw new Error(`audio context ${context.state}`);
      let buffer = speechAudioBufferCache.get(absoluteUrl);
      if (!buffer) {
        const response = await fetch(absoluteUrl, { credentials: "omit", mode: "cors" });
        if (!response.ok) throw new Error(`audio failed: ${response.status}`);
        buffer = await context.decodeAudioData(await response.arrayBuffer());
        speechAudioBufferCache.set(absoluteUrl, buffer);
      }
      await new Promise<void>((resolve) => {
        const source = context.createBufferSource();
        try { activeSpeechBufferSource?.stop(); } catch { /* Already ended. */ }
        activeSpeechBufferSource = source;
        source.buffer = buffer;
        source.playbackRate.value = selectedSpeechRate.value?.rateMultiplier || 1;
        const analyser = context.createAnalyser();
        source.connect(analyser).connect(context.destination);
        source.onended = () => {
          if (activeSpeechBufferSource === source) activeSpeechBufferSource = undefined;
          resolve();
        };
        onPlaybackStart(analyser);
        source.start(0);
      });
    })().catch((error) => {
      if (allowHtmlFallback) return playHtmlAudioUrl(url, absoluteUrl, onPlaybackStart);
      throw error;
    });
  }
  return playHtmlAudioUrl(url, absoluteUrl, onPlaybackStart);
}

/** Play only the persisted sentence window from a shared source recording.
 * The stop boundary is enforced by the player, so a seek never leaks the
 * following reply into the current sentence. */
function playHumanRecordingRange(asset: ClientAudioAsset, url: string, onPlaybackStart: (analyser?: AnalyserNode) => void) {
  const absoluteUrl = new URL(url, window.location.href).href;
  const isFilm = isFilmVideoSentence(currentSentence.value);
  const audio = isFilm ? getFilmRecordingVideo(absoluteUrl) : getHumanRecordingAudio(absoluteUrl);
  const range = humanRecordingRange(currentSentence.value, asset);
  const startSeconds = Math.max(0, Number(range?.startMs || asset.startMs || 0) / 1000);
  const configuredEnd = Number(range?.endMs || asset.endMs || 0) / 1000;
  const fallbackDuration = Math.max(0.25, Number(asset.durationMs || 0) / 1000);
  const endSeconds = configuredEnd > startSeconds ? configuredEnd : startSeconds + fallbackDuration;
  return new Promise<void>((resolve, reject) => {
    if (activeSpeechHtmlAudio && activeSpeechHtmlAudio !== audio) {
      const previous = activeSpeechHtmlAudio;
      previous.pause();
      htmlPlaybackFinishers.get(previous)?.();
    }
    activeSpeechHtmlAudio = audio;
    audio.muted = false;
    audio.volume = 1;
    audio.playbackRate = 1;
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      audio.removeEventListener("timeupdate", stopAtBoundary);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      htmlPlaybackFinishers.delete(audio);
      if (activeSpeechHtmlAudio === audio) activeSpeechHtmlAudio = undefined;
      if (error) reject(error);
      else resolve();
    };
    const stopAtBoundary = () => {
      if (audio.currentTime + 0.015 < endSeconds) return;
      audio.pause();
      // Keep the playhead on the sentence end.  The next sentence explicitly
      // seeks to its own start; this prevents residual audio from leaking.
      audio.currentTime = endSeconds;
      finish();
    };
    const onEnded = () => finish();
    const onError = () => {
      // Ignore an error emitted by a source that has already been superseded
      // by another course/package selection.
      const stillCurrent = isFilm
        ? filmRecordingVideo === audio && filmRecordingVideoUrl === absoluteUrl
        : humanRecordingAudio === audio && humanRecordingAudioUrl === absoluteUrl;
      if (!stillCurrent) return finish();
      finish(new Error(`${isFilm ? "影视原声画面" : "真人原声"}加载失败（${audio.error?.code || "未知错误"}）`));
    };
    const timeoutId = window.setTimeout(() => {
      audio.pause();
      finish(new Error("真人原声加载超时"));
    }, Math.max(12_000, Math.round((endSeconds - startSeconds + 8) * 1000)));
    htmlPlaybackFinishers.set(audio, () => finish());
    audio.addEventListener("timeupdate", stopAtBoundary);
    audio.addEventListener("ended", onEnded, { once: true });
    audio.addEventListener("error", onError, { once: true });
    const start = () => {
      try { audio.currentTime = startSeconds; } catch { /* Metadata will finish shortly. */ }
      audio.play().then(() => onPlaybackStart()).catch((error) => {
        finish(new Error(error instanceof Error ? `${isFilm ? "影视原声" : "真人原声"}播放失败：${error.message}` : `${isFilm ? "影视原声" : "真人原声"}播放失败`));
      });
    };
    if (audio.readyState >= 1) start();
    else {
      const onMetadata = () => {
        audio.removeEventListener("loadedmetadata", onMetadata);
        if (!settled) start();
      };
      audio.addEventListener("loadedmetadata", onMetadata, { once: true });
      audio.load();
    }
  });
}

function playHtmlAudioUrl(url: string, absoluteUrl: string, onPlaybackStart: (analyser?: AnalyserNode) => void) {
  return new Promise<void>((resolve, reject) => {
    const preparedUrl = preparedSpeechAudio?.src
      ? new URL(preparedSpeechAudio.src, window.location.href).href
      : "";
    const audio = preparedUrl === absoluteUrl ? preparedSpeechAudio! : document.createElement("audio");
    if (preparedUrl !== absoluteUrl) {
      if (new URL(absoluteUrl).origin !== window.location.origin) audio.crossOrigin = "anonymous";
      audio.src = url;
    }
    audio.muted = false;
    audio.volume = 1;
    audio.playbackRate = selectedSpeechRate.value?.rateMultiplier || 1;
    audio.setAttribute("playsinline", "true");
    if (activeSpeechHtmlAudio && activeSpeechHtmlAudio !== audio) {
      const previous = activeSpeechHtmlAudio;
      previous.pause();
      htmlPlaybackFinishers.get(previous)?.();
    }
    activeSpeechHtmlAudio = audio;
    audio.currentTime = 0;
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      audio.onended = null;
      audio.onerror = null;
      htmlPlaybackFinishers.delete(audio);
      if (activeSpeechHtmlAudio === audio) activeSpeechHtmlAudio = undefined;
      if (error) reject(error);
      else resolve();
    };
    htmlPlaybackFinishers.set(audio, () => finish());
    const timeoutId = window.setTimeout(() => {
      audio.pause();
      finish(new Error("语音文件加载超时"));
    }, 12_000);
    audio.onended = () => {
      finish();
    };
    audio.onerror = () => finish(new Error(`语音文件加载失败（${audio.error?.code || "未知错误"}）`));
    // Keep the media element connected directly to the browser audio output.
    // Routing a cross-origin OSS media element through Web Audio can produce
    // silence even when the MP3 itself is valid and playable.
    audio.play().then(() => onPlaybackStart()).catch((error) => {
      finish(new Error(error instanceof Error ? `语音播放失败：${error.message}` : "语音播放失败"));
    });
  });
}

function isAutoplayDenied(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  return /notallowed|user gesture|play\(\) failed|interact/i.test(message);
}

function unlockSpeechAudioContext() {
  try {
    const AudioCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return null;
    if (!speechAudioContext && !hasActiveUserGesture()) return null;
    speechAudioContext ||= new AudioCtor();
    if (speechAudioContext.state === "suspended") {
      if (!hasActiveUserGesture()) return null;
      speechAudioResumePromise = speechAudioContext.resume().catch(() => undefined);
    } else {
      speechAudioResumePromise = Promise.resolve();
    }
    return speechAudioContext;
  } catch {
    return null;
  }
}

function hasActiveUserGesture() {
  return navigator.userActivation?.isActive === true;
}

function unlockFeedbackAudioContext() {
  try {
    const AudioCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return null;
    if (!keyboardAudioContext && !hasActiveUserGesture()) return null;
    keyboardAudioContext ||= new AudioCtor();
    if (keyboardAudioContext.state === "suspended") {
      if (!hasActiveUserGesture()) return null;
      void keyboardAudioContext.resume().catch(() => undefined);
    }
    return keyboardAudioContext;
  } catch {
    return null;
  }
}

function unlockAudioFromUserGesture() {
  unlockSpeechAudioContext();
  unlockFeedbackAudioContext();
}

function playKeyboardClick(kind: "key" | "advance" = "key") {
  if (!settings.value.keyboardSound || settings.value.keyboardSoundVolume <= 0) return;
  try {
    const context = unlockFeedbackAudioContext();
    if (!context || context.state !== "running") return;
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(kind === "advance" ? 620 : 360, now);
    oscillator.frequency.exponentialRampToValueAtTime(kind === "advance" ? 760 : 460, now + 0.035);
    gain.gain.setValueAtTime(0.0001, now);
    const volumeScale = Math.min(1, Math.max(0, settings.value.keyboardSoundVolume / 100));
    gain.gain.exponentialRampToValueAtTime((kind === "advance" ? 0.045 : 0.028) * volumeScale, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.065);
  } catch {
    // Audio feedback is optional and should never block typing.
  }
}

function previewKeyboardSound() {
  if (!settings.value.keyboardSound) return;
  playKeyboardClick("key");
}

async function toggleFullscreen() {
  if (window.huosuLearningDesktop) {
    desktopFullscreen.value = await window.huosuLearningDesktop.toggleFullscreen();
    return;
  }
  const target = theaterRef.value || document.documentElement;
  if (!document.fullscreenElement) await target.requestFullscreen();
  else await document.exitFullscreen();
  browserFullscreen.value = Boolean(document.fullscreenElement);
}

function showSpeechNotice(message: string, isError: boolean) {
  speechNotice.value = message;
  speechNoticeIsError.value = isError;
}

function stopSpeechStream() {
  speechStream?.getTracks().forEach((track) => track.stop());
  speechStream = undefined;
  speechRecorder = undefined;
}

async function toggleRecording() {
  if (speechAssessing.value || !currentSentence.value) return;
  if (isRecording.value) {
    speechRecorder?.stop();
    return;
  }
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    showSpeechNotice("当前浏览器不支持录音，请使用 Chrome、Edge 或桌面端。", true);
    return;
  }
  if (!currentUser.value) {
    showSpeechNotice("登录后即可使用跟读识别，并保存学习记录。", true);
    showTrialGate();
    return;
  }
  try {
    speechAssessment.value = null;
    speechStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((candidate) => MediaRecorder.isTypeSupported(candidate));
    speechRecorder = mimeType ? new MediaRecorder(speechStream, { mimeType }) : new MediaRecorder(speechStream);
    speechChunks = [];
    speechRecorder.ondataavailable = (event) => { if (event.data.size) speechChunks.push(event.data); };
    speechRecorder.onstop = () => { void submitSpeechRecording(speechRecorder?.mimeType || mimeType || "audio/webm"); };
    speechRecorder.start();
    isRecording.value = true;
  } catch (error) {
    stopSpeechStream();
    showSpeechNotice(error instanceof Error && error.name === "NotAllowedError" ? "请允许麦克风权限后再开始跟读。" : "麦克风暂不可用，请检查设备或浏览器权限。", true);
  }
}

async function submitSpeechRecording(mimeType: string) {
  isRecording.value = false;
  const sentence = currentSentence.value;
  const blob = new Blob(speechChunks, { type: mimeType });
  stopSpeechStream();
  if (!sentence || !blob.size) {
    showSpeechNotice("没有收到有效录音，请重试。", true);
    return;
  }
  if (blob.size > 8 * 1024 * 1024) {
    showSpeechNotice("录音超过 8MB，请缩短后重试。", true);
    return;
  }
  speechAssessing.value = true;
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = "";
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    const result = await assessSpeech({ sentenceId: sentence.id, text: sentence.english, audioBase64: btoa(binary), mimeType });
    speechAssessment.value = result;
    showSpeechNotice(result.fallbackUsed ? "主模型不可用，已由备用识别模型完成。" : "识别完成。", false);
  } catch (error) {
    showSpeechNotice(friendlySpeechAssessmentError(error), true);
  } finally {
    speechAssessing.value = false;
    speechChunks = [];
  }
}

function friendlySpeechAssessmentError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  if (/status code 413|request entity too large|payload too large/i.test(message)) {
    return "录音数据超过服务端接收限制，请缩短录音后重试。";
  }
  return message || "口语识别失败，请稍后重试。";
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && pwaInstallGuideOpen.value) {
    event.preventDefault();
    closePwaInstallGuide();
    return;
  }
  const mod = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  const isSpace = event.key === " " || event.key === "Spacebar";

  // 课程选择、浮层关闭或登录后，隐藏输入框可能丢失焦点。
  // 当用户不在真实表单控件中输入时，将练习按键直接交给打字/听写逻辑。
  const eventTarget = event.target instanceof HTMLElement ? event.target : null;
  const isGhostInput = eventTarget === typingInputRef.value;
  const isRealFormInput = Boolean(eventTarget?.closest("input:not(.ghost-type-input), textarea, select, [contenteditable='true']"));

  // 小关完成弹窗出现后，Enter 与主按钮保持同一行为，让用户无需离开键盘。
  // 忽略长按重复和输入法合成，避免一次回车连续跨过多关。
  if (courseCompletionOpen.value && nextLessonInCurrentCourse.value && event.key === "Enter"
    && !event.repeat && !event.isComposing && !mod && !event.altKey && !event.shiftKey) {
    event.preventDefault();
    void continueNextLesson();
    return;
  }
  const practiceInputKey = event.key === "Enter"
    || event.key === "Backspace"
    || event.key === "Delete"
    || event.key === "ArrowLeft"
    || event.key === "ArrowRight"
    || event.key === "Home"
    || event.key === "End"
    || isSpace
    || (!mod && !event.altKey && event.key.length === 1);
  const practiceInputBlocked = landingVisible.value
    || trialGateOpen.value
    || onboardingOpen.value
    || courseCompletionOpen.value
    || visibilityResumeOpen.value
    || accountPanelOpen.value
    || settingsOpen.value
    || sidebarOpen.value
    || smartPlanOpen.value
    || adaptiveChoiceOpen.value;

  if (!isGhostInput && !isRealFormInput && !practiceInputBlocked && currentSentence.value
    && ["dictation", "typing"].includes(mode.value) && practiceInputKey) {
    handleTypingKeydown(event);
    if (event.defaultPrevented) return;
  }

  if (event.key !== "Enter") resetEnterSubmitStreak();

  if (event.shiftKey && event.key === "ArrowRight") {
    event.preventDefault();
    nextSentence();
    return;
  }
  if (event.shiftKey && event.key === "ArrowLeft") {
    event.preventDefault();
    prevSentence();
    return;
  }
  // Do not hijack Ctrl/Command+A while the learner is completing a real form.
  // The ghost practice input has its own handler above; this branch covers
  // keyboard focus that has temporarily left the practice area.
  if (mod && key === "a" && !isRealFormInput && !practiceInputBlocked && currentSentence.value) {
    event.preventDefault();
    toggleAnswer();
    return;
  }
  if (mod && key === "p") {
    event.preventDefault();
    void handleSpeak(currentSentence.value?.english || "");
    return;
  }
  // 听写和打字的空格键归答案输入区独占，不允许全局快捷键抢占。
  if (isSpace && ["dictation", "typing"].includes(mode.value)) return;
  if (isSpace && practiceCanAdvanceByShortcut()) {
    event.preventDefault();
    nextSentence();
    return;
  }
  if (isSpace && mode.value === "speaking") {
    event.preventDefault();
    toggleRecording();
    return;
  }
  if (isSpace && mode.value === "learning") {
    event.preventDefault();
    void handleSpeak(currentSentence.value?.english || "");
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    handleEnterSubmitShortcut();
  }
}

function handleCourseCompletionShortcut(event: KeyboardEvent) {
  if (!courseCompletionOpen.value || !nextLessonInCurrentCourse.value || event.key !== "Enter") return;
  if (event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
  // Capture before the hidden practice input consumes Enter as another submission.
  event.preventDefault();
  event.stopPropagation();
  void continueNextLesson();
}

function isGenericPlaceholderStructure(sentence: Sentence) {
  const parts = sentence.structure || [];
  if (!parts.length) return true;
  const labels = parts.map((part) => part.label.trim()).join("/");
  const genericCopy = parts.some((part) => /说明句子谈论的对象|说明动作或状态|补充对象、地点、时间或原因/.test(part.explain));
  return labels === "主语/谓语/补充" && genericCopy;
}

function displayStructure(sentence: Sentence) {
  return safeSentenceStructure(sentence);
}

function phoneticForSegment(sentence: Sentence, text: string, index: number) {
  if (!sentence.phonetic) return "";
  if (canonicalSentence(text) === canonicalSentence(sentence.english)) return sentence.phonetic;
  const phoneticParts = sentence.phonetic.replace(/^\/|\/$/g, "").split(/\s+/);
  // Missing alignment must not shift another word's pronunciation underneath this phrase.
  if (phoneticParts.length !== normalizeWords(sentence.english).length) return "";
  const start = displayStructure(sentence).slice(0, index).reduce((count, part) => count + normalizeWords(part.text).length, 0);
  return phoneticParts.slice(start, start + normalizeWords(text).length).join(" ");
}

function wordGrammarRoles(sentence: Sentence) {
  return displayStructure(sentence).flatMap((part, partIndex) => {
    const color = colorForRole(part.label, partIndex);
    return normalizeWords(part.text).map((_word, wordIndex) => ({
      label: part.label,
      color,
      firstInRole: wordIndex === 0
    }));
  });
}


function colorForRole(label: string, index: number) {
  if (label.includes("主")) return roleColors[0];
  if (label.includes("谓") || label.includes("表")) return roleColors[1];
  if (label.includes("宾")) return roleColors[2];
  if (label.includes("定")) return roleColors[3];
  if (label.includes("状")) return roleColors[4];
  if (label.includes("补")) return roleColors[5];
  return roleColors[index % roleColors.length];
}

function keywordExplain(word: string) {
  const matched = localWordEntry(word);
  return matched?.meanings[0]?.zh || "";
}

function localWordEntry(word: string) {
  const target = canonicalWord(word);
  const words = bootstrap.value?.words || [];
  return words.find((entry) => canonicalWord(entry.word) === target)
    || words.find((entry) => {
      const source = canonicalWord(entry.word);
      return target === `${source}s` || target === `${source}es` || target === `${source}ed` || target === `${source}ing`;
    });
}

function firstWord(text: string) {
  return normalizeWords(text)[0] || text;
}

function stableShuffleScore(word: string, order: number) {
  return word.split("").reduce((sum, char) => sum + char.charCodeAt(0), order * 17) % 97;
}

function formatClock(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function formatStudyTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "--" : date.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
}

watch(() => currentUser.value?.id, () => {
  stopAdaptiveTrainingPolling();
  adaptiveTrainingBatches.value = [];
  adaptiveTrainingBatch.value = null;
  aiHistoryHasMore.value = false;
  aiHistoryOffset = 0;
  aiHistoryError.value = "";
  practiceSyncNotice.value = "";
  retryPracticeSync();
});
watch(learnerCategoryRoots, (roots) => {
  // 分类数据异步加载后，句子库默认选中第一个一级分类，
  // 并由 selectSentenceRoot 展开其第一个二级分类的三级项。
  if (roots.length && !activeRootCategory.value) selectSentenceRoot(roots[0].id);
}, { immediate: true });
watch(currentSentence, () => {
  void loadCurrentStudyDictionary();
  resetForCurrentSentence();
  cachePracticePosition();
  void prepareCurrentSentenceAudio();
  scheduleCurrentPracticeWindowPreload();
  scheduleTypingKeyMeasurement();
});
watch(currentLesson, (lesson) => {
  if (lesson) finishPracticeBootLoading();
});
watch(mode, () => {
  void loadCurrentStudyDictionary();
  cachePracticePosition();
  scheduleTypingKeyMeasurement();
  restartAudioPreloadForCurrentPractice();
});
watch(() => [settings.value.voiceCode, settings.value.speechRate], () => {
  restartAudioPreloadForCurrentPractice();
});
watch(settings, persistSettings, { deep: true });
watch(landingVisible, (visible) => {
  if (visible) {
    scheduleLandingTypingDemo(760);
  } else {
    stopLandingTypingDemo();
  }
});

onMounted(async () => {
  window.addEventListener(USER_AUTH_EXPIRED_EVENT, resetExpiredUserSession);
  pwaStandalone.value = isRunningAsPwa();
  window.addEventListener("beforeinstallprompt", handleBeforePwaInstallPrompt);
  window.addEventListener("appinstalled", handlePwaInstalled);
  void registerPwaServiceWorker();
  window.addEventListener("online", retryPracticeSync);
  dailyRecommendationBaseReady.value = false;
  if (!landingVisible.value && !currentLesson.value) {
    practiceBootLoading.value = true;
    practiceBootStageTimerId = window.setInterval(() => {
      practiceBootStage.value = Math.min(practiceBootLabels.length - 1, practiceBootStage.value + 1);
    }, 1150);
  }
  let cachedLearnerActivityRestored = false;
  let earlyLearningResume: Promise<boolean> | null = null;
  if (landingVisible.value) scheduleLandingTypingDemo(760);
  // When a validated user and checkpoint were cached on this device, restore
  // the exact practice page immediately while /auth/me validates in parallel.
  if (currentUser.value) {
    restorePreferredPracticeMode();
    // Paint the last lesson from local storage before waiting for /auth/me or
    // any catalogue/activity request. Network validation continues below.
    restoreCachedPracticeImmediately();
    const restoredAdaptiveHome = restoreCachedAdaptiveHome();
    cachedLearnerActivityRestored = restoreCachedLearnerActivity();
    if (restoredAdaptiveHome) dailyRecommendationBaseReady.value = true;
    if (cachedLearnerActivityRestored) {
      earlyLearningResume = restoreLearningCheckpoint(readPreferredPracticeMode(currentUser.value.id));
    }
  }
  try {
    const me = await getMe();
    currentUser.value = me.id === "guest" ? null : me;
    // /auth/me 对失效会话会安全返回游客态，避免首屏出现 401；此时仍必须
    // 主动清掉旧的本地令牌和 HttpOnly 学员 Cookie，防止后续请求反复携带它。
    if (!currentUser.value && (me as { sessionExpired?: boolean }).sessionExpired) {
      resetExpiredUserSession();
      await clearExpiredUserSession();
    }
    cacheAuthenticatedUser(currentUser.value);
    restorePreferredPracticeMode();
    if (currentUser.value) {
      landingVisible.value = false;
      // 首次通过 Cookie 恢复登录时，内存里还没有用户对象；此处同样要
      // 恢复本地练习包，不能让后续的“继续学习”记录替换真人句子包。
      if (!currentLesson.value) restoreCachedPracticeImmediately();
      const restoredAdaptiveHome = adaptiveHome.value ? true : restoreCachedAdaptiveHome();
      if (!cachedLearnerActivityRestored) cachedLearnerActivityRestored = restoreCachedLearnerActivity();
      // 当天推荐摘要和最近学习位置已经缓存在本机时，刷新后立即允许选择。
      if (restoredAdaptiveHome) dailyRecommendationBaseReady.value = true;
    } else if (!reviewPreviewCourseId && restoreCachedPracticeImmediately()) {
      // 游客从真人录音包刷新后也要回到原课程和原句，而不是重新挑默认课。
      landingVisible.value = false;
    }
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    // Only an explicit authentication rejection clears the local fast-resume
    // state. A timeout/offline refresh keeps the last valid practice page.
    if (status === 401 || status === 403) {
      resetExpiredUserSession();
      await clearExpiredUserSession();
    }
  }
  // 刷新时只读取轻量推荐摘要。课程恢复、今日训练包和音频都必须等用户
  // 明确选择学习路径后再加载，避免无效请求、模型调用和界面跳动。
  const priorityAdaptiveHome = currentUser.value
    ? refreshAdaptiveHome().finally(() => {
        dailyRecommendationBaseReady.value = true;
      })
    : Promise.resolve();
  const preferredMode = currentUser.value ? readPreferredPracticeMode(currentUser.value.id) : undefined;
  // Restore the last lesson from the cached checkpoint immediately. When the
  // cache is missing, only wait for the focused activity endpoint—not the full
  // catalogue, review, OpenAPI and commerce initialization chain.
  const priorityLearningResume = currentUser.value && !reviewPreviewCourseId
    ? (currentLesson.value ? Promise.resolve(true) : (earlyLearningResume || (cachedLearnerActivityRestored
        ? restoreLearningCheckpoint(preferredMode)
        : refreshLearnerActivity().then(() => restoreLearningCheckpoint(preferredMode)))))
    : Promise.resolve(false);
  const learningStateRefresh = refreshLearningState({
    skipAdaptiveHome: Boolean(currentUser.value),
    skipLearnerActivity: Boolean(currentUser.value)
  });
  if (currentUser.value && !reviewPreviewCourseId) {
    // A missing/invalid checkpoint falls back only after bootstrap has supplied
    // the starter catalogue. Successful resume never waits for that heavy work.
    void priorityLearningResume.then(async (restoredFromCheckpoint) => {
      if (!currentUser.value || currentLesson.value || onboardingOpen.value) return;
      if (!restoredFromCheckpoint) {
        await learningStateRefresh;
        if (!currentUser.value || currentLesson.value || onboardingOpen.value) return;
        const starterCourse = pickStarterCourse();
        if (starterCourse?.lessons[0]) await loadLesson(starterCourse.lessons[0].id, { mode: preferredMode });
      }
      await nextTick();
      focusTyping();
    }).catch(() => undefined).finally(() => finishPracticeBootLoading());
    await priorityAdaptiveHome;
  } else {
    await Promise.all([learningStateRefresh, priorityAdaptiveHome]);
    dailyRecommendationBaseReady.value = true;
  }
  if (isDesktopShell) await registerCurrentDesktopInstallation();
  await refreshDesktopDownloadCenter();
  if (reviewPreviewCourseId) {
    await openReviewPreviewCourse();
    finishPracticeBootLoading();
  } else if (!currentUser.value) {
    // 已从本地恢复的真人句子包不再被默认课程覆盖。
    if (!currentLesson.value) {
      const starterCourse = pickStarterCourse();
      if (starterCourse?.lessons[0]) await loadLesson(starterCourse.lessons[0].id);
    }
    finishPracticeBootLoading();
  }
  if (!currentUser.value && guestTrialState.value.locked) {
    landingVisible.value = false;
    showTrialGate();
  } else if (!dailyRecommendationOpen.value && !dailyRecommendationChoiceCommitted.value) {
    window.setTimeout(() => {
      void maybeStartIntroDemo();
    }, 520);
  }
  timerId = window.setInterval(() => {
    elapsedSeconds.value = Math.floor((Date.now() - sessionStartedAt.value) / 1000);
    if (document.visibilityState === "visible" && currentSentence.value && !landingVisible.value
      && !trialGateOpen.value && !onboardingOpen.value && !courseCompletionOpen.value && !visibilityResumeOpen.value
      && !accountPanelOpen.value && !settingsOpen.value && !sidebarOpen.value && !smartPlanOpen.value
      && !adaptiveChoiceOpen.value && !feedback.value?.correct) practiceActiveMs = Math.min(600000, practiceActiveMs + 1000);
    if (elapsedSeconds.value % 30 === 0) retryPracticeSync();
  }, 1000);
  removeDesktopUpdateListener = window.huosuLearningDesktop?.onUpdateProgress?.((progress) => {
    desktopUpdateProgress.value = progress;
    if (progress.phase === "idle") {
      window.setTimeout(() => {
        if (desktopUpdateProgress.value?.phase === "idle") desktopUpdateProgress.value = null;
      }, 1500);
    }
  });
  if (isDesktopShell) {
    desktopFullscreen.value = await window.huosuLearningDesktop?.getFullscreenState?.() || false;
    removeDesktopFullscreenListener = window.huosuLearningDesktop?.onFullscreenChange?.((isFullscreen) => {
      desktopFullscreen.value = isFullscreen;
    });
  } else {
    browserFullscreen.value = Boolean(document.fullscreenElement);
    document.addEventListener("fullscreenchange", syncBrowserFullscreenState);
  }
  window.addEventListener("keydown", handleCourseCompletionShortcut, { capture: true });
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", scheduleTypingKeyMeasurement);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pointerdown", unlockAudioFromUserGesture, { capture: true });
  window.addEventListener("keydown", unlockAudioFromUserGesture, { capture: true });
});

onUnmounted(() => {
  window.removeEventListener(USER_AUTH_EXPIRED_EVENT, resetExpiredUserSession);
  studyGrammarRequest++;
  if (studyGrammarTimer) clearTimeout(studyGrammarTimer);
  stopSpeechStream();
  filmRecordingVideo?.pause();
  filmRecordingVideo = undefined;
  window.removeEventListener("beforeinstallprompt", handleBeforePwaInstallPrompt);
  window.removeEventListener("appinstalled", handlePwaInstalled);
  window.removeEventListener("online", retryPracticeSync);
  if (timerId) window.clearInterval(timerId);
  if (smsCountdownTimerId) window.clearInterval(smsCountdownTimerId);
  if (resumeNoticeTimerId) window.clearTimeout(resumeNoticeTimerId);
  if (practiceWindowPreloadTimer) window.clearTimeout(practiceWindowPreloadTimer);
  if (pendingWordSaveTimerId) window.clearTimeout(pendingWordSaveTimerId);
  if (waveformAnimationFrameId) window.cancelAnimationFrame(waveformAnimationFrameId);
  if (practiceBootStageTimerId) window.clearInterval(practiceBootStageTimerId);
  if (practiceBootFinishTimerId) window.clearTimeout(practiceBootFinishTimerId);
  stopAdaptiveTrainingPolling();
  stopWechatPayPolling();
  stopLandingTypingDemo();
  removeDesktopUpdateListener?.();
  removeDesktopFullscreenListener?.();
  document.removeEventListener("fullscreenchange", syncBrowserFullscreenState);
  window.removeEventListener("keydown", handleCourseCompletionShortcut, { capture: true });
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", scheduleTypingKeyMeasurement);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("pointerdown", unlockAudioFromUserGesture, { capture: true });
  window.removeEventListener("keydown", unlockAudioFromUserGesture, { capture: true });
});

function syncBrowserFullscreenState() {
  browserFullscreen.value = Boolean(document.fullscreenElement);
}
</script>
